import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { User, Calendar, CreditCard, AlertCircle, Plus, Sparkles, X, ChevronDown, ChevronUp, Shield } from 'lucide-react';
import AppHeader from './shared/AppHeader';
import ImageCropModal from './gallery/ImageCropModal';
import { DeleteAvatarModal, DeleteAccountModal, DeletingAccountModal } from './modals/DeleteModals';
import Gallery, { VideoGeneratorFullscreen } from './gallery/Gallery';
import HollywoodSection from './hollywood/HollywoodSection';
import { getCachedGenerations, setCachedGenerations } from '../utils/galleryCache';
import SnapTransferModal from './modals/SnapTransferModal';
import JuliaInlinePanel from './julia/JuliaInlinePanel';
import JupiterOrbRealtime from './julia/JupiterOrbRealtime';
import V6DropdownControls from './shared/V6DropdownControls';
import V6ModernPicker from './shared/V6ModernPicker';
import SnapshotSnake from './SnapshotSnake';
import SnapshotPong from './SnapshotPong';
import PromptHistoryPanel from './generation/PromptHistoryPanel';
import TwoFactorSetup from './auth/TwoFactorSetup';
import TwoFactorGate from './auth/TwoFactorGate';
import BeatleapWizard from './beatleap/BeatleapWizard';
import MagicCreate from './generation/MagicCreate';
import LipSyncPanel from './lipsync/LipSyncPanel';
import DashboardDemoOverlay, { DashboardRewardFlyouts, DashboardDemoVideoPlayer } from './onboarding/DashboardDemoOverlay';
import { PROMPT_TEMPLATES } from '../data/promptTemplates';  // NEU: 50 Vorlagen!
import { API_URL, TRANSLATIONS } from '../config';
import { buildSuperPrompt, SUPER_NEGATIVE_PROMPT } from '../utils/superPrompt';
import { apiClient, getToken } from '../utils/apiClient';  // NEU: Zentrale API mit Auth-Header
import { formatSnaps, snapText } from '../utils/snapFormatter';

// Storage-Helper: Bei Impersonation (sessionStorage gesetzt) → sessionStorage zuerst
const _isImpersonating = () => {
  try { return sessionStorage.getItem('snapshot_impersonation') === 'true'; } catch { return false; }
};
const _getStoredUser = () => {
  try {
    const raw = sessionStorage.getItem('snapshot_user') || localStorage.getItem('snapshot_user');
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};
const _getStoredToken = () => {
  try {
    return sessionStorage.getItem('snapshot_token') || localStorage.getItem('snapshot_token');
  } catch { return null; }
};
const _setStoredUser = (user) => {
  const target = _isImpersonating() ? sessionStorage : localStorage;
  try { target.setItem('snapshot_user', JSON.stringify(user)); } catch {}
};

export default function IDCardDashboard({ onStartCreation, onBackToDashboard, onShowGallery, currentUser, onLogout, readOnlyMode = false, showcaseData = null, limitToFirst = false }) {
  // ReadOnly Mode für Showcase (Gäste können nur schauen, nicht agieren)
  const navigate = useNavigate();
  
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDeleteAvatar, setShowDeleteAvatar] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);  // NEU: 2FA Setup
  const [show2FAGate, setShow2FAGate] = useState(false);  // NEU: 2FA Gate
  const [pending2FAAction, setPending2FAAction] = useState(null);  // NEU: Action nach 2FA
  const [photos, setPhotos] = useState([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState(new Set()); // Optimistisch gelöschte Bild-IDs (Index-Shift-sicher weil setPhotos nicht aufgerufen wird)
  const [loadingGallery, setLoadingGallery] = useState(true);
  const [avatarsLoading, setAvatarsLoading] = useState(true);
  const [isProcessingOAuth, setIsProcessingOAuth] = useState(false);
  
  // NEW: Prompt & Feintuning States
  const [showPromptField, setShowPromptField] = useState(true);  // IMMER sichtbar!
  const [showInfoModal, setShowInfoModal] = useState(false);  // NEU: Info Modal
  const [showAutoExtendHint, setShowAutoExtendHint] = useState(false);  // Einmaliger Hinweis nach Avatar-Anlage
  const [currentGame, setCurrentGame] = useState(() => Math.floor(Math.random() * 2));  // Zufällig: 0=Snake, 1=Pong
  const [onboardingConfig, setOnboardingConfig] = useState(null);
  const [onboardingUserState, setOnboardingUserState] = useState(null);
  const [demoShowcase, setDemoShowcase] = useState(null);
  const [demoModeActive, setDemoModeActive] = useState(false);
  const [demoTourStarted, setDemoTourStarted] = useState(false);
  const [demoModal, setDemoModal] = useState(null);
  const [demoBubble, setDemoBubble] = useState(null);
  const [demoPendingImageJobs, setDemoPendingImageJobs] = useState([]);
  const [demoPendingVideos, setDemoPendingVideos] = useState([]);
  const [demoGeneratedImages, setDemoGeneratedImages] = useState([]);
  const [demoGeneratedVideos, setDemoGeneratedVideos] = useState([]);
  const [demoVideoPrompt, setDemoVideoPrompt] = useState('');
  const [demoVideoSourceImage, setDemoVideoSourceImage] = useState(null);
  const [demoPlayingVideoUrl, setDemoPlayingVideoUrl] = useState(null);
  const [demoRewardFlyouts, setDemoRewardFlyouts] = useState([]);
  const demoTimersRef = useRef([]);
  const demoRunIdRef = useRef(0);

  // Inline-Fehlerbanner für Bildgenerierung (ersetzt Browser-alert()).
  const [imageError, setImageError] = useState(null);
  
  // Accordion States für ID Card
  const [accordionOpen, setAccordionOpen] = useState({
    createImage: false,   // Standard: Geschlossen (Clean Look!)
    julia: false,        // Standard: Geschlossen
    quickActions: false,  // Standard: Geschlossen
    videoCreate: false,  // Video erstellen (Studio inline)
    videoEditor: false,  // Video Editor Akkordeon
    lipsync: false,       // Lip-Sync / Sprechender Avatar
    hollywood: false,     // Hollywood — Multi-Reference Video
  });

  // EXKLUSIV-MODUS: nur ein Akkordeon zur Zeit offen — verhindert Chaos auf der Timeline.
  // Toggle: ist der gewünschte schon offen → zumachen; sonst nur diesen öffnen, alle anderen zu.
  const toggleAccordion = (name) => {
    setAccordionOpen(prev => {
      const wasOpen = !!prev[name];
      const allClosed = Object.fromEntries(Object.keys(prev).map(k => [k, false]));
      return wasOpen ? allClosed : { ...allClosed, [name]: true };
    });
  };
  
  // 🎬 VIDEO EDITOR STATE (angehoben aus Gallery)
  const [assemblyQueue, setAssemblyQueue] = useState([]); // [{type, url, thumbnail, id, duration, textOverlay}]
  const [galleryTab, setGalleryTab] = useState('images'); // 🎬 Tab-Control für Gallery ('images' | 'videos')

  // Listener für externe Tab-Wechsel (z.B. aus HollywoodSection.handleGenerate)
  useEffect(() => {
    const handler = (e) => {
      const tab = e.detail?.tab;
      if (tab === 'videos' || tab === 'images') setGalleryTab(tab);
    };
    window.addEventListener('snapshot:setGalleryTab', handler);
    return () => window.removeEventListener('snapshot:setGalleryTab', handler);
  }, []);
  const [videoPreselectedImage, setVideoPreselectedImage] = useState(null); // Bild das im Video-Accordion vorausgewählt werden soll
  const [videoPreselectedPrompt, setVideoPreselectedPrompt] = useState(null); // Prompt der im Video-Accordion übernommen werden soll
  const [videoPreselectedMotionTags, setVideoPreselectedMotionTags] = useState(null); // Chips die im Video-Accordion vorausgewählt werden sollen
  const [videoPreselectedSettings, setVideoPreselectedSettings] = useState(null); // Dauer, Auflösung, Format, Source-Image
  const [videoPreselectedModel, setVideoPreselectedModel] = useState('grok'); // Video-Modus: 'cinematic_video' oder 'grok' — Default matched videoMode-Tab-Default
  const [assembling, setAssembling] = useState(false);
  const [assemblyResult, setAssemblyResult] = useState(null);
  const [assemblies, setAssemblies] = useState([]); // Fertige Videos
  const [selectedClipIndex, setSelectedClipIndex] = useState(0); // Ausgewählter Clip in Timeline
  const [activeToolbar, setActiveToolbar] = useState(null); // 'text' | null
  const [textInput, setTextInput] = useState(''); // Text-Input für Overlay
  
  // 🎵 Beatleap Wizard
  const [showBeatleapWizard, setShowBeatleapWizard] = useState(false);
  // 🎬✨ AI Magic Create
  const [showMagicCreate, setShowMagicCreate] = useState(false);
  const [editingProject, setEditingProject] = useState(null); // For re-editing existing projects
  const [loadingAssemblies, setLoadingAssemblies] = useState(true);
  const [playingAssembly, setPlayingAssembly] = useState(null);
  const [kinoVideoReady, setKinoVideoReady] = useState(null); // Fertiges Kino-Modus Video für Gallery

  // Prompt History States
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [promptHistory, setPromptHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [hollywoodPreset, setHollywoodPreset] = useState(null); // Prompt aus 'Übernehmen' für Hollywood

  // Geführte Video-Generierung: null = Picker, sonst spezifischer Modus.
  //   'kino'      = Kino-Modus (cinematic_video, lang & filmisch)
  //   'shorts'    = Shorts (grok, kurz für Social)
  //   'hollywood' = Hollywood (Multi-Reference Storytelling)
  const [videoMode, setVideoMode] = useState('shorts');
  const enterVideoMode = (mode) => {
    setVideoMode(mode);
    if (mode === 'kino')   setVideoPreselectedModel('cinematic_video');
    if (mode === 'shorts') setVideoPreselectedModel('grok');
    // Hollywood nutzt keine VideoGeneratorFullscreen-Settings
  };
  const leaveVideoMode = () => {
    setVideoMode(null);
    setVideoPreselectedModel(null);
  };
  const [promptText, setPromptText] = useState('');
  const [originalPromptDE, setOriginalPromptDE] = useState(''); // Deutscher Original-Prompt (nie übersetzt)
  const [bodyMode, setBodyMode] = useState('portrait');
  const [cinematicMood, setCinematicMood] = useState('golden');
  const [portraitStyle, setPortraitStyle] = useState('natural');
  const [fullBodyStyle, setFullBodyStyle] = useState('street');
  // Pending Image Jobs: Array von {id, startTime, startImageCount} für parallele Generierungen
  const [pendingImageJobs, setPendingImageJobs] = useState(() => {
    const saved = localStorage.getItem('snapshotpro_generating');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Legacy-Format (Boolean) → migrieren
        if (parsed.generating && Date.now() - parsed.startTime < 3 * 60 * 1000) {
          return [{ id: 'legacy', startTime: parsed.startTime, startImageCount: parsed.startImageCount || 0 }];
        }
        // Neues Format (Array)
        if (Array.isArray(parsed)) {
          return parsed.filter(j => Date.now() - j.startTime < 3 * 60 * 1000);
        }
      } catch (e) {}
      localStorage.removeItem('snapshotpro_generating');
    }
    return [];
  });

  // Abgeleiteter Boolean für Abwärtskompatibilität (Polling, UI-Checks)
  const isGenerating = pendingImageJobs.length > 0;

  const addPendingImageJob = () => addPendingImageJobs(1)[0];

  const addPendingImageJobs = (count = 1) => {
    const safeCount = Math.max(1, Math.min(8, Number(count) || 1));
    const currentCount = photos.reduce((sum, gen) => sum + (gen.image_urls?.length || 0), 0);
    const now = Date.now();
    const newJobs = Array.from({ length: safeCount }, (_, i) => ({
      id: `gen_${now}_${i}_${Math.random().toString(36).slice(2, 6)}`,
      startTime: now,
      startImageCount: currentCount,
      requiredAtCount: currentCount + i + 1
    }));
    setPendingImageJobs(prev => {
      const updated = [...prev, ...newJobs];
      localStorage.setItem('snapshotpro_generating', JSON.stringify(updated));
      return updated;
    });
    console.log(`🔄 Added ${safeCount} pending job(s), startCount:`, currentCount);
    return newJobs.map(j => j.id);
  };

  const removePendingImageJobs = (jobIds) => {
    const idSet = new Set(jobIds);
    setPendingImageJobs(prev => {
      const updated = prev.filter(j => !idSet.has(j.id));
      if (updated.length === 0) {
        localStorage.removeItem('snapshotpro_generating');
      } else {
        localStorage.setItem('snapshotpro_generating', JSON.stringify(updated));
      }
      return updated;
    });
  };

  const removePendingImageJob = (jobId) => {
    setPendingImageJobs(prev => {
      const updated = prev.filter(j => j.id !== jobId);
      if (updated.length === 0) {
        localStorage.removeItem('snapshotpro_generating');
      } else {
        localStorage.setItem('snapshotpro_generating', JSON.stringify(updated));
      }
      return updated;
    });
    console.log('✅ Removed pending job:', jobId);
  };

  const clearAllPendingImageJobs = () => {
    setPendingImageJobs([]);
    localStorage.removeItem('snapshotpro_generating');
    console.log('✅ Cleared all pending jobs');
  };

  // Legacy-Wrapper für setIsGenerating (Abwärtskompatibilität)
  const setIsGenerating = (value, imageCount = null) => {
    if (!value) {
      clearAllPendingImageJobs();
    }
    // value=true wird jetzt über addPendingImageJob() gemacht
  };
  const [isEnhancing, setIsEnhancing] = useState(false);  // Prompt optimieren
  const [isPromptGerman, setIsPromptGerman] = useState(true);  // DE/EN Toggle
  const [isTranslating, setIsTranslating] = useState(false);  // Translation loading
  const isPromptGermanRef = useRef(true);  // Ref für aktuellen Wert (kein Closure-Problem!)

  // Sync ref mit state
  useEffect(() => {
    isPromptGermanRef.current = isPromptGerman;
  }, [isPromptGerman]);

  // Thumbnail-Preload: erste 12 sichtbare Bild-URLs parallel vorladen,
  // damit das Galerie-First-Paint flüssig rennt. Wird automatisch entfernt,
  // wenn photos sich ändert (nächstes Avatar / nach Delete), damit wir den
  // <head> nicht vollmüllen.
  useEffect(() => {
    if (!photos || photos.length === 0) return;
    const head = document.head;
    const links = [];
    const urls = new Set();
    for (const gen of photos) {
      const imgs = gen.thumbnail_urls || gen.image_urls || (gen.image_url ? [gen.image_url] : []);
      for (const u of imgs) {
        if (!u || urls.has(u)) continue;
        urls.add(u);
        if (urls.size > 12) break;
      }
      if (urls.size > 12) break;
    }
    for (const u of urls) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = u;
      head.appendChild(link);
      links.push(link);
    }
    return () => {
      for (const l of links) {
        try { head.removeChild(l); } catch (_) { /* already removed */ }
      }
    };
  }, [photos]);

  const clearDemoTimers = () => {
    demoTimersRef.current.forEach(clearTimeout);
    demoTimersRef.current = [];
  };

  const scheduleDemoTask = (fn, delay) => {
    const timerId = setTimeout(fn, delay);
    demoTimersRef.current.push(timerId);
    return timerId;
  };

  const demoWait = (delay) => new Promise((resolve) => {
    scheduleDemoTask(resolve, delay);
  });

  const updateStoredUserForOnboarding = (updates) => {
    try {
      const storage = sessionStorage.getItem('snapshot_impersonation') === 'true' ? sessionStorage : localStorage;
      const raw = storage.getItem('snapshot_user');
      const parsed = raw ? JSON.parse(raw) : {};
      const next = { ...parsed, ...updates };
      storage.setItem('snapshot_user', JSON.stringify(next));
      window.dispatchEvent(new Event('snapshot-user-updated'));
    } catch (error) {
      console.error('Failed to update stored user after onboarding:', error);
    }
  };

  const smoothScrollToSelector = async (selector, block = 'center') => {
    const element = document.querySelector(selector);
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const currentY = window.scrollY || window.pageYOffset;
    const viewportH = window.innerHeight;
    let targetY;
    if (block === 'start') {
      targetY = currentY + rect.top - 96;
    } else if (block === 'end') {
      targetY = currentY + rect.bottom - viewportH + 32;
    } else {
      targetY = currentY + rect.top - (viewportH - rect.height) / 2;
    }
    const maxY = (document.documentElement.scrollHeight || 0) - viewportH;
    targetY = Math.max(0, Math.min(targetY, maxY));

    const startY = currentY;
    const distance = targetY - startY;
    if (Math.abs(distance) < 2) {
      await demoWait(200);
      return;
    }

    // Langsame, sanfte Scroll-Animation: ~1400 ms ease-in-out,
    // zusätzlich distance-basierter Zuschlag für sehr weite Wege.
    const baseDuration = 1400;
    const extraPerPixel = 0.35;
    const duration = Math.min(2400, baseDuration + Math.abs(distance) * extraPerPixel);
    const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
    const startTime = performance.now();

    await new Promise((resolve) => {
      const step = (now) => {
        if (demoRunIdRef.current === null) {
          resolve();
          return;
        }
        const elapsed = now - startTime;
        const progress = Math.min(1, elapsed / duration);
        window.scrollTo(0, startY + distance * easeInOut(progress));
        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          resolve();
        }
      };
      requestAnimationFrame(step);
    });

    await demoWait(250);
  };

  const waitForSelector = async (selector, timeoutMs = 2400) => {
    const startedAt = Date.now();
    let element = document.querySelector(selector);

    while (!element && Date.now() - startedAt < timeoutMs) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => requestAnimationFrame(resolve));
      element = document.querySelector(selector);
    }

    return element;
  };

  const typewriteDemoText = async (text, setter, speed = 32, targetSelector = null) => {
    setter('');
    const syncScroll = () => {
      if (!targetSelector) return;
      const el = document.querySelector(targetSelector);
      if (!el) return;
      if (typeof el.scrollTop === 'number') {
        el.scrollTop = el.scrollHeight;
      }
    };
    // Scroll-Sync im nächsten Frame, fire-and-forget — kein Await im Loop,
    // damit der Typewriter einen konstanten Takt hat und nicht stottert.
    for (let index = 1; index <= text.length; index += 1) {
      setter(text.slice(0, index));
      requestAnimationFrame(syncScroll);
      // eslint-disable-next-line no-await-in-loop
      await demoWait(speed);
    }
    syncScroll();
    await demoWait(900);
  };

// Browser Back/Forward: Overlays schließen statt Seite verlassen
  useEffect(() => {
    const hasOverlay = showBeatleapWizard || showMagicCreate || playingAssembly;
    if (hasOverlay) {
      // Nur pushen wenn noch kein Overlay-State im History liegt
      if (!window.history.state?.overlay) {
        window.history.pushState({ overlay: true }, '');
      }
    }
  }, [showBeatleapWizard, showMagicCreate, playingAssembly]);

  useEffect(() => {
    const handlePopState = (e) => {
      // Wenn ein Overlay offen ist → schließen statt Navigation
      if (showBeatleapWizard) {
        setShowBeatleapWizard(false);
        setEditingProject(null);
        return;
      }
      if (showMagicCreate) {
        setShowMagicCreate(false);
        return;
      }
      if (playingAssembly) {
        setPlayingAssembly(null);
        return;
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [showBeatleapWizard, showMagicCreate, playingAssembly]);

  // 🎬 Fertige Videos beim Mount laden (nur wenn eingeloggt)
  useEffect(() => {
    if (readOnlyMode) return;
    fetchAssemblies();
  }, [readOnlyMode]);

  // Toggle DE/EN Translation (nutzt ref statt state für aktuellen Wert)
  const handleTranslatePrompt = async () => {
    if (isTranslating || !promptText) return;
    
    setIsTranslating(true);
    
    try {
      // Nutze REF für aktuellen Wert (kein stale closure!)
      const currentIsGerman = isPromptGermanRef.current;
      const sourceLang = currentIsGerman ? 'de' : 'en';
      const targetLang = currentIsGerman ? 'en' : 'de';
      
      console.log('🌐 Translate:', sourceLang, '→', targetLang, '(isGerman:', currentIsGerman, ')');
      
      const res = await fetch(`${API_URL}/api/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: promptText,
          source_lang: sourceLang,
          target_lang: targetLang
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setPromptText(data.translated_text);
        const newIsGerman = !currentIsGerman;
        setIsPromptGerman(newIsGerman);
        isPromptGermanRef.current = newIsGerman;  // Ref auch updaten!
      }
    } catch (error) {
      console.error('Translation error:', error);
    } finally {
      setIsTranslating(false);
    }
  };
  const [superPrompt, setSuperPrompt] = useState("");
  const [jupiterRecommendations, setJupiterRecommendations] = useState(null);  // NEU: Jupiter Empfehlungen
  const [profileImageUrl, setProfileImageUrl] = useState(null);  // NEU: Profilbild aus DB
  const [sheetModalOpen, setSheetModalOpen] = useState(false);  // Referenz-Sheet-Modal
  const [juliaConversation, setJuliaConversation] = useState([]);  // NEU: Julia Conversation (bleibt erhalten!)
  
  // V6-STYLE: Neue Dropdown-States
  const [timeOfDay, setTimeOfDay] = useState('sunset');  // Smart Default
  const [weather, setWeather] = useState('clear');
  const [camera, setCamera] = useState('none');  // Default: keine Kamera-Perspektive
  const [bodyFraming, setBodyFraming] = useState('none');  // Default: automatisch (KI entscheidet)
  const [imageStyle, setImageStyle] = useState('none');  // Default: kein Style (Astria style parameter)
  const [referenceImage, setReferenceImage] = useState(null);  // Reference image URL
  const [referenceImagePreview, setReferenceImagePreview] = useState(null);  // Preview URL
  const [referenceImageRatio, setReferenceImageRatio] = useState(null);  // Detected ratio (e.g. "4:3")
  const [cropModalImage, setCropModalImage] = useState(null);  // Bild für Crop-Modal (base64)
  const [showCropModal, setShowCropModal] = useState(false);  // Crop-Modal anzeigen
  const [showFlowPicker, setShowFlowPicker] = useState(false);  // Flow-Auswahl nach Crop
  const [referenceFlowChoice, setReferenceFlowChoice] = useState(null);  // null bis User wählt
  const [cropAspectRatio, setCropAspectRatio] = useState(9/16);  // Gewähltes Crop-Format
  const [showFormatPicker, setShowFormatPicker] = useState(false);  // Format-Auswahl vor Crop
  const [isUploadingReference, setIsUploadingReference] = useState(false);  // Upload loading
  const [isAnalyzingImage, setIsAnalyzingImage] = useState(false);  // Image analysis loading

  // Geführte Bild-Generierung: null = Picker zeigen, sonst Form im jeweiligen Modus
  //   'scene'     = Text-Prompt only (kein Referenzbild)
  //   'composite' = User wird in eine Szene komponiert (backend reference_flow_choice='2.1')
  //   'rebuild'   = Vorlage-Bild wird mit User-Gesicht neu gebaut (backend '2.2')
  const [generationMode, setGenerationMode] = useState('scene');

  const enterGenerationMode = (mode) => {
    setGenerationMode(mode);
    // Bei Moduswechsel das Referenzbild zurücksetzen, damit der User nicht
    // versehentlich ein altes Bild im neuen Modus mitschleppt.
    setReferenceImage(null);
    setReferenceImagePreview(null);
    setReferenceImageRatio(null);
    // Backend-Mapping (Field-Namen verwirrend, hier die effektive Semantik):
    //   '2.2' = NanoBanana-Compositor → User wird IN die Referenz-Szene gesetzt → 'composite'
    //   '2.1' = img2img mit input_image_url → Bild wird nachgebaut mit User-Gesicht → 'rebuild'
    if (mode === 'scene')     setReferenceFlowChoice(null);
    if (mode === 'composite') setReferenceFlowChoice('2.2');
    if (mode === 'rebuild')   setReferenceFlowChoice('2.1');
  };
  const leaveGenerationMode = () => {
    setGenerationMode(null);
    setReferenceImage(null);
    setReferenceImagePreview(null);
    setReferenceImageRatio(null);
    setReferenceFlowChoice(null);
    // Prompt-Text und Advanced-Settings bleiben absichtlich erhalten —
    // der User hat dort ggf. schon Arbeit reingesteckt.
  };

  // Bei Referenz-Modi: Output-Format automatisch auf das Aspect-Ratio des
  // hochgeladenen Bildes setzen. Hochkant → portrait_9_16, Quer → landscape_16_9,
  // Quadrat/4:5 wird auf 9:16 gerundet. Aktiv in 'rebuild' (Bild komplett neu
  // gebaut, Format muss matchen) UND 'composite' (User in Szene eingefuegt,
  // Szenenformat = Output).
  useEffect(() => {
    if (generationMode !== 'rebuild' && generationMode !== 'composite') return;
    if (!referenceImagePreview || referenceImagePreview === 'loading') return;
    try {
      const img = new window.Image();
      img.onload = () => {
        const ratio = img.width / img.height;
        setImageFormat(ratio >= 1 ? 'landscape_16_9' : 'portrait_9_16');
      };
      img.src = referenceImagePreview;
    } catch (e) {}
  }, [generationMode, referenceImagePreview]);

  // 🎬 VIDEO EDITOR FUNCTIONS (angehoben aus Gallery)
  const addToQueue = (item) => {
    setAssemblyQueue(prev => {
      if (prev.find(q => q.id === item.id)) return prev;
      const newItem = { ...item, duration: item.type === 'image' ? 3 : 5 };
      return [...prev, newItem];
    });
  };

  const removeFromQueue = (id) => {
    setAssemblyQueue(prev => prev.filter(q => q.id !== id));
  };

  const moveInQueue = (fromIndex, toIndex) => {
    setAssemblyQueue(prev => {
      const newQueue = [...prev];
      const [item] = newQueue.splice(fromIndex, 1);
      newQueue.splice(toIndex, 0, item);
      return newQueue;
    });
  };
  
  const updateDuration = (id, newDuration) => {
    setAssemblyQueue(prev => prev.map(item => 
      item.id === id ? { ...item, duration: newDuration } : item
    ));
  };
  
  const clearQueue = () => {
    setAssemblyQueue([]);
    setAssemblyResult(null);
    setSelectedClipIndex(0);
    setActiveToolbar(null);
  };

  const isInQueue = (id) => assemblyQueue.some(q => q.id === id);
  
  // Clip auswählen
  const selectClip = (index) => {
    setSelectedClipIndex(index);
    setActiveToolbar(null); // Tool schließen bei Clip-Wechsel
  };
  
  // Text-Overlay zu gewähltem Clip hinzufügen (mit Position)
  const addTextToClip = (text) => {
    if (text?.trim() && assemblyQueue[selectedClipIndex]) {
      setAssemblyQueue(prev => prev.map((item, i) => 
        i === selectedClipIndex 
          ? { ...item, textOverlay: { text: text?.trim(), x: 50, y: 80 } } // x,y in % (Mitte-unten)
          : item
      ));
      setTextInput('');
      setActiveToolbar(null);
    }
  };
  
  // Text-Overlay entfernen
  const removeTextFromClip = (index) => {
    setAssemblyQueue(prev => prev.map((item, i) => 
      i === index ? { ...item, textOverlay: null } : item
    ));
  };
  
  // Text-Position im Preview aktualisieren
  const updateTextPosition = (index, x, y) => {
    setAssemblyQueue(prev => prev.map((item, i) => {
      if (i !== index || !item.textOverlay) return item;
      return { 
        ...item, 
        textOverlay: { ...item.textOverlay, x: Math.max(5, Math.min(95, x)), y: Math.max(5, Math.min(95, y)) }
      };
    }));
  };
  
  // State für aktives Text-Dragging
  const [draggingText, setDraggingText] = useState(false);
  const previewRef = useRef(null);
  
  const getTotalDuration = () => {
    return assemblyQueue.reduce((total, item) => total + (item.duration || 3), 0);
  };
  
  // 🎬 FETCH FERTIGE VIDEOS
  const fetchAssemblies = async () => {
    try {
      const response = await apiClient.get('/api/beatleap/videos');
      if (response.ok) {
        const data = await response.json();
        setAssemblies(data.videos || data.assemblies || []);
      }
    } catch (e) {
      console.error('❌ fetchAssemblies error:', e);
    } finally {
      setLoadingAssemblies(false);
    }
  };
  
  // 🎬 DELETE ASSEMBLY
  const deleteAssembly = async (id) => {
    if (!window.confirm('Video wirklich löschen?')) return;
    try {
      const response = await apiClient.delete(`/api/assembly/${id}`);
      if (response.ok) {
        setAssemblies(prev => prev.filter(a => a.id !== id));
      }
    } catch (e) {
      console.error('❌ deleteAssembly error:', e);
    }
  };

  const createAssembly = async () => {
    console.log('🎬 createAssembly called, queue:', assemblyQueue);
    
    if (assemblyQueue.length < 2) {
      alert('Mindestens 2 Assets benötigt');
      return;
    }
    
    setAssembling(true);
    setAssemblyResult(null);
    
    try {
      const assets = assemblyQueue.map(item => ({
        type: item.type,
        url: item.url,
        duration: item.type === 'image' ? (item.duration || 3) : undefined
      }));
      
      console.log('🎬 Sending assets:', assets);
      
      const response = await apiClient.post('/api/assembly/create', { 
        assets,
        title: `Video (${assemblyQueue.length} Clips)`
      });
      
      console.log('🎬 Response:', response.status, response.ok);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🎬 Success data:', data);
        setAssemblyResult({
          success: true,
          url: `https://snapshotpro.ai${data.video_url}`,
          clipCount: data.clip_count,
          duration: data.duration_seconds
        });
        setAssemblyQueue([]);
        fetchAssemblies(); // Fertige Videos neu laden
      } else {
        const error = await response.json();
        console.error('🎬 Error response:', error);
        setAssemblyResult({ success: false, error: error.detail || 'Assembly failed' });
      }
    } catch (e) {
      console.error('🎬 Exception:', e);
      setAssemblyResult({ success: false, error: e.message || 'Netzwerkfehler' });
    } finally {
      setAssembling(false);
    }
  };
  
  // NEU: Clean Prompt Mode - Chips nur bei Bedarf!
  const [advancedMode, setAdvancedMode] = useState(false);  // Standard: AUS
  const [style, setStyle] = useState('cinematic');
  const [trainingStartTime, setTrainingStartTime] = useState(null);  // Echte Startzeit vom Server
  const [substyle, setSubstyle] = useState('epic');  // NEU: Substil
  const [imageFormat, setImageFormat] = useState('portrait_9_16');
  const [numImages, setNumImages] = useState(1);  // Anzahl Bilder (Default 1 - User entscheidet bewusst)
  const [imageQuality, setImageQuality] = useState('medium');  // 'low' (Standard), 'medium' (HD), 'high' (Ultra)
  const [pricingSettings, setPricingSettings] = useState(null);  // Dynamische Preise aus Admin
  const [showMoreFormats, setShowMoreFormats] = useState(false);  // Progressive Disclosure für Formate
  const [dynamicOptions, setDynamicOptions] = useState(null);  // NEU: Dynamische Backend-Keywords!
  
  // AUTO-DETECT body_mode aus camera-Auswahl
  const getBodyModeFromCamera = (cam) => {
    if (cam === '85mm') return 'portrait';
    if (cam === '24mm' || cam === 'drone' || cam === 'top') return 'wide';
    return 'full';  // 35mm, 50mm, low
  };
  
  // Update bodyMode automatisch wenn Kamera geändert wird
  useEffect(() => {
    const newBodyMode = getBodyModeFromCamera(camera);
    setBodyMode(newBodyMode);
  }, [camera]);

  // Pricing-Settings aus Admin laden
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await apiClient.get('/api/pricing/settings/public');
        if (res.ok) {
          const data = await res.json();
          setPricingSettings(data);
        }
      } catch (err) {
        console.error('Pricing settings laden fehlgeschlagen:', err);
      }
    };
    fetchPricing();
  }, []);

  // Handler für Julia-Prompt-Übernahme
  const handleJuliaPromptApply = (text) => {
    setPromptText(text);
    setOriginalPromptDE(text);
    console.log('📋 Julia Prompt übernommen:', text);
    
    // Öffne "Bild erstellen" Accordion
    setAccordionOpen(prev => ({...prev, createImage: true}));
    
    // Scroll zum Prompt-Feld
    setTimeout(() => {
      const promptField = document.querySelector('[data-testid="dream-image-textarea"]');
      if (promptField) {
        promptField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        promptField.focus();
      }
    }, 300);
  };

  // Label & Keywords Mapping (für Live Prompt-Vorschau)
  const getLabelForValue = (category, value) => {
    const labels = {
      timeOfDay: {
        'sunrise': 'Sonnenaufgang',
        'morning': 'Morgen',
        'noon': 'Mittag',
        'afternoon': 'Nachmittag',
        'sunset': 'Sonnenuntergang',
        'golden_hour': 'Goldene Stunde',
        'blue_hour': 'Blaue Stunde',
        'evening': 'Abend',
        'night': 'Nacht',
        'midnight': 'Mitternacht'
      },
      weather: {
        'clear': 'Klar',
        'cloudy': 'Bewölkt',
        'overcast': 'Bedeckt',
        'foggy': 'Nebelig',
        'rainy': 'Regnerisch',
        'stormy': 'Stürmisch',
        'snowy': 'Schneefall'
      },
      camera: {
        '24mm': '24mm Weitwinkel',
        '35mm': '35mm Standard',
        '50mm': '50mm Portrait',
        '85mm': '85mm Tele',
        'drone': 'Drohne',
        'drone': 'Von oben',
        'low': 'Von unten'
      },
      style: {
        'cinematic': 'Cinematic',
        'action': 'Action',
        'editorial': 'Editorial',
        'vintage': 'Vintage',
        'documentary': 'Doku',
        'fashion': 'Fashion',
        'artistic': 'Künstlerisch'
      },
      image_format: {
        'square_1_1': 'Quadrat 1:1',
        'portrait_9_16': 'Portrait 9:16',
        'portrait_4_5': 'Portrait 4:5',
        'landscape_16_9': 'Landscape 16:9',
        'landscape_3_2': 'Landscape 3:2'
      }
    };
    
    return labels[category]?.[value] || value;
  };

  const getKeywordsForValue = (category, value) => {
    // PRIORITÄT 1: Dynamische Keywords aus Backend (wie V2!)
    if (dynamicOptions) {
      const categoryMap = {
        'timeOfDay': 'time_of_day',
        'weather': 'weather',
        'camera': 'camera',
        'style': 'styles'
      };
      
      const backendCategory = categoryMap[category];
      if (backendCategory && dynamicOptions[backendCategory]) {
        const option = dynamicOptions[backendCategory][value];
        if (option && typeof option === 'object') {
          return option.keywords || option.short || '';
        }
      }
    }
    
    // FALLBACK: Hardcodierte Keywords (falls Backend nicht verfügbar)
    const keywords = {
      timeOfDay: {
        'sunrise': 'early morning light, dawn, soft pink and orange hues',
        'morning': 'bright daylight, fresh morning atmosphere',
        'noon': 'overhead sun, bright natural light',
        'afternoon': 'warm afternoon light',
        'sunset': 'golden hour, warm orange and red tones',
        'golden_hour': 'magical golden light, long shadows',
        'blue_hour': 'twilight, deep blue sky, soft ambient light',
        'evening': 'dusk, fading light',
        'night': 'dark sky, artificial lights',
        'midnight': 'deep night, dramatic darkness'
      },
      weather: {
        'clear': 'clear sky, bright sunshine',
        'cloudy': 'soft diffused light, clouds',
        'overcast': 'gray sky, even lighting',
        'foggy': 'misty atmosphere, ethereal fog',
        'rainy': 'wet surfaces, rain drops',
        'stormy': 'dramatic clouds, moody atmosphere',
        'snowy': 'snow, winter atmosphere'
      },
      camera: {
        '24mm': 'wide angle lens, environmental context',
        '35mm': 'standard lens, natural perspective',
        '50mm': 'portrait lens, shallow depth of field',
        '85mm': 'telephoto lens, compressed perspective',
        'drone': 'aerial view, bird eye perspective',
        'drone': 'overhead shot, looking down',
        'low': 'low angle, looking up'
      },
      style: {
        'cinematic': 'cinematic lighting, film-like quality',
        'action': 'dynamic energy, motion',
        'editorial': 'magazine quality, professional',
        'vintage': 'retro aesthetic, nostalgic',
        'documentary': 'authentic, real-life feel',
        'fashion': 'stylish, trendy, fashionable',
        'artistic': 'creative, artistic interpretation'
      }
    };
    
    return keywords[category]?.[value] || '';
  };

  const handleJupiterRecommendations = (data) => {
    // data = { recommendations, summary, isTextOnly: true }
    setJupiterRecommendations(data);
    console.log('👩 Julia Empfehlungen erhalten:', data);
  };
  
  // Get current user from localStorage or props
  const user = currentUser || _getStoredUser();
  const userId = user.id;
  if (!userId) {
    console.error('No user ID found');
    return;
  }
  const isMasterAdmin = user.email === 'info@snapshotpro.ai';
  const isAdmin = isMasterAdmin || user.is_admin === true;
  
  // Handle Google OAuth session_id from URL fragment
  useEffect(() => {
    const handleGoogleOAuthCallback = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        setIsProcessingOAuth(true);
        
        try {
          // Extract session_id from URL fragment
          const sessionId = hash.split('session_id=')[1].split('&')[0];
          
          // Call backend to exchange session_id for JWT
          const response = await fetch(`${API_URL}/api/auth/google/callback`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ session_id: sessionId }),
          });
          
          if (!response.ok) {
            throw new Error('Google OAuth callback failed');
          }
          
          const data = await response.json();
          
          // Store token and user in localStorage
          localStorage.setItem('snapshot_token', data.token);
          localStorage.setItem('snapshot_user', JSON.stringify(data.user));
          
          // Clean URL fragment
          window.history.replaceState(null, '', window.location.pathname);
          
          // Reload to apply new user data
          window.location.reload();
        } catch (error) {
          console.error('Google OAuth error:', error);
          alert('Google Login fehlgeschlagen. Bitte versuchen Sie es erneut.');
          // Clean URL and redirect to landing
          window.history.replaceState(null, '', '/');
          window.location.href = '/';
        } finally {
          setIsProcessingOAuth(false);
        }
      }
    };
    
    handleGoogleOAuthCallback();
  }, []);
  
  // NEW: Multi-Avatar States (using real user data)
  const [avatars, setAvatars] = useState([
    {
      id: 'avatar_1',
      avatarName: user.name || 'Nicht festgelegt',
      idNumber: user.display_id ? String(user.display_id).padStart(6, '0') : '000000',
      credits: null,  // CRITICAL FIX: null = loading, 0 = tatsächlich 0
      createdAt: user.created_at || new Date().toISOString(),
      status: 'pending', // Will be updated based on training jobs
      profileImage: null
    }
  ]);
  
  // STEP 3: Persistent Avatar Selection via URL parameter
  const [activeAvatarIndex, setActiveAvatarIndex] = useState(() => {
    // Try to get avatar_id from URL
    const urlParams = new URLSearchParams(window.location.search);
    const avatarIdFromUrl = urlParams.get('avatarId');
    
    if (avatarIdFromUrl) {
      // Will be set properly after avatars are loaded
      return 0; // Temporary, will update in useEffect
    }
    
    // Otherwise use last selected from localStorage
    const lastAvatarIndex = localStorage.getItem('snapshot_last_avatar_index');
    return lastAvatarIndex ? parseInt(lastAvatarIndex) : 0;
  });
  
  // useRef to access current avatars in polling without causing re-renders
  const avatarsRef = useRef([]);
  const activeAvatarIndexRef = useRef(0);
  const lastLoadedAvatarIdRef = useRef(null); // Track last loaded gallery avatar
  const menuRef = useRef(null); // Ref for menu panel (touch-blocker)
  
  // Update refs when state changes
  useEffect(() => {
    avatarsRef.current = avatars;
    activeAvatarIndexRef.current = activeAvatarIndex;
  }, [avatars, activeAvatarIndex]);
  
  // Sync activeAvatarIndex with avatarId from URL when avatars load
  useEffect(() => {
    if (avatars.length === 0) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const avatarIdFromUrl = urlParams.get('avatarId');
    
    if (avatarIdFromUrl) {
      // Find avatar with this ID
      const avatarIndex = avatars.findIndex(a => a.id === avatarIdFromUrl);
      
      if (avatarIndex !== -1 && avatarIndex !== activeAvatarIndex) {
        console.log(`🔄 URL-based avatar switch: ${avatars[avatarIndex].avatarName}`);
        setActiveAvatarIndex(avatarIndex);
      }
    } else {
      // Reload-Restore nach Avatar-ID (NICHT Index): der gespeicherte Index ist instabil,
      // wenn sich die Avatar-Reihenfolge ändert → sonst falscher Avatar / leere Galerie nach Reload.
      const savedId = localStorage.getItem('snapshot_current_avatar_id'); // roh gespeichert (kein JSON)
      if (savedId) {
        const idx = avatars.findIndex(a => a.id === savedId);
        if (idx !== -1 && idx !== activeAvatarIndex) {
          console.log(`🔄 Reload-Restore auf gespeicherten Avatar (id): ${avatars[idx].avatarName}`);
          setActiveAvatarIndex(idx);
        }
      }
    }
  }, [avatars]); // Only run when avatars change
  
  // Persist activeAvatarIndex AND avatarId to localStorage on change
  useEffect(() => {
    if (avatars.length > 0 && activeAvatarIndex >= 0 && activeAvatarIndex < avatars.length) {
      localStorage.setItem('snapshot_last_avatar_index', activeAvatarIndex.toString());
      const currentAvatar = avatars[activeAvatarIndex];
      // 🔑 Speichere aktuelle Avatar-ID für BeatleapStudio/MagicCreate
      if (currentAvatar?.id) {
        localStorage.setItem('snapshot_current_avatar_id', currentAvatar.id);
      }
      console.log(`💾 Persisted active avatar: ${currentAvatar.avatarName} (index: ${activeAvatarIndex}, id: ${currentAvatar?.id})`);
    }
  }, [activeAvatarIndex, avatars]);
  
  // iOS-safe body scroll lock with fixed position
  useEffect(() => {
    if (menuOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      
      // Lock body & html with fixed position (iOS-safe)
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.overscrollBehavior = 'none';
      
      // Non-passive touch blocker for background only (using ref)
      const blockBg = (e) => {
        if (menuRef.current && !menuRef.current.contains(e.target)) {
          e.preventDefault();
        }
      };
      
      document.addEventListener('touchmove', blockBg, { passive: false });
      
      // Cleanup function
      return () => {
        // Restore scroll position
        const scrollY = -parseInt(document.body.style.top || '0', 10);
        
        // Unlock body & html
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        document.documentElement.style.overscrollBehavior = '';
        
        // Restore scroll
        window.scrollTo(0, scrollY);
        
        // Remove touch blocker
        document.removeEventListener('touchmove', blockBg);
      };
    }
  }, [menuOpen]);
  
  // Event-Listener für Profilbild-Updates (Avatar-spezifisch)
  useEffect(() => {
    // Im ReadOnly-Mode (Showcase) Event-Listener überspringen
    if (readOnlyMode) return;
    
    const handleProfileImageUpdate = (event) => {
      // Update avatars array mit neuem Profilbild
      setAvatars(prev => prev.map((avatar, index) => 
        index === activeAvatarIndex 
          ? { ...avatar, profileImage: event.detail.imageUrl }
          : avatar
      ));
      setProfileImageUrl(event.detail.imageUrl);
    };
    
    window.addEventListener('profileImageUpdated', handleProfileImageUpdate);
    return () => window.removeEventListener('profileImageUpdated', handleProfileImageUpdate);
  }, [activeAvatarIndex, readOnlyMode]);
  
  // Initial: Profilbild für aktuellen Avatar laden
  useEffect(() => {
    // Im ReadOnly-Mode (Showcase): Nutze currentUser.profile_image_url
    if (readOnlyMode) {
      if (currentUser?.profile_image_url) {
        setProfileImageUrl(currentUser.profile_image_url);
      }
      return;
    }
    
    // Normal-Mode: Nutze avatar.profileImage aus State
    const currentAvatar = avatars[activeAvatarIndex];
    if (currentAvatar?.profileImage) {
      setProfileImageUrl(currentAvatar.profileImage);
    } else {
      setProfileImageUrl(null);
    }
  }, [avatars, activeAvatarIndex, readOnlyMode, currentUser]);
  
  const userData = avatars[activeAvatarIndex];

  useEffect(() => () => clearDemoTimers(), []);

  useEffect(() => {
    if (readOnlyMode || !currentUser?.id) return undefined;

    let cancelled = false;

    const loadOnboardingConfig = async () => {
      try {
        const response = await apiClient.get('/api/onboarding/config');
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled) return;
        setOnboardingConfig(data.config || null);
        setOnboardingUserState(data.user_state || null);
        setDemoShowcase(data.config?.demo_showcase || null);
        if (
          data.config?.tour_active &&
          data.config?.demo_showcase &&
          data.user_state?.onboarding_completed === false
        ) {
          setDemoModeActive(true);
        }
      } catch (error) {
        console.error('Error loading onboarding config:', error);
      }
    };

    loadOnboardingConfig();

    return () => {
      cancelled = true;
    };
  }, [readOnlyMode, currentUser?.id]);
  
  // Mock-Imageen ENTFERNT - nur echte User-Daten anzeigen

  const getStatusConfig = (status) => {
    switch (status) {
      case 'training':
        return {
          color: 'bg-yellow-500',
          text: 'Training läuft',
          glow: 'shadow-yellow-500/50'
        };
      case 'ready':
        return {
          color: 'bg-[#10B981]',
          text: 'Bereit',
          glow: 'shadow-[#10B981]/50'
        };
      case 'error':
        return {
          color: 'bg-red-500',
          text: 'Fehler',
          glow: 'shadow-red-500/50'
        };
      default:
        return {
          color: 'bg-gray-500',
          text: 'Nicht erstellt',
          glow: 'shadow-gray-500/50'
        };
    }
  };

  const isDemoTourEligible = (
    !readOnlyMode &&
    userData?.status === 'pending' &&
    onboardingConfig?.tour_active === true &&
    onboardingUserState?.onboarding_completed === false &&
    !!demoShowcase
  );

  const demoShowcaseImageUrl = onboardingConfig?.selected_demo_image_url
    || demoShowcase?.generations?.flatMap((generation) => generation.image_urls || []).find(Boolean)
    || null;
  const selectedDemoGeneration = demoShowcase?.generations?.find((generation) =>
    (generation.image_urls || []).includes(demoShowcaseImageUrl)
  ) || null;
  const demoShowcaseVideo = onboardingConfig?.selected_demo_video
    || demoShowcase?.videos?.find((video) => video.id === onboardingConfig?.selected_demo_video_id)
    || demoShowcase?.videos?.find((video) => video.video_url)
    || null;
  const demoImagePrompt = selectedDemoGeneration?.prompt?.trim()
    || onboardingConfig?.default_image_prompt
    || 'Ein cineastisches Portrait bei Golden Hour mit weichem Licht und starkem Blick in die Kamera.';
  const demoVideoPromptText = demoShowcaseVideo?.user_prompt?.trim()
    || onboardingConfig?.default_video_prompt
    || 'Die Person blickt direkt in die Kamera, lacht kurz und macht einen kleinen Schritt nach vorn.';
  const effectiveDemoImageUrl = demoGeneratedImages[0]?.image_urls?.[0] || demoShowcaseImageUrl;
  const demoCardData = isDemoTourEligible ? {
    id: demoShowcase?.avatar?.id || 'demo-avatar',
    avatarName: onboardingConfig?.demo_avatar_display_name || demoShowcase?.avatar?.title || 'Demo Avatar',
    idNumber: onboardingConfig?.demo_avatar_display_id || '000099',
    createdAt: demoShowcase?.avatar?.created_at || new Date().toISOString(),
    status: 'ready'
  } : null;

  const getCountdownInfo = () => {
    const now = new Date();
    let expiryDate;

    // Kein Avatar vorhanden → nicht "abgelaufen" anzeigen
    if (!userData?.expires_at && !showcaseData?.avatar?.expires_at && !userData?.expiresAt && userData?.status !== 'ready') {
      return {
        color: 'bg-gray-500',
        text: 'Kein Avatar',
        glow: '',
        active: true  // active=true verhindert "Game Over"-Meldung
      };
    }

    // Priority 1: Use expires_at from userData (training_jobs)
    if (userData?.expires_at) {
      expiryDate = new Date(userData.expires_at);
    }
    // Priority 2: Use expires_at from showcaseData (Astria's real expiry)
    else if (showcaseData?.avatar?.expires_at) {
      expiryDate = new Date(showcaseData.avatar.expires_at);
    }
    // Priority 3: Use expiresAt from userData (if backend provides it)
    else if (userData?.expiresAt) {
      expiryDate = new Date(userData.expiresAt);
    }
    // Fallback: createdAt + 30 days (nur für ready-Avatare)
    else {
      const createdDate = userData?.created_at || userData?.createdAt;
      expiryDate = new Date(createdDate || new Date());
      expiryDate.setDate(expiryDate.getDate() + 30);
    }
    
    const daysLeft = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft > 7) {
      return {
        color: 'bg-[#10B981]',
        text: `Noch ${daysLeft} Tage aktiv`,
        glow: 'shadow-[#10B981]/50',
        active: true
      };
    } else if (daysLeft > 0 && daysLeft <= 7) {
      return {
        color: 'bg-yellow-500',
        text: `Noch ${daysLeft} Tage aktiv`,
        glow: 'shadow-yellow-500/50',
        active: true
      };
    } else {
      // Abgelaufen - aber prüfe auto_extend
      if (userData?.auto_extend) {
        return {
          color: 'bg-yellow-500',
          text: 'Verlängerung ausstehend',
          glow: 'shadow-yellow-500/50',
          active: true
        };
      }
      return {
        color: 'bg-red-500',
        text: 'Abgelaufen',
        glow: 'shadow-red-500/50',
        active: false
      };
    }
  };

  const statusConfig = getStatusConfig(userData?.status || 'pending');
  const countdownInfo = getCountdownInfo();
  const displayUserData = demoModeActive && isDemoTourEligible ? demoCardData : userData;
  const displayStatusConfig = demoModeActive && isDemoTourEligible ? getStatusConfig('ready') : statusConfig;
  const displayProfileImageUrl = demoModeActive && isDemoTourEligible
    ? (onboardingConfig?.demo_avatar_profile_image_url || demoShowcase?.avatar?.profile_image_url || demoShowcase?.user?.profile_image_url || null)
    : (profileImageUrl || userData?.profileImage || null);
  const displayCharacterSheetUrl = userData?.characterSheet || null;
  const canShowGeneratorSections = userData?.status === 'ready' || (demoModeActive && isDemoTourEligible);
  const canDeleteAvatar = userData?.status === 'ready' && !readOnlyMode && !(demoModeActive && isDemoTourEligible);
  const effectiveGalleryGenerations = demoModeActive && isDemoTourEligible
    ? [...demoGeneratedImages, ...(demoShowcase?.generations || [])]
    : photos;
  const effectiveShowcaseVideos = demoModeActive && isDemoTourEligible
    ? [...demoGeneratedVideos, ...(demoShowcase?.videos || [])]
    : (showcaseData?.videos || []);
  const effectivePendingImageJobs = demoModeActive && isDemoTourEligible ? demoPendingImageJobs : pendingImageJobs;

  const handleCookieSettings = () => {
    window.dispatchEvent(new Event('openCookieSettings'));
  };

  // NEW: Prompt Handler
  const handlePromptToggle = () => {
    setShowPromptField(!showPromptField);
    if (!showPromptField) {
      setPromptText('');
      setOriginalPromptDE('');
      setSuperPrompt('');
      setBodyMode('portrait');
    }
  };

  const resetDemoTourVisualState = () => {
    clearDemoTimers();
    setDemoBubble(null);
    setDemoPendingImageJobs([]);
    setDemoPendingVideos([]);
    setDemoVideoPrompt('');
    setDemoPlayingVideoUrl(null);
    setDemoRewardFlyouts([]);
    setVideoPreselectedModel(null);
    setVideoPreselectedPrompt(null);
    setAccordionOpen(prev => ({ ...prev, createImage: false, videoCreate: false }));
    setGalleryTab('images');
  };

  const launchDemoRewardFlyouts = () => {
    setDemoRewardFlyouts([
      {
        id: `demo-reward-image-${Date.now()}`,
        label: '+1 Gratis-Bild',
        targetSelector: '#header-free-images-anchor'
      },
      {
        id: `demo-reward-video-${Date.now() + 1}`,
        label: '+1 Gratis-Video',
        targetSelector: '#header-free-videos-anchor'
      }
    ]);
  };

  const handleSkipDemoTour = async () => {
    demoRunIdRef.current += 1;
    resetDemoTourVisualState();
    setDemoTourStarted(false);
    setDemoModal(null);
    setDemoModeActive(false);

    let rewardData = null;
    try {
      const response = await apiClient.post('/api/onboarding/complete');
      const data = response.ok ? await response.json() : null;
      rewardData = data;
      setOnboardingUserState(prev => prev ? {
        ...prev,
        onboarding_completed: true,
        onboarding_step: 'STEP_FINISH',
        free_images: data?.free_images ?? prev.free_images,
        free_video_credits: data?.free_video_credits ?? prev.free_video_credits
      } : prev);
      updateStoredUserForOnboarding({
        onboarding_completed: true,
        free_images: data?.free_images,
        free_video_credits: data?.free_video_credits
      });
    } catch (error) {
      console.error('Error skipping onboarding demo:', error);
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
    scheduleDemoTask(() => {
      launchDemoRewardFlyouts();
    }, 500);

    scheduleDemoTask(() => {
      setDemoModal({
        eyebrow: 'Direktstart',
        title: 'Alles klar, du möchtest direkt starten.',
        text: `Dein erster Avatar geht aufs Haus. Außerdem haben wir dir ${rewardData?.free_images ? '1 Gratis-Bild' : 'ein Gratis-Bild'} und ${rewardData?.free_video_credits ? '1 Gratis-Video' : 'ein Gratis-Video'} gutgeschrieben, damit du Snapshot direkt testen kannst.`,
        primaryLabel: 'Jetzt Avatar erstellen',
        onPrimary: () => {
          setDemoModal(null);
          setDemoBubble({
            eyebrow: 'Dein nächster Schritt',
            title: 'Beginne jetzt mit deinem eigenen Avatar.',
            text: 'Dein erster Avatar ist geschenkt. Danach kannst du direkt dein Gratis-Bild und dein Gratis-Video einlösen.',
            targetSelector: '[data-testid="create-first-avatar-button"]',
            placement: 'top',
            allowInteraction: true,
            targetLabel: 'Deinen ersten Avatar erstellen',
            onTargetClick: () => {
              setDemoBubble(null);
              onStartCreation();
            }
          });
        }
      });
    }, 1500);
  };

  const finishDemoTourWithRewards = async () => {
    demoRunIdRef.current += 1;
    resetDemoTourVisualState();
    setDemoTourStarted(false);
    setDemoModeActive(false);

    let rewardData = null;
    try {
      const response = await apiClient.post('/api/onboarding/complete');
      const data = response.ok ? await response.json() : null;
      rewardData = data;
      setOnboardingUserState(prev => prev ? {
        ...prev,
        onboarding_completed: true,
        onboarding_step: 'STEP_FINISH',
        free_images: data?.free_images ?? prev.free_images,
        free_video_credits: data?.free_video_credits ?? prev.free_video_credits
      } : prev);
      updateStoredUserForOnboarding({
        onboarding_completed: true,
        free_images: data?.free_images,
        free_video_credits: data?.free_video_credits
      });
    } catch (error) {
      console.error('Error completing onboarding demo:', error);
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
    scheduleDemoTask(() => {
      launchDemoRewardFlyouts();
    }, 500);

    scheduleDemoTask(() => {
      setDemoModal({
        eyebrow: 'Tour abgeschlossen',
        title: 'Herzlichen Glückwunsch — du hast die Tour geschafft.',
        text: `So einfach funktioniert Snapshot: Dein erster Avatar ist kostenlos. Außerdem haben wir dir ${rewardData?.free_images ? '1 Gratis-Bild' : 'ein Gratis-Bild'} und ${rewardData?.free_video_credits ? '1 Gratis-Video' : 'ein Gratis-Video'} zum Testen gutgeschrieben. Oben in der Leiste siehst du beides gleich eingebucht. Und jeden Tag wartet dort ein weiteres Gratis-Bild auf dich, das du 24 Stunden lang claimen kannst.`,
        primaryLabel: 'Zu meinem ersten Avatar',
        onPrimary: () => {
          setDemoModal(null);
          setDemoBubble({
            eyebrow: 'Dein nächster Schritt',
            title: 'Beginne jetzt mit deinem eigenen Avatar.',
            text: 'Dein erster Avatar ist geschenkt. Erstelle ihn jetzt und nutze danach direkt dein Gratis-Bild und dein Gratis-Video.',
            targetSelector: '[data-testid="create-first-avatar-button"]',
            placement: 'top',
            allowInteraction: true,
            targetLabel: 'Deinen ersten Avatar erstellen',
            onTargetClick: () => {
              setDemoBubble(null);
              onStartCreation();
            }
          });
        }
      });
    }, 1500);
  };

  const handleDemoVideoPlaybackEnded = () => {
    setDemoPlayingVideoUrl(null);
    finishDemoTourWithRewards();
  };

  const startDemoVideoSequence = async (sourceImageUrl = null) => {
    const runId = Date.now();
    demoRunIdRef.current = runId;
    clearDemoTimers();
    setDemoModal(null);
    setDemoBubble(null);
    setDemoVideoPrompt('');
    setVideoPreselectedModel('cinematic_video');
    setVideoPreselectedPrompt(null);
    const nextSourceImage = sourceImageUrl || effectiveDemoImageUrl || demoShowcaseImageUrl;
    setDemoVideoSourceImage(nextSourceImage);
    setVideoPreselectedImage(nextSourceImage);

    // === AKT A: Tray bleibt zu, Pfeil mit Klick-Puls auf den Tray-Header, dann öffnen ===
    // Bild-Akkordeon SCHON JETZT schließen — sonst springt das Layout erst
    // nach dem Klick-Puls zusammen und der Viewport wirkt wie "runtergescrollt".
    setAccordionOpen(prev => ({ ...prev, createImage: false }));
    setGalleryTab('videos');
    await waitForSelector('#demo-video-accordion-header');
    if (demoRunIdRef.current !== runId) return;
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    if (demoRunIdRef.current !== runId) return;
    await smoothScrollToSelector('#demo-video-accordion-header', 'start');
    if (demoRunIdRef.current !== runId) return;

    setDemoBubble({
      eyebrow: 'Video erstellen',
      title: 'Öffne jetzt den Video-Bereich.',
      text: 'Hier erwecken wir dein Bild zum Leben. Wir klicken gleich für dich drauf.',
      targetSelector: '#demo-video-accordion-header',
      placement: 'bottom',
      clickHint: true
    });
    await demoWait(1800);
    if (demoRunIdRef.current !== runId) return;

    setAccordionOpen(prev => ({ ...prev, videoCreate: true }));
    await waitForSelector('#demo-video-source-slider');
    if (demoRunIdRef.current !== runId) return;
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    if (demoRunIdRef.current !== runId) return;

    // === AKT B: Bild auswählen, Prompt eintippen, Generieren ===
    // Kein weiterer Scroll hier — der Tray klappt nach unten auf, wir bleiben
    // am Header stehen und zeigen die Bubbles direkt auf die neuen Inhalte.
    setDemoBubble({
      eyebrow: 'Schritt 1',
      title: 'Zuerst wählst du dein Bild aus.',
      text: 'Wir nehmen das Bild, das wir gerade erstellt haben, als Ausgangspunkt für dein Video.',
      targetSelector: '#demo-video-source-slider',
      placement: 'bottom'
    });
    await demoWait(2800);
    if (demoRunIdRef.current !== runId) return;

    setDemoBubble({
      eyebrow: 'Schritt 2',
      title: 'Dann gibst du deinen Prompt ein.',
      text: 'Je genauer du beschreibst, was dein Avatar tun soll, desto besser wird das Ergebnis.',
      targetSelector: '#demo-video-kino-textarea',
      placement: 'bottom'
    });
    await smoothScrollToSelector('#demo-video-kino-textarea');
    if (demoRunIdRef.current !== runId) return;

    await typewriteDemoText(
      demoVideoPromptText,
      setDemoVideoPrompt,
      30,
      '#demo-video-kino-textarea'
    );
    if (demoRunIdRef.current !== runId) return;

    setDemoBubble({
      eyebrow: 'Video erstellen',
      title: 'Jetzt starten wir die Videoerstellung.',
      text: 'Ein Klick reicht — dein Video landet gleich unten in der Galerie.',
      targetSelector: '#demo-video-generate-button',
      placement: 'top',
      clickHint: true
    });
    await smoothScrollToSelector('#demo-video-generate-button');
    if (demoRunIdRef.current !== runId) return;
    await demoWait(3000);
    if (demoRunIdRef.current !== runId) return;

    // === AKT C: Galerie, fertiges Video, Gratulation ===
    setDemoPendingVideos([{
      id: `demo-pending-video-${Date.now()}`,
      created_at: new Date().toISOString(),
      source_image_url: nextSourceImage,
      video_model: 'cinematic_video'
    }]);
    setDemoBubble(null);
    // Der Tab ist zwar schon auf "videos", aber die Pending-Video-Card muss
    // erst gemountet werden, bevor wir zu ihr scrollen können. Falls sie nach
    // dem Timeout immer noch nicht existiert, fallen wir auf den Gallery-Anker
    // zurück, damit der Scroll nach unten garantiert passiert.
    const pendingCard = await waitForSelector('#demo-pending-video-card', 3500);
    if (demoRunIdRef.current !== runId) return;
    const scrollTarget = pendingCard
      ? '#demo-pending-video-card'
      : '#video-gallery-section';
    await smoothScrollToSelector(scrollTarget, 'start');
    if (demoRunIdRef.current !== runId) return;

    setDemoBubble({
      eyebrow: 'Galerie',
      title: 'Dein Video wird erstellt.',
      text: 'Das dauert in der Regel 2 bis 3 Minuten. Hier zeigen wir dir den Fortschritt.',
      targetSelector: scrollTarget,
      placement: 'top'
    });

    await demoWait(6200);
    if (demoRunIdRef.current !== runId) return;

    const fakeVideo = demoShowcaseVideo ? {
      ...demoShowcaseVideo,
      id: `demo-video-${Date.now()}`,
      created_at: new Date().toISOString(),
      source_image_url: nextSourceImage,
      user_prompt: demoVideoPromptText
    } : null;

    setDemoPendingVideos([]);
    if (fakeVideo) {
      setDemoGeneratedVideos([fakeVideo]);
      await demoWait(1400);
      if (demoRunIdRef.current !== runId) return;
      setDemoPlayingVideoUrl(fakeVideo.video_url);
    } else {
      handleDemoVideoPlaybackEnded();
    }
  };

  const triggerDemoImageGeneration = async (runId, imagePrompt) => {
    setGalleryTab('images');
    setDemoPendingImageJobs([{ id: `demo-pending-image-${Date.now()}`, startTime: Date.now() }]);
    await waitForSelector('#demo-pending-image-card');
    if (demoRunIdRef.current !== runId) return;

    setDemoBubble({
      eyebrow: 'Galerie',
      title: 'Dein Bild wird erstellt.',
      text: 'Das dauert normalerweise 40 bis 70 Sekunden. Hier siehst du den laufenden Prozess.',
      targetSelector: '#demo-pending-image-card',
      placement: 'top'
    });
    await smoothScrollToSelector('#demo-pending-image-card', 'center');
    if (demoRunIdRef.current !== runId) return;

    await demoWait(5200);
    if (demoRunIdRef.current !== runId) return;

    const fakeGeneration = demoShowcaseImageUrl ? {
      id: `demo-generation-${Date.now()}`,
      image_urls: [demoShowcaseImageUrl],
      prompt: demoImagePrompt,
      settings: { image_format: 'portrait_9_16' },
      created_at: new Date().toISOString()
    } : null;

    setDemoPendingImageJobs([]);
    if (fakeGeneration) {
      setDemoGeneratedImages([fakeGeneration]);
      setDemoVideoSourceImage(fakeGeneration.image_urls[0]);
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (demoRunIdRef.current !== runId) return;

      await waitForSelector('#demo-generated-image-card');
      if (demoRunIdRef.current !== runId) return;
      await smoothScrollToSelector('#demo-generated-image-card', 'center');
      if (demoRunIdRef.current !== runId) return;

      setDemoBubble({
        eyebrow: 'Dein Bild',
        title: 'Dein Bild ist fertig.',
        text: 'Jetzt erwecken wir es zum Leben und machen daraus ein Video.',
        targetSelector: '#demo-generated-image-card',
        placement: 'top'
      });
      await demoWait(2600);
      if (demoRunIdRef.current !== runId) return;

      await startDemoVideoSequence(fakeGeneration.image_urls[0]);
      return;
    }
    await startDemoVideoSequence();
  };

  const startDemoImageSequence = async () => {
    const runId = Date.now();
    demoRunIdRef.current = runId;
    setDemoTourStarted(true);
    clearDemoTimers();
    setDemoModal(null);
    setDemoBubble(null);
    setPromptText('');
    setOriginalPromptDE('');
    setImageFormat('portrait_9_16');
    setNumImages(1);
    setAccordionOpen(prev => ({ ...prev, createImage: true, videoCreate: false }));

    await smoothScrollToSelector('[data-demo-anchor="image-create"]', 'start');
    if (demoRunIdRef.current !== runId) return;

    setDemoBubble({
      eyebrow: 'Schritt 1',
      title: 'Beschreibe, was fur ein Bild du haben mochtest.',
      text: 'Wir tippen jetzt einen Demo-Prompt fur dich ein.',
      targetSelector: '[data-testid="dream-image-textarea"]',
      placement: 'bottom'
    });
    await smoothScrollToSelector('[data-testid="dream-image-textarea"]');
    if (demoRunIdRef.current !== runId) return;

    const imagePrompt = demoImagePrompt;
    await typewriteDemoText(imagePrompt, (value) => {
      setPromptText(value);
      setOriginalPromptDE(value);
    }, 30, '[data-testid="dream-image-textarea"]');
    if (demoRunIdRef.current !== runId) return;
    await demoWait(900);
    if (demoRunIdRef.current !== runId) return;

    setDemoBubble({
      eyebrow: 'Format',
      title: 'Hier wahlst du das Bildformat.',
      text: 'Fur Social Content ist 9:16 ein sehr guter Start.',
      targetSelector: '[data-demo-image-format="portrait_9_16"]',
      placement: 'top'
    });
    await smoothScrollToSelector('[data-demo-image-format="portrait_9_16"]');
    if (demoRunIdRef.current !== runId) return;
    await demoWait(1500);
    if (demoRunIdRef.current !== runId) return;

    setDemoBubble({
      eyebrow: 'Bild erstellen',
      title: 'Jetzt starten wir die Demo-Bilderstellung.',
      text: 'Ein Klick reicht aus, dann landet das Ergebnis direkt unten in deiner Galerie.',
      targetSelector: '#demo-image-generate-button',
      placement: 'top',
      clickHint: true
    });
    await smoothScrollToSelector('#demo-image-generate-button');
    if (demoRunIdRef.current !== runId) return;
    await demoWait(3000);
    if (demoRunIdRef.current !== runId) return;
    await triggerDemoImageGeneration(runId, imagePrompt);
  };

  useEffect(() => {
    if (!isDemoTourEligible || !demoModeActive || demoTourStarted || demoModal || demoBubble || demoGeneratedImages.length > 0 || demoGeneratedVideos.length > 0 || demoPendingImageJobs.length > 0 || demoPendingVideos.length > 0 || demoPlayingVideoUrl) {
      return;
    }

    setDemoModal({
      eyebrow: 'Willkommen',
      title: 'Willkommen bei Snapshot.',
      text: 'Wir zeigen dir kurz, wie einfach Snapshot funktioniert. Du siehst gleich einen Demo-Avatar, eine Demo-Galerie und den Weg von Bild zu Video.',
      primaryLabel: 'Zeig mir wie es geht',
      secondaryLabel: 'Tour überspringen',
      onPrimary: startDemoImageSequence,
      onSecondary: handleSkipDemoTour
    });
  }, [
    demoBubble,
    demoGeneratedImages.length,
    demoGeneratedVideos.length,
    demoModal,
    demoModeActive,
    demoTourStarted,
    demoPendingImageJobs.length,
    demoPendingVideos.length,
    demoPlayingVideoUrl,
    isDemoTourEligible
  ]);

  // V6-STYLE: Generate Images (Backend baut Prompt!)
  const handleCreateSuperImage = async () => {
    const jobIds = addPendingImageJobs(numImages);

    // Sofort zur Galerie scrollen
    setGalleryTab('images');
    requestAnimationFrame(() => {
      document.querySelector('[data-testid="gallery-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    // SNAPSHOT aller Werte BEVOR irgendwas geleert wird
    const _prompt = promptText;
    const _numImages = numImages;
    const _imageFormat = imageFormat;
    const _flowChoice = referenceFlowChoice;

    // Referenzbild: Mehrere Quellen prüfen (React State ist oft veraltet)
    let _refImageFinal = referenceImage || window.__lastRefImageUrl || null;

    // Falls Preview da aber keine URL → Upload noch nicht fertig, warten
    if (referenceImagePreview && !_refImageFinal) {
      console.log('⏳ Warte auf Referenzbild-Upload...');
      for (let i = 0; i < 100 && !_refImageFinal; i++) {
        await new Promise(r => setTimeout(r, 100));
        _refImageFinal = window.__lastRefImageUrl || null;
      }
      if (!_refImageFinal) console.error('❌ Referenzbild-Upload timeout!');
    }

    console.log('📸 Generate — Ref:', _refImageFinal?.substring(0, 60), 'Flow:', _flowChoice, 'Prompt:', _prompt?.substring(0, 40));

    // JETZT erst Formular leeren + zur Galerie scrollen
    setPromptText('');
    setSuperPrompt('');
    setShowPromptField(false);
    setReferenceImage(null);
    setReferenceImagePreview(null);
    setReferenceImageRatio(null);
    setReferenceFlowChoice(null);
    window.__lastRefImageUrl = null;
    setNumImages(1);
    
    try {
      // Get user ID
      const user = _getStoredUser();
      const userId = user.id;
      
      // Find training job
      const trainingRes = await apiClient.get('/api/training/list');
      const trainingData = await trainingRes.json();
      const jobs = trainingData.jobs || [];
      
      // Get active avatar
      const activeAvatar = avatars[activeAvatarIndex];
      const readyJob = jobs.find(j => j.id === activeAvatar.id && j.status === 'ready');
      
      if (!readyJob) {
        throw new Error('Aktiver Avatar ist nicht ready oder nicht gefunden');
      }
      
      // ÜBERSETZE User-Text zu Englisch (API braucht Englisch!)
      // FIX 2026-02-25: IMMER übersetzen wenn User auf Deutsch ist!
      // FIX 2026-02-25 V2: Backend-API nutzen (POST) statt Google URL (GET)
      // Problem: Google Translate URL hat Längen-Limit → lange Texte werden abgeschnitten!
      const translateToEnglish = async (text) => {
        if (!text || !text?.trim()) return "";

        // Automatische Spracherkennung: Prüfe ob Text bereits Englisch ist
        // Einfache Heuristik: Deutsche Wörter erkennen
        const germanIndicators = /\b(und|oder|ich|ein|eine|der|die|das|ist|mit|von|auf|für|als|auch|nicht|aber|wie|wenn|wird|hat|bin|vor|nach|über|unter|durch|mein|dein|sein|ihr|wir|sie|bei|vom|zum|zur|dem|den|des)\b/i;
        const isLikelyGerman = germanIndicators.test(text);

        if (!isLikelyGerman) {
          // Text scheint bereits Englisch → direkt senden
          console.log('🌐 Text appears to be English, sending as-is');
          return text;
        }

        // Text ist Deutsch → übersetzen via Backend API
        console.log('🌐 German text detected → translating to English...');

        try {
          const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
          const res = await fetch(`${BACKEND_URL}/translate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              text: text,
              source_lang: 'de',
              target_lang: 'en'
            })
          });

          if (!res.ok) {
            console.warn('Backend translation failed, trying Google fallback...');
            const googleUrl = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=de&tl=en&dt=t&q=${encodeURIComponent(text.substring(0, 2000))}`;
            const googleRes = await fetch(googleUrl);
            if (googleRes.ok) {
              const googleData = await googleRes.json();
              return googleData[0]?.[0]?.[0] || text;
            }
            return text;
          }

          const data = await res.json();
          const translated = data.translated_text || data.translated || text;
          console.log('🌐 Translation:', text.substring(0, 40) + '... → ' + translated.substring(0, 40) + '...');
          return translated;
        } catch (error) {
          console.error('Translation error:', error);
          return text;
        }
      };
      
      const translatedText = await translateToEnglish(_prompt);

      console.log('✅ V6-GENERATION: Sende Dropdown-Werte an Backend...');
      console.log('   User-Text (Original):', _prompt);
      console.log('   User-Text (Englisch):', translatedText);
      console.log('   Tageszeit:', timeOfDay);
      console.log('   Wetter:', weather);
      console.log('   Kamera:', camera);
      console.log('   Bildstil:', style);
      console.log('   Format:', imageFormat);
      
      // STEP 1: Backend baut Prompt
      // CLEAN MODE: Wenn Erweiterte Optionen AUS → Nur User-Text (1:1 für ChatGPT-Prompts!)
      const requestBody = {
        training_id: readyJob.id,
        prompt: _prompt || '',  // DEUTSCH! Foto-Director übernimmt Übersetzung + Prompt-Engineering
        display_prompt: _prompt || '',  // DEUTSCH! Original User-Input für History
        num_images: _numImages,
        image_format: _imageFormat,
        quality: imageQuality,  // 'low' (Standard) | 'medium' (HD) | 'high' (Ultra)
        clean_mode: !advancedMode,  // NEU: Clean Mode wenn Chips AUS
        camera: camera,  // IMMER senden (auch im Clean Mode!)
        body_mode: getBodyModeFromCamera(camera),  // IMMER senden
        body_framing: bodyFraming,  // NEU: Bildausschnitt (none = Auto, full_body, half_body, portrait)
        astria_style: imageStyle !== 'none' ? imageStyle : null,  // Astria Style Parameter (null = kein Style)
      };
      
      // Reference Image
      if (_refImageFinal) {
        requestBody.image_reference_url = _refImageFinal;
        requestBody.reference_strength = 0.7;
        requestBody.reference_flow_choice = _flowChoice || '2.1';
        console.log('🖼️ Reference image:', _refImageFinal);
        console.log('🔀 Flow override:', _flowChoice || '2.1');
      } else if (_flowChoice) {
        // Flow-Override IMMER senden wenn gesetzt (unabhängig ob Bild-URL schon da)
        requestBody.reference_flow_choice = _flowChoice;
        console.log('🔀 Flow override:', _flowChoice);
      }
      
      // NUR wenn erweiterte Optionen aktiv: ALLE Chip-Werte hinzufügen
      if (advancedMode) {
        requestBody.time_of_day = timeOfDay;
        requestBody.weather = weather;
        requestBody.style = style;
        requestBody.substyle = substyle;
        requestBody.portrait_style = 'natural';
        requestBody.cinematic_mood = 'golden';
        console.log('✅ Advanced Mode: Alle Chips werden mitgesendet');
      } else {
        console.log('✅ Clean Mode: User-Text + Kamera:', camera);
      }
      
      // TIMEOUT: 5 Minuten für die Multi-Agent Pipeline + Astria-Generierung
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 Minuten

      let res;
      try {
        // Domain-relativ — läuft immer gegen die Domain, von der das HTML stammt.
        // Vermeidet doppeltes /api/ wenn REACT_APP_BACKEND_URL bereits auf .../api endet,
        // und vermeidet Cross-Domain-Calls (Dev→Live) bei Dev-Tests.
        res = await fetch(`/api/generate?avatar_id=${activeAvatar.id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${_getStoredToken()}`
          },
          body: JSON.stringify(requestBody),
          signal: controller.signal
        });
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          throw new Error('Zeitüberschreitung - bitte erneut versuchen');
        }
        throw fetchError;
      }
      clearTimeout(timeoutId);
      
      // Check status BEFORE reading body (gleicher Fix wie Training!)
      if (!res.ok) {
        const errorText = await res.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { detail: 'Image fehlgeschlagen' };
        }
        throw new Error(errorData.detail || 'Image fehlgeschlagen');
      }
      
      // Success: Read body only once
      const data = await res.json();
      console.log('✅ Image erfolgreich:', data);

      // Optimistisch das frische Ergebnis SOFORT in die Galerie übernehmen —
      // unabhängig vom (kurz Redis-gecachten) /generations/list. Verhindert, dass
      // der Platzhalter stehen bleibt bzw. das Bild erst nach einem Reload erscheint.
      const _newUrls = data.image_urls || [];
      if (_newUrls.length > 0) {
        const optimisticGen = {
          id: data.generation_id,
          generation_id: data.generation_id,
          image_urls: _newUrls,
          status: 'completed',
          avatar_id: activeAvatar.id,
          created_at: new Date().toISOString(),
          prompt: _prompt,
        };
        setPhotos(prev => {
          const list = prev || [];
          if (list.some(g => (g.id || g.generation_id) === data.generation_id)) return list;
          return [optimisticGen, ...list];
        });
      }
      // Placeholder entfernen → nahtloser Übergang
      removePendingImageJobs(jobIds);
      // Echte Daten (reale generation_id + Thumbnails) nachziehen — fresh=1 umgeht den
      // 60s-Server-Cache, damit das neue Bild GARANTIERT geliefert wird und nicht durch
      // eine stale Liste wieder aus der Galerie fliegt. Zweiter Lauf zieht die Thumbnails nach.
      setTimeout(() => { fetchPhotos(null, { fresh: true }); }, 1500);
      setTimeout(() => { fetchPhotos(null, { fresh: true }); }, 8000);

      // Prompt History wird jetzt server-seitig gespeichert (in /api/generate)
      // History aktualisieren wenn Accordion offen
      if (accordionOpen.quickActions) loadPromptHistory();

      // UI bereits oben zurückgesetzt (vor dem API-Call)
      
      // 🎬 Tab auf Bilder wechseln (nicht Videos!)
      setGalleryTab('images');
      
      // Auto-Scroll zur Pending-Animation
      setTimeout(() => {
        const pendingCard = document.getElementById('pending-image-card');
        if (pendingCard) {
          pendingCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          const gallerySection = document.querySelector('[data-testid="gallery-section"]');
          if (gallerySection) {
            gallerySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, 300);
      
    } catch (error) {
      console.error('❌ Image error:', error);
      // Netzwerkfehler, Timeout, Tab-Wechsel → KEIN Alert
      const msg = error?.message || '';
      if (msg.includes('Load failed') || msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('aborted') || msg.includes('Zeitüberschreitung') || msg.includes('timeout')) {
        console.log('⚠️ Netzwerk/Timeout — Generierung läuft im Hintergrund, lade Galerie nach...');
        // Galerie nach 10s und 30s nachladen — Bild kommt im Hintergrund
        setTimeout(() => { fetchPhotos(); }, 10000);
        setTimeout(() => { fetchPhotos(); removePendingImageJobs(jobIds); }, 30000);
      } else if (msg.includes('402') || msg.includes('SNAPs') || msg.includes('Credits')) {
        setImageError(msg);
        removePendingImageJobs(jobIds);
      } else {
        console.error('Generierungsfehler:', msg);
        // Auch hier: Galerie nachladen falls Bild trotzdem generiert wurde
        setTimeout(() => { fetchPhotos(); removePendingImageJobs(jobIds); }, 15000);
      }
    }
  };

  // REMIX: Generate variations of existing image
  const handleRemix = async (remixData) => {
    const remixJobIds = addPendingImageJobs(remixData.num_images || 4);
    
    try {
      const user = _getStoredUser();
      const userId = user.id;
      
      const trainingRes = await apiClient.get('/api/training/list');
      const trainingData = await trainingRes.json();
      const jobs = trainingData.jobs || [];
      
      const activeAvatar = avatars[activeAvatarIndex];
      const readyJob = jobs.find(j => j.id === activeAvatar.id && j.status === 'ready');
      
      if (!readyJob) {
        throw new Error('Aktiver Avatar ist nicht ready');
      }
      
      console.log('🎨 REMIX: Starting remix image...');
      console.log('   Original Prompt:', remixData.prompt);
      console.log('   Change Request:', remixData.changeRequest);
      console.log('   Reference Image:', remixData.imageUrl);
      console.log('   Reference Strength:', remixData.referenceStrength);
      console.log('   Num Images:', remixData.num_images);
      
      // Remix-Image Request
      const res = await apiClient.post(`/api/generate?avatar_id=${activeAvatar.id}`, {
        training_id: readyJob.id,
        prompt: remixData.prompt,                    // Original-Beschreibung
        change_request: remixData.changeRequest,     // NEU: Separater Änderungswunsch
        num_images: remixData.num_images || 4,
        time_of_day: remixData.settings.time_of_day,
        weather: remixData.settings.weather,
        camera: remixData.settings.camera,
        style: remixData.settings.style,
        substyle: remixData.settings.substyle,
        image_format: remixData.settings.image_format,
        // REMIX-specific params
        image_reference_url: remixData.imageUrl,
        reference_strength: remixData.referenceStrength,
        is_remix: true,
        original_image_id: remixData.imageId,
        // Legacy params
        body_mode: 'portrait',
        portrait_style: 'natural',
        cinematic_mood: 'golden'
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { detail: 'Remix fehlgeschlagen' };
        }
        throw new Error(errorData.detail || 'Remix fehlgeschlagen');
      }
      
      const data = await res.json();
      console.log('✅ Remix erfolgreich:', data);

      // Warte auf Generierung (reload gallery bis Bilder da sind)
      await fetchPhotos();
      
      // Toast Feedback (dynamisch mit korrekter Bildanzahl)
      const numImages = remixData.num_images || 4;
      const imageText = numImages === 1 ? 'Bild' : 'Bilder';
      const toast = document.createElement('div');
      toast.textContent = `✅ ${numImages} Remix-${imageText} erfolgreich generiert!`;
      toast.className = 'fixed top-20 left-1/2 transform -translate-x-1/2 bg-[#10B981] text-black px-6 py-3 rounded-xl font-bold shadow-xl z-50 animate-bounce';
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 4000);
      
      // Auto-Scroll zur Pending-Animation
      setTimeout(() => {
        const pendingCard = document.getElementById('pending-image-card');
        if (pendingCard) {
          pendingCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          const gallerySection = document.querySelector('[data-testid="gallery-section"]');
          if (gallerySection) {
            gallerySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, 300);
      
    } catch (error) {
      console.error('❌ Remix error:', error);
      const msg = error?.message || '';
      if (!msg.includes('Load failed') && !msg.includes('Failed to fetch') && !msg.includes('NetworkError') && !msg.includes('aborted')) {
        if (msg.includes('402') || msg.includes('SNAPs') || msg.includes('Credits')) {
          setImageError(msg);
        } else {
          alert(msg || 'Fehler beim Remixen');
        }
        removePendingImageJobs(remixJobIds);
      }
    }
  };

  // URL → Referenzbild hochladen
  // Gemeinsame File-Upload-Logik (für onChange + Drag&Drop)
  const handleFileSelected = (file) => {
    if (!file || isUploadingReference || referenceImagePreview) return;
    const fname = (file.name || '').toLowerCase();
    const isHeic = fname.endsWith('.heic') || fname.endsWith('.heif') || (file.type || '').includes('heic') || (file.type || '').includes('heif');

    if (isHeic) {
      setReferenceImagePreview('loading');
      setReferenceFlowChoice('2.1');
      uploadReferenceImage(file, false).then(() => {
        const url = window.__lastRefImageUrl;
        if (url) {
          setReferenceImagePreview(url);
          const img = new window.Image();
          img.onload = () => {
            const ratio = img.width / img.height;
            if (ratio < 0.7) setImageFormat('portrait_9_16');
            else if (ratio > 1.4) setImageFormat('landscape_16_9');
            else if (ratio > 0.9 && ratio < 1.1) setImageFormat('square_1_1');
            else if (ratio <= 0.9) setImageFormat('portrait_4_5');
            else setImageFormat('landscape_16_9');
          };
          img.src = url;
        }
      });
    } else {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target.result;
        setReferenceImagePreview(dataUrl);
        setReferenceFlowChoice('2.1');
        const img = new window.Image();
        img.onload = () => {
          const ratio = img.width / img.height;
          if (ratio < 0.7) setImageFormat('portrait_9_16');
          else if (ratio > 1.4) setImageFormat('landscape_16_9');
          else if (ratio > 0.9 && ratio < 1.1) setImageFormat('square_1_1');
          else if (ratio <= 0.9) setImageFormat('portrait_4_5');
          else setImageFormat('landscape_16_9');
        };
        img.src = dataUrl;
        const base64 = dataUrl.split(',')[1];
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: file.type || 'image/jpeg' });
        uploadReferenceImage(blob, true);
      };
      reader.readAsDataURL(file);
    }
  };

  const [isDragging, setIsDragging] = useState(false);

  const handleUrlUpload = async (url) => {
    if (isUploadingReference) return;
    setIsUploadingReference(true);
    try {
      const res = await fetch(`/api/image-proxy-base64`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url })
      });
      if (res.ok) {
        const data = await res.json();
        setReferenceImagePreview(data.data_url);
        setReferenceFlowChoice('2.1');
        // Bildformat erkennen und vorauswählen
        const img = new window.Image();
        img.onload = () => {
          const ratio = img.width / img.height;
          if (ratio < 0.7) setImageFormat('portrait_9_16');
          else if (ratio > 1.4) setImageFormat('landscape_16_9');
          else if (ratio > 0.9 && ratio < 1.1) setImageFormat('square_1_1');
          else if (ratio <= 0.9) setImageFormat('portrait_4_5');
          else setImageFormat('landscape_16_9');
        };
        img.src = data.data_url;
        // Base64 → Blob ohne fetch() (CSP blockiert data: URLs)
        const base64 = data.data_url.split(',')[1];
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: 'image/jpeg' });
        await uploadReferenceImage(blob, true);
      }
    } catch (err) {
      console.error('URL upload error:', err);
    } finally {
      setIsUploadingReference(false);
    }
  };

  // Upload Reference Image (direkt oder nach Crop)
  const uploadReferenceImage = async (fileOrBlob, isBlob = false) => {
    setIsUploadingReference(true);
    try {
      const formData = new FormData();
      if (isBlob) {
        formData.append('file', fileOrBlob, 'upload.jpg');
      } else {
        // File-Objekt: Dateiname beibehalten oder Fallback
        const fname = fileOrBlob.name || 'upload.jpg';
        formData.append('file', fileOrBlob, fname);
      }
      
      const res = await fetch(`/api/reference-image/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${_getStoredToken()}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setReferenceImage(data.url);
        setReferenceImagePreview(data.url);
        // Auto-Preselect von Bildformat + Ratio aus den tatsächlichen Bildmaßen
        const w = Number(data.finalWidth || data.originalWidth) || 0;
        const h = Number(data.finalHeight || data.originalHeight) || 0;
        if (w > 0 && h > 0) {
          const ratio = w / h;
          const formats = [
            { value: 'portrait_9_16', label: '9:16', r: 9 / 16 },
            { value: 'portrait_4_5',  label: '4:5',  r: 4 / 5 },
            { value: 'square_1_1',    label: '1:1',  r: 1 },
            { value: 'landscape_16_9',label: '16:9', r: 16 / 9 },
          ];
          let best = formats[0];
          let bestDiff = Infinity;
          for (const f of formats) {
            const d = Math.abs(Math.log(ratio) - Math.log(f.r));
            if (d < bestDiff) { bestDiff = d; best = f; }
          }
          setImageFormat(best.value);
          setReferenceImageRatio(best.label);
        } else {
          setReferenceImageRatio('9:16');
        }
        window.__lastRefImageUrl = data.url;
        console.log('✅ Referenzbild hochgeladen:', data.url);
      } else {
        const errText = await res.text().catch(() => '');
        console.error('Upload failed:', res.status, errText);
        alert(`Upload fehlgeschlagen (${res.status}): ${errText.substring(0, 100)}`);
        setReferenceImagePreview(null);
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert(`Upload fehlgeschlagen: ${err.message}`);
      setReferenceImagePreview(null); 
    } finally { 
      setIsUploadingReference(false); 
    }
  };

  // Crop-Modal Callback - gecroptes Bild hochladen
  const handleCropComplete = async (croppedData) => {
    setShowCropModal(false);
    setCropModalImage(null);

    // Zeige Preview sofort
    setReferenceImagePreview(croppedData.base64);

    // Lade gecroptes Bild hoch
    await uploadReferenceImage(croppedData.blob, true);

    // Flow-Auswahl Modal anzeigen
    setShowFlowPicker(true);
  };

  const handleGenerateFromPrompt = async () => {
    // CHIP-FIRST: Text ist OPTIONAL!

    const promptJobIds = addPendingImageJobs(4);

    try {
      // Get user ID
      const user = _getStoredUser();
      const userId = user.id;
      
      // Find training job
      const trainingRes = await apiClient.get('/api/training/list');
      const trainingData = await trainingRes.json();
      const jobs = trainingData.jobs || [];
      
      // Get active avatar (not just first ready job!)
      const activeAvatar = avatars[activeAvatarIndex];
      const readyJob = jobs.find(j => j.id === activeAvatar.id && j.status === 'ready');
      
      if (!readyJob) {
        throw new Error('Aktiver Avatar ist nicht ready oder nicht gefunden');
      }
      
      console.log('✅ Using active avatar:', activeAvatar.avatarName, 'ID:', readyJob.id);
      console.log("🔎 ACTIVE AVATAR FULL OBJECT:", activeAvatar);
      
      // Get gender token - FIX für 422 "must include ohwx mann/frau"
      let genderToken = 'man'; // Fallback

      // Prio 1: Avatar-Gender aus UI (case-insensitive!)
      const avatarGender = activeAvatar?.gender?.toLowerCase();
      if (avatarGender === 'woman' || avatarGender === 'frau') {
        genderToken = 'woman';
      } else if (avatarGender === 'man' || avatarGender === 'mann') {
        genderToken = 'man';
      } else if (avatarGender === 'child' || avatarGender === 'kind') {
        genderToken = 'child';
      }
      
      // Prio 2: API Response (falls Avatar-Gender nicht gesetzt)
      if (!activeAvatar?.gender) {
        const astriaName = readyJob.astria_response?.name?.toLowerCase();
        if (astriaName === 'woman' || astriaName === 'frau') {
          genderToken = 'woman';
        } else if (astriaName === 'man' || astriaName === 'mann') {
          genderToken = 'man';
        } else if (astriaName === 'child' || astriaName === 'kind') {
          genderToken = 'child';
        }
      }
      
      console.log('🔍 Gender Token:', genderToken, '(Avatar Gender:', activeAvatar?.gender, ', API Response:', readyJob.astria_response?.name, ')');
      
      // FOTOREGISSEUR: Immer den rohen User-Text senden (deutsch)
      // Der Backend Foto-Director übernimmt Übersetzung + Prompt-Generierung
      const basePrompt = (promptText && promptText.trim()) || "";
      
      const finalPromptWithTune = basePrompt;
      
      console.log('🎨 Final Prompt sent to API:', finalPromptWithTune);
      console.log('📋 User Input:', promptText);
      console.log('⚙️ Feintuning Selections:', feintuningSelections);
      console.log('✨ Super Prompt aktiv:', !!superPrompt);
      
      // AVATAR-GUARD: Send avatar_id as required parameter
      const requestBody = {
        training_id: readyJob.id,
        prompt: finalPromptWithTune,
        display_prompt: originalPromptDE || promptText || '',
        num_images: 4,
        // V6-STYLE: Dropdown-Parameter senden!
        time_of_day: timeOfDay,
        weather: weather,
        camera: camera,
        style: style,
        image_format: imageFormat,
        // ALTE Parameter (für Abwärtskompatibilität)
        body_mode: bodyMode || 'portrait',
        portrait_style: portraitStyle || 'natural',
        cinematic_mood: cinematicMood || 'golden'
      };

      // Referenzbild mitsenden (für Director-Analyse + Composite)
      if (referenceImage) {
        requestBody.image_reference_url = referenceImage;
        requestBody.reference_strength = 0.7;
        requestBody.reference_flow_choice = referenceFlowChoice || '2.1';
        console.log('🖼️ Reference image mitgesendet:', referenceImage);
        console.log('🔀 Flow override:', referenceFlowChoice || '2.1');
      }

      const res = await apiClient.post(`/api/generate?avatar_id=${activeAvatar.id}`, requestBody);
      
      // Check status BEFORE reading body
      if (!res.ok) {
        const errorText = await res.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { detail: 'Image fehlgeschlagen' };
        }
        throw new Error(errorData.detail || 'Image fehlgeschlagen');
      }
      
      // Read body only ONCE
      const data = await res.json();
      console.log('✅ Image erfolgreich:', data);

      // Galerie neu laden — Bilder sind jetzt auf dem Server fertig
      // DANN erst den Placeholder entfernen → nahtloser Übergang
      await fetchPhotos();
      removePendingImageJobs(promptJobIds);

      // History aktualisieren wenn Accordion offen
      if (accordionOpen.quickActions) loadPromptHistory();

      console.log('✅ Image complete, gallery reloaded for Avatar:', activeAvatar?.avatarName);
      
      // Close prompt field + Reset ALL states (batch update für sauberes UI Reset)
      setShowPromptField(false);
      setPromptText('');
      setOriginalPromptDE('');
      setSuperPrompt('');
      setBodyMode('portrait');
      
      // 🎬 Tab auf Bilder wechseln + zur Galerie scrollen
      setGalleryTab('images');
      setTimeout(() => {
        const gallerySection = document.getElementById('video-gallery-section');
        if (gallerySection) {
          gallerySection.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
      }, 50);
      
    } catch (error) {
      console.error('❌ Image error:', error);
      // Bei echtem Fehler: Placeholder entfernen
      removePendingImageJobs(promptJobIds);
    }
  };

  // NEW: Add new Avatar
  const handleAddAvatar = () => {
    onStartCreation(); // This goes to the wizard
  };

  // NEW: Switch active avatar
  const handleSwitchAvatar = (index) => {
    const newAvatar = avatars[index];
    // Sofort alte Daten leeren → keine Avatar-Vermischung
    setPhotos([]);
    setLoadingGallery(true);
    setActiveAvatarIndex(index);
    // Pass avatar_id directly to avoid race condition with state update
    if (newAvatar?.id) {
      fetchPhotos(newAvatar.id);
    }
  };

  // NEW: Generate Super Prompt with advanced settings
  const handleGenerateSuperPrompt = async () => {
    // Map Feintuning-Selections zu bodyMode
    let bodyMode = "portrait";

    if (feintuningSelections?.körper === "Ganzkörper") {
      bodyMode = "full";
    } else if (feintuningSelections?.körper === "Oberkörper") {
      bodyMode = "upper";
    } else if (feintuningSelections?.körper === "3/4") {
      bodyMode = "three_quarter";
    }

    // Get genderToken from active avatar (case-insensitive!)
    const activeAvatar = avatars[activeAvatarIndex];
    const readyJob = avatars.find(a => a.status === 'ready' && a.id === activeAvatar?.id);
    
    let genderToken = 'man'; // Fallback
    
    // Prio 1: Avatar-Gender (case-insensitive)
    const avatarGender = activeAvatar?.gender?.toLowerCase();
    if (avatarGender === 'woman' || avatarGender === 'frau') {
      genderToken = 'woman';
    } else if (avatarGender === 'man' || avatarGender === 'mann') {
      genderToken = 'man';
    } else if (avatarGender === 'child' || avatarGender === 'kind') {
      genderToken = 'child';
    }
    
    // Prio 2: API Response (fallback)
    if (!activeAvatar?.gender) {
      const astriaName = readyJob?.astria_response?.name?.toLowerCase();
      if (astriaName === 'woman' || astriaName === 'frau') {
        genderToken = 'woman';
      } else if (astriaName === 'man' || astriaName === 'mann') {
        genderToken = 'man';
      } else if (astriaName === 'child' || astriaName === 'kind') {
        genderToken = 'child';
      }
    }
    
    console.log('🔍 SuperPrompt Gender Token:', genderToken, '(Avatar Gender:', activeAvatar?.gender, ', API Response:', readyJob?.astria_response?.name, ')');

    const generated = await buildSuperPrompt({
      userText: promptText,
      genderToken: genderToken || "mann",
      tuneId: userData?.tuneId,
      bodyMode,
      portraitStyle: portraitStyle || 'natural',
      fullBodyStyle: fullBodyStyle || 'street',
      vibe: "photographic",
      randomize: true,
      avatarCharacteristics: {}  // LEER! Backend fügt Avatar-Daten hinzu!
    });

    setSuperPrompt(generated);
  };

  // NEW: Build English prompt from German selections
  const buildEnglishPrompt = (germanPrompt, feintuningSelections = {}, tuneId = null, genderToken = 'person') => {
    let promptParts = [];
    let hasSensual = false;
    
    // Add user's manual input (if any)
    if (germanPrompt?.trim()) {
      promptParts.push(germanPrompt?.trim());
    }
    
    // Add feintuning selections
    Object.keys(feintuningSelections).forEach(categoryKey => {
      const selectedIds = feintuningSelections[categoryKey];
      selectedIds.forEach(id => {
        // Check if sensual variant
        if (id.endsWith('_s')) {
          hasSensual = true;
        }
        // Remove _s suffix for translation lookup
        const baseId = id.replace('_s', '');
        const translation = TRANSLATIONS[baseId];
        if (translation) {
          promptParts.push(translation);
        }
      });
    });
    
    // Build professional image prompt
    const userContent = promptParts.join(', ');
    
    // IMMER mit LoRA-Tag + "ohwx {gender}" beginnen (Standard)
    let finalPrompt = '';
    if (tuneId) {
      finalPrompt = `<lora:${tuneId}:1.0> `;
    }
    finalPrompt += userContent ? `ohwx ${genderToken} ${userContent}` : `ohwx ${genderToken}`;
    
    if (hasSensual) {
      finalPrompt += ', (sensual lighting)';
    }
    
    // IMMER mit professionellen Qualifiers enden
    finalPrompt += ', professional photography, high quality';
    
    return finalPrompt;
  };

  const fetchPhotos = async (overrideAvatarId = null, { fresh = false } = {}) => {
    try {
      const activeAvatar = avatars[activeAvatarIndex];
      const avatarId = overrideAvatarId || activeAvatar?.id;

      if (!avatarId || avatarId === 'avatar_1') {
        setPhotos(prev => prev.length > 0 ? prev : []);
        setLoadingGallery(false);
        return;
      }

      // SWR: Cache sofort anzeigen (kein "0 Bilder" Flash).
      // Bei fresh=true (direkt nach einer Generierung) NICHT aus dem lokalen Cache
      // vorbelegen — der ist noch stale und würde das eben erzeugte Bild kurz wieder
      // aus der Galerie werfen.
      if (!fresh) {
        const cached = getCachedGenerations(avatarId);
        if (cached && cached.length > 0) {
          setPhotos(cached);
          setLoadingGallery(false); // Sofort als "geladen" markieren
        }
      }

      // Revalidieren — fresh=1 umgeht den serverseitigen 60s-Cache.
      const res = await apiClient.get(`/api/generations/list?avatar_id=${avatarId}${fresh ? '&fresh=1' : ''}`);
      if (res.ok) {
        const data = await res.json();
        const apiGens = data.generations || [];

        setPhotos(apiGens);
        setCachedGenerations(avatarId, apiGens);
      } else {
        setPhotos(prev => prev.length > 0 ? prev : []);
      }
    } catch (error) {
      console.error('Error fetching images:', error);
      setPhotos(prev => prev.length > 0 ? prev : []);
    } finally {
      setLoadingGallery(false);
    }
  };
  
  // PROMPT HISTORY: Load History
  const loadPromptHistory = async () => {
    setLoadingHistory(true);
    try {
      // ReadOnly: Nutze showcaseData
      if (readOnlyMode && showcaseData?.prompt_history) {
        setPromptHistory(showcaseData.prompt_history);
        setLoadingHistory(false);
        return;
      }
      
      const res = await apiClient.get('/api/prompt-history/list');
      if (res.ok) {
        const data = await res.json();
        setPromptHistory(data);
        console.log(`📋 Prompt History loaded: ${data.length} items`);
      } else {
        console.error('❌ Failed to load prompt history');
        setPromptHistory([]);
      }
    } catch (error) {
      console.error('Error loading prompt history:', error);
      setPromptHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };
  
  // PROMPT HISTORY: Use Prompt (übernehmen)
  const handleUsePrompt = (historyItem) => {
    // Remix-Prompts: Prompt ins Beschreibungsfeld + Remix-Bild vorauswählen
    if (historyItem.generation_type === 'remix' || historyItem.prompt_source === 'remix') {
      setPromptText(historyItem.user_prompt);
      setOriginalPromptDE(historyItem.user_prompt);
      setShowHistoryPanel(false);
      // Accordion für Bildgenerierung öffnen (Remix ist foto-basiert)
      setAccordionOpen(prev => ({...prev, history: false, createImage: true}));

      const toast = document.createElement('div');
      toast.textContent = 'Remix-Prompt übernommen';
      toast.className = 'fixed top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 px-5 py-2.5 bg-black/70 backdrop-blur-md text-white rounded-full text-sm font-medium z-[999999]';
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 2000);
      return;
    }

    // Hollywood-Video-Prompts: Hollywood-Modus öffnen und Prompt dort einfügen
    if (historyItem.video_model === 'hollywood') {
      setHollywoodPreset({ prompt: historyItem.user_prompt || '', slots: historyItem.hollywood_slots || [] });
      setShowHistoryPanel(false);
      setAccordionOpen(prev => ({...prev, history: false, hollywood: true}));
      const t = document.createElement('div');
      t.textContent = 'Prompt in Hollywood übernommen';
      t.className = 'fixed top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 px-5 py-2.5 bg-black/70 backdrop-blur-md text-white rounded-full text-sm font-medium z-[999999]';
      document.body.appendChild(t); setTimeout(() => t.remove(), 2000);
      setTimeout(() => { const el = document.querySelector('[data-accordion="hollywood"]'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 250);
      return;
    }

    // Video-Prompts: Video-Accordion öffnen und Prompt dort übernehmen
    if (historyItem.generation_type === 'video') {
      setVideoPreselectedPrompt(historyItem.user_prompt);
      setVideoPreselectedMotionTags(historyItem.motion_tags || null);
      // Video-Modus korrekt setzen: Kino oder Basic
      const vm = historyItem.video_model;
      const targetModel = (vm === 'kino_modus' || vm === 'cinematic_video') ? 'cinematic_video' : 'grok';
      setVideoPreselectedModel(targetModel);
      setVideoPreselectedSettings({
        duration: historyItem.video_duration || null,
        resolution: historyItem.video_resolution || null,
        aspectRatio: historyItem.video_aspect_ratio || null,
        sourceImageUrl: historyItem.source_image_url || null
      });
      if (historyItem.source_image_url) {
        setVideoPreselectedImage(historyItem.source_image_url);
      }
      setShowHistoryPanel(false);
      // History-Accordion schließen, Video-Accordion öffnen
      setAccordionOpen(prev => ({...prev, history: false, videoCreate: true}));
      console.log('✅ Video-Prompt übernommen → Video-Generator:', historyItem.user_prompt);

      const toast = document.createElement('div');
      toast.textContent = 'Video-Prompt übernommen';
      toast.className = 'fixed top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 px-5 py-2.5 bg-black/70 backdrop-blur-md text-white rounded-full text-sm font-medium z-[999999]';
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 2000);
      setTimeout(() => {
        const el = document.querySelector('[data-accordion="video-create"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
      return;
    }

    setPromptText(historyItem.user_prompt);
    setOriginalPromptDE(historyItem.user_prompt);

    // Settings wiederherstellen: Nur tatsächliche Werte, sonst Defaults
    const s = historyItem.settings || {};

    // Advanced Mode: Nur aktivieren wenn tatsächlich Advanced-Einstellungen gespeichert waren
    const wasAdvanced = s.advanced_mode === true;
    setAdvancedMode(wasAdvanced);

    // Advanced-only Settings: Nur setzen wenn gespeichert, sonst auf neutral
    setTimeOfDay(s.time_of_day || 'day');
    setWeather(s.weather || 'clear');
    setStyle(s.style || 'realistic');
    setSubstyle(s.substyle || 'standard');

    // Allgemeine Settings (gelten in beiden Modi)
    setCamera(s.camera || 'none');
    setBodyFraming(s.body_framing || 'none');
    setImageFormat(s.image_format || 'portrait_9_16');

    // Bildanzahl wiederherstellen
    if (historyItem.num_images && historyItem.num_images > 0) {
      setNumImages(historyItem.num_images);
    }

    // Panel schließen
    setShowHistoryPanel(false);
    
    console.log('✅ Prompt übernommen:', historyItem.user_prompt);
    console.log('✅ Settings übernommen:', historyItem.settings);
    
    // Toast Feedback - elegant & minimalistisch, mittig im Sichtfeld
    const toast = document.createElement('div');
    toast.textContent = 'Prompt übernommen';
    toast.className = 'fixed top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 px-5 py-2.5 bg-black/70 backdrop-blur-md text-white rounded-full text-sm font-medium z-[999999]';
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2000);
    
    // Öffne Accordion + Scroll
    setAccordionOpen(prev => ({...prev, createImage: true}));
    setTimeout(() => {
      const promptField = document.querySelector('[data-testid="dream-image-textarea"]');
      if (promptField) {
        promptField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        promptField.focus();
      }
    }, 300);
  };
  
  // PROMPT HISTORY: Delete Prompt
  const handleDeletePrompt = async (historyId) => {
    if (!window.confirm('Diesen Prompt wirklich löschen?')) {
      return;
    }
    
    try {
      const res = await apiClient.delete(`/api/prompt-history/${historyId}`);
      if (res.ok) {
        // Remove from local state
        setPromptHistory(prev => prev.filter(item => item.id !== historyId));
        console.log('🗑️ Prompt gelöscht:', historyId);
      } else {
        alert('Fehler beim Löschen');
      }
    } catch (error) {
      console.error('Error deleting prompt:', error);
      alert('Fehler beim Löschen');
    }
  };
  
  // PROMPT HISTORY: Open Panel
  const handleOpenHistory = () => {
    setShowHistoryPanel(true);
    loadPromptHistory();
  };

  // Handle image deletion - supports single ID or array of IDs (batch delete)
  const handleImageDelete = (imageIdOrArray) => {
    const imageIds = Array.isArray(imageIdOrArray) ? imageIdOrArray : [imageIdOrArray];

    // IDs direkt merken — kein URL-Lookup nötig.
    setPendingDeleteIds(prev => {
      const next = new Set(prev);
      imageIds.forEach(id => next.add(id));
      return next;
    });

    window.dispatchEvent(new CustomEvent('imagesDeleted', { detail: { imageIds } }));
  };

  // Memoized: verhindert unnötige useEffect-Trigger in VideoGeneratorFullscreen
  // Filtert pendingDeleteIds heraus — ID-basiert, sicher weil setPhotos nicht aufgerufen wird
  const preloadedPhotos = useMemo(() =>
    ((demoModeActive && isDemoTourEligible ? effectiveGalleryGenerations : photos) || []).flatMap(gen =>
      (gen.image_urls || [gen.image_url]).filter(Boolean).map((url, idx) => ({
        id: `${gen.id}_${idx}`,
        url: url,
        prompt: gen.prompt,
        created_at: gen.created_at
      }))
    ).filter(p => {
      if (pendingDeleteIds.has(p.id)) return false;
      const genId = p.id.split('_').slice(0, -1).join('_');
      if (pendingDeleteIds.has(`gen_${genId}`)) return false;
      return true;
    }),
    [demoModeActive, effectiveGalleryGenerations, isDemoTourEligible, pendingDeleteIds, photos]);

  useEffect(() => {
    // ReadOnly Mode: NIEMALS API-Calls machen!
    if (readOnlyMode) {
      if (showcaseData) {
        // Setze Avatar-Daten aus showcaseData
        const showcaseAvatar = {
          id: showcaseData.avatar.id,
          avatarName: showcaseData.avatar.title,
          status: showcaseData.avatar.status,
          idNumber: '001',
          tuneId: null,
          credits: 0,
          createdAt: showcaseData.avatar.created_at
        };
        
        setAvatars([showcaseAvatar]);
        setAvatarsLoading(false);
        const allImages = showcaseData.generations || [];
        // Für Skip-Seite: Initial nur erste Karte, dann "Show More"
        setPhotos(limitToFirst ? allImages.slice(0, 1) : allImages);
        setLoadingGallery(false);
      }
      return; // Skip normale API-Calls IMMER im readOnlyMode
    }
    
    // Normale Lade-Logik für eingeloggte User
    const loadData = async () => {
      await checkTrainingStatus();  // Load all training jobs (multi-avatar!)
      setAvatarsLoading(false);
      await fetchUserCredits(); // Fetch real credits from backend
      
      // CRITICAL FIX: Erneuter Credits-Fetch nach 2s (falls erster fehlschlägt)
      setTimeout(async () => {
        const currentCredits = avatarsRef.current[0]?.credits || 0;
        if (currentCredits === 0) {
          console.log('⚠️ Credits still 0 after initial load - retrying...');
          await fetchUserCredits();
        }
      }, 2000);
      
      // fetchPhotos will be called by the second useEffect when avatars change
    };
    
    loadData();
    
    // Auto-Update Poller: Check training status AND credits every 10s
    const pollInterval = setInterval(() => {
      // Use refs to avoid triggering re-renders
      const currentAvatars = avatarsRef.current;
      const currentIndex = activeAvatarIndexRef.current;
      const currentAvatar = currentAvatars[currentIndex];
      
      // Always fetch credits (to catch admin changes)
      fetchUserCredits();
      
      // Poll if avatar exists (not just when training - to catch status changes)
      if (currentAvatar && currentAvatars.length > 0) {
        // Stop polling only if ALL avatars are ready or failed
        const allAvatarsFinished = currentAvatars.every(a => 
          a.status === 'ready' || a.status === 'failed'
        );
        
        if (!allAvatarsFinished) {
          console.log('🔄 Auto-polling training status...');
          checkTrainingStatus();
        } else {
          console.log('✅ All avatars finished - polling stopped');
        }
      }
    }, 10000); // 10 seconds
    
    return () => clearInterval(pollInterval);
  }, [limitToFirst]); // Re-run when limitToFirst changes
  
  // Reload gallery when avatar changes OR when avatars are loaded
  useEffect(() => {
    // Skip im ReadOnly Mode (Daten kommen aus showcaseData)
    if (readOnlyMode) {
      return;
    }
    
    // Only fetch if we have real avatars loaded
    if (avatars.length > 0 && avatars[0].id !== 'avatar_1') {
      // Determine which avatar ID to use
      const urlParams = new URLSearchParams(window.location.search);
      const avatarIdFromUrl = urlParams.get('avatarId');
      
      let targetAvatarId;
      if (avatarIdFromUrl) {
        const urlAvatar = avatars.find(a => a.id === avatarIdFromUrl);
        targetAvatarId = urlAvatar?.id || avatars[activeAvatarIndex]?.id;
      } else {
        targetAvatarId = avatars[activeAvatarIndex]?.id;
      }
      
      // FIX: Only reload gallery if avatar ID actually changed
      // This prevents fetchPhotos from overwriting optimistic deletes every 10s
      if (targetAvatarId && targetAvatarId !== lastLoadedAvatarIdRef.current) {
        console.log(`🔄 Gallery reload: ${lastLoadedAvatarIdRef.current} → ${targetAvatarId}`);
        lastLoadedAvatarIdRef.current = targetAvatarId;
        fetchPhotos(targetAvatarId);
      }
    } else {
      // Kein Avatar → Leere Galerie, Spinner stoppen
      lastLoadedAvatarIdRef.current = null;
      setPhotos(prev => prev.length > 0 ? prev : []); // Keep if already loaded
      setLoadingGallery(false);
    }
  }, [activeAvatarIndex, avatars]);

  // Refresh-Trigger State für Galerie (wird von Events gesetzt)
  const [galleryRefreshTrigger, setGalleryRefreshTrigger] = useState(0);
  
  // Listen for galleryRefresh events (from Remix completion)
  useEffect(() => {
    if (readOnlyMode) return;
    
    const handleGalleryRefresh = (event) => {
      console.log('galleryRefresh event received:', event.detail);
      
      // Wenn newImage mitgesendet wurde, sofort in Galerie pushen
      if (event.detail?.newImage) {
        const newImg = event.detail.newImage;
        console.log('Sofort-Update: Neues Bild in Galerie:', newImg.id);
        setPhotos(prev => {
          if (prev.some(p => p.id === newImg.id)) return prev;
          return [newImg, ...prev];
        });
      }
      
      // Trotzdem Refresh triggern für DB-Sync
      setGalleryRefreshTrigger(prev => prev + 1);
    };
    
    window.addEventListener('galleryRefresh', handleGalleryRefresh);
    return () => window.removeEventListener('galleryRefresh', handleGalleryRefresh);
  }, [readOnlyMode]);
  
  // Wenn Refresh-Trigger sich ändert, lade Galerie neu
  useEffect(() => {
    if (galleryRefreshTrigger > 0 && !readOnlyMode) {
      console.log('🔄 Galerie wird neu geladen (Trigger:', galleryRefreshTrigger, ')');
      const refresh = async () => {
        await new Promise(resolve => setTimeout(resolve, 1000)); // Erhöht für Remix-DB-Sync
        await fetchPhotos();
      };
      refresh();
    }
  }, [galleryRefreshTrigger]);

  // POLLING: Wenn isGenerating=true, alle 3 Sekunden Galerie refreshen bis neue Bilder da sind
  useEffect(() => {
    // Skip if not logged in
    const token = _getStoredToken();
    if (!token) return;
    if (pendingImageJobs.length === 0 || readOnlyMode) return;

    // Ältesten Job als Referenz für startCount nehmen
    const oldestJob = pendingImageJobs.reduce((a, b) => a.startImageCount < b.startImageCount ? a : b);
    const startCount = oldestJob.startImageCount;

    console.log('🔄 Polling started — ', pendingImageJobs.length, 'pending jobs, looking for images >', startCount);

    let pollCount = 0;
    const MAX_POLLS = 60;

    const pollInterval = setInterval(async () => {
      const activeAvatar = avatars[activeAvatarIndex];
      if (!activeAvatar?.id || activeAvatar.id === 'avatar_1') return;

      pollCount++;

      try {
        const res = await apiClient.get(`/api/generations/list?avatar_id=${activeAvatar.id}`);
        if (res.ok) {
          const data = await res.json();
          const apiGens = data.generations || [];
          const currentCount = apiGens.reduce((sum, gen) => sum + (gen.image_urls?.length || 0), 0);

          console.log('🔄 Polling...', startCount, '→', currentCount, `(${pollCount}/${MAX_POLLS}, ${pendingImageJobs.length} jobs)`);

          // Prüfe ob failed Generierungen dabei sind
          const hasNewFailed = apiGens.some(g => g.status === 'failed' && new Date(g.created_at) > new Date(Date.now() - 120000));
          if (hasNewFailed && currentCount <= startCount) {
            console.log('❌ Failed generation detected');
            setPhotos(apiGens);
            clearAllPendingImageJobs();
            return;
          }

          // Neue Bilder da → Galerie updaten, abgeschlossene Jobs entfernen
          if (currentCount > startCount) {
            console.log('✅ New images detected!', currentCount - startCount, 'neue Bilder');
            setPhotos(apiGens);
            setGalleryTab('images');

            // Jobs entfernen deren Bilder angekommen sind (pro-Bild-Loader)
            // Fallback für legacy Jobs ohne requiredAtCount: nutze startImageCount + 1
            setPendingImageJobs(prev => {
              const updated = prev.filter(j => {
                const threshold = j.requiredAtCount ?? (j.startImageCount + 1);
                return currentCount < threshold;
              });
              if (updated.length === 0) {
                localStorage.removeItem('snapshotpro_generating');
              } else {
                localStorage.setItem('snapshotpro_generating', JSON.stringify(updated));
              }
              return updated;
            });
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
      }

      if (pollCount >= MAX_POLLS) {
        console.log('⏰ Polling timeout');
        clearAllPendingImageJobs();
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [pendingImageJobs.length, readOnlyMode, avatars, activeAvatarIndex]);

  // SERVER-SIDE PENDING CHECK: Nach Avatar-Load prüfen ob Image läuft
  // Das ist 100% zuverlässig - kein localStorage-Problem mehr!
  useEffect(() => {
    if (readOnlyMode) return;
    
    // Skip if not logged in
    const token = _getStoredToken();
    if (!token) return;
    
    const activeAvatar = avatars[activeAvatarIndex];
    if (!activeAvatar?.id || activeAvatar.id === 'avatar_1') return;
    
    const checkPending = async () => {
      try {
        const res = await apiClient.get(`/api/generations/pending?avatar_id=${activeAvatar.id}`);
        if (res.ok) {
          const data = await res.json();
          if (data.pending) {
            console.log('🔄 Server says image pending - restoring spinner!');
            
            // WICHTIG: Hole ERST die aktuellen Bilder vom Server
            // damit wir den korrekten startImageCount haben!
            const genRes = await apiClient.get(`/api/generations/list?avatar_id=${activeAvatar.id}`);
            let currentCount = 0;
            if (genRes.ok) {
              const genData = await genRes.json();
              const gens = genData.generations || [];
              currentCount = gens.reduce((sum, gen) => sum + (gen.image_urls?.length || 0), 0);
              // Auch images State updaten
              setPhotos(gens);
            }
            
            // localStorage mit korrektem Count setzen (für Polling)
            localStorage.setItem('snapshotpro_generating', JSON.stringify({
              generating: true,
              startTime: Date.now(),
              startImageCount: currentCount
            }));
            console.log('🔄 Set startImageCount to', currentCount);
            
            // State setzen
            setIsGenerating(true);
          } else {
            // Server sagt NICHT pending - Banner ausschalten!
            console.log('✅ Server says NOT pending - hiding spinner');
            localStorage.removeItem('snapshotpro_generating');
            setIsGenerating(false);
          }
        }
      } catch (error) {
        console.error('Pending check error:', error);
      }
    };
    
    checkPending();
  }, [avatars, activeAvatarIndex, readOnlyMode]); // Läuft wenn Avatar sich ändert

  // Block body scroll when Vorlagen-Modal is open
  // Einmaliger Auto-Verlängerung-Hinweis-Modal:
  //   öffnet sich, sobald der aktive Avatar 'ready' (Sheet fertig) ist UND
  //   der User den Hinweis noch nicht bestätigt hat (users.auto_extend_hint_seen).
  useEffect(() => {
    const u = avatars[activeAvatarIndex];
    if (!u) return;
    const isReady = u.character_sheet_status === 'ready' || u.status === 'ready';
    const hintSeen = !!_getStoredUser()?.auto_extend_hint_seen;
    if (isReady && !hintSeen) {
      setShowAutoExtendHint(true);
    }
  }, [avatars, activeAvatarIndex]);

  useEffect(() => {
    if (showInfoModal) {
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
    }

    return () => {
      document.documentElement.style.overflow = '';
    };
  }, [showInfoModal]);

  // Lade Dropdown-Options aus Backend (für bessere Keywords!)
  useEffect(() => {
    if (readOnlyMode) return;
    loadDropdownOptions();
  }, [readOnlyMode]);
  
  const loadDropdownOptions = async () => {
    try {
      const res = await apiClient.get('/api/admin/astria-tuning/public/dropdowns');
      if (res.ok) {
        const data = await res.json();
        setDynamicOptions(data);
      }
    } catch (error) {
      console.error('Error loading dropdown options:', error);
    }
  };

  // Fetch user credits from backend (MIT RETRY-MECHANISMUS)
  const fetchUserCredits = async (retryCount = 0, maxRetries = 3) => {
    try {
      console.log(`🔄 Fetching credits (Attempt ${retryCount + 1}/${maxRetries + 1})...`);
      
      const res = await apiClient.get('/api/user/credits');
      
      if (!res.ok) {
        throw new Error(`API returned ${res.status}`);
      }
      
      const data = await res.json();
      const credits = data.credits || 0;
      
      console.log(`✅ Credits loaded successfully: ${credits} SNAPs`);
      
      // Update credits nur wenn geändert (verhindert unnötige Re-Renders!)
      setAvatars(prevAvatars => {
        // Prüfe ob Credits sich geändert haben
        const currentCredits = prevAvatars[0]?.credits;
        if (currentCredits === credits) {
          console.log('→ Credits unverändert, skip update');
          return prevAvatars; // KEIN Update → KEIN Re-Render!
        }
        
        console.log(`→ Credits geändert: ${currentCredits} → ${credits}`);
        return prevAvatars.map(avatar => ({
          ...avatar,
          credits: credits
        }));
      });
      
      return credits;
      
    } catch (error) {
      console.error(`❌ Error fetching credits (Attempt ${retryCount + 1}):`, error);
      
      // Retry mit exponential backoff
      if (retryCount < maxRetries) {
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        console.log(`⏳ Retrying in ${delay/1000}s...`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return await fetchUserCredits(retryCount + 1, maxRetries);
      } else {
        console.error('❌ Failed to load credits after all retries');
        // Fallback: Credits bleiben wie sie sind (aus LocalStorage oder 0)
        return null;
      }
    }
  };
  
  // Training Timer: Berechne verbleibende Zeit basierend auf Startzeit
  const [timerDisplay, setTimerDisplay] = useState('~10:00');
  
  useEffect(() => {
    if (userData?.status !== 'training') return;
    
    // Startzeit aus userData holen (createdAt des Trainings - jetzt als ISO-String vom Backend)
    const startTime = userData?.createdAt ? new Date(userData.createdAt).getTime() : Date.now();
    const estimatedDuration = 11 * 60 * 1000; // 11 Minuten in ms
    
    console.log('⏱️ Timer Debug:', {
      createdAt: userData?.createdAt,
      startTime: new Date(startTime).toISOString(),
      now: new Date().toISOString()
    });
    
    const updateTimer = () => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, estimatedDuration - elapsed);
      const mins = Math.floor(remaining / 60000);
      const secs = Math.floor((remaining % 60000) / 1000);
      setTimerDisplay(`${mins}:${secs.toString().padStart(2, '0')}`);
    };
    
    updateTimer(); // Sofort berechnen
    const timerInterval = setInterval(updateTimer, 1000);
    
    return () => clearInterval(timerInterval);
  }, [userData?.status, userData?.createdAt]);
  
  // Format timer as MM:SS (Fallback)
  const formatTimer = () => timerDisplay;
  
  // Check training status on mount
  const checkTrainingStatus = async () => {
    try {
      const res = await apiClient.get('/api/training/list');
      if (res.ok) {
        const data = await res.json();
        const jobs = data.jobs || [];
        
        console.log('🔍 Loading Training Jobs:', jobs.length);
        
        if (jobs.length > 0) {
          // LIVE-SYNC FIX: For each training job, query individual endpoint with live sync
          const updatedJobs = await Promise.all(
            jobs.map(async (job) => {
              // Only do live-sync for training jobs (not ready/failed)
              if (job.status === 'training' && job.id) {
                try {
                  const jobRes = await apiClient.get(`/api/training/${job.id}`);
                  if (jobRes.ok) {
                    const liveJob = await jobRes.json();
                    console.log(`🔄 Live-sync for ${job.title}: ${job.status} → ${liveJob.status}`);
                    return liveJob; // Use live-synced data
                  }
                } catch (e) {
                  console.error(`Failed to live-sync job ${job.id}:`, e);
                }
              }
              return job; // Use cached data for ready/failed
            })
          );
          
          // Create avatar object for EACH training job
          const avatarList = updatedJobs.map((job, index) => {
            console.log("🧩 FULL TRAINING JOB FROM BACKEND:", job);
            console.log(`  Avatar ${index + 1}: ${job.title} | Status: ${job.status} | Tune: ${job.tune_id} (${typeof job.tune_id})`);
            return {
              id: job.id,
              trainingJobId: job.id,
              avatarName: job.title || `Avatar ${job.avatar_number || index + 1}`,
              idNumber: (() => {
                // Persistent fortlaufende Avatar-Nummer (vom Backend) statt Listen-Index
                const num = job.avatar_number || (index + 1);
                const numStr = String(num).padStart(2, '0');
                return user.display_id
                  ? `${String(user.display_id).padStart(6, '0')}-${numStr}`
                  : `000000-${numStr}`;
              })(),
              credits: user.credits || 0,
              createdAt: job.created_at || new Date().toISOString(),
              status: job.status, // 'training', 'ready', 'failed'
              profileImage: job.profile_image_url || null,  // NEU: Avatar-Profilbild
              characterSheet: job.character_sheet_url
                ? `${job.character_sheet_url}?v=${encodeURIComponent(job.updated_at || job.character_sheet_status || '')}`
                : null,  // Referenz-Sheet (Modal); ?v=updated_at bricht den 7-Tage-Browsercache bei Neu-Generierung des Sheets
              tuneId: job.tune_id,  // WICHTIG: Muss Number bleiben!
              gender: job.gender || job.category || job.astria_response?.name || null,  // FIX: Gender für genderToken-Logik
              // Avatar characteristics (Snapshot 2.0)
              age: job.age || null,
              ethnicity: job.ethnicity || null,
              eye_color: job.eye_color || null,
              body_type: job.body_type || null,
              height: job.height || null,
              breast_size: job.breast_size || null,
              hair_description: job.hair_description || null,  // NEU: Avatar-Frisur laden!
              expires_at: job.expires_at || job.astria_response?.expires_at || null,  // Gültigkeit von Astria (inkl. Verlängerungen)
              auto_extend: job.auto_extend || false  // Auto-Verlängerung
            };
          });
          
          console.log('✅ Avatars loaded:', avatarList.map(a => `${a.avatarName} (${a.status})`));
          setAvatars(avatarList);
        } else {
          // FIX: Reset to default "pending" state when no avatars exist
          console.log('⚠️ No training jobs found - resetting to default state');
          setAvatars([{
            id: 'avatar_1',
            avatarName: user.name || 'Nicht festgelegt',
            idNumber: user.display_id ? String(user.display_id).padStart(6, '0') : '000000',
            credits: user.credits || 0,
            createdAt: user.created_at || new Date().toISOString(),
            status: 'pending',
            profileImage: null
          }]);
          setActiveAvatarIndex(0);
        }
      }
    } catch (error) {
      console.error('Error checking training status:', error);
    }
  };

  return (
    <div className="min-h-screen bg-black grain-texture text-white">
      
      {/* Shared Header Component */}
      {!readOnlyMode && <AppHeader menuOpen={menuOpen} onMenuToggle={() => setMenuOpen(!menuOpen)} />}
      
      {/* GENERATING BANNER - ENTFERNT - nur noch Placeholder-Kachel in Galerie */}
      
      {/* Overlay - Schließt Menü bei Klick außerhalb */}
      {!readOnlyMode && menuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30"
          onClick={() => setMenuOpen(false)}
        ></div>
      )}
      
      {/* Google OAuth Processing Loading */}
      {isProcessingOAuth && (
        <div className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-xl text-white/80">Google Login wird verarbeitet...</p>
          </div>
        </div>
      )}
      
      {/* Overlay behind menu - closes menu on tap */}
      {menuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30"
          style={{ 
            pointerEvents: 'all'
          }}
          onClick={() => setMenuOpen(false)}
        />
      )}
      
      {/* Side Menu - iOS-SAFE SCROLL */}
      {!readOnlyMode && (
      <div 
        ref={menuRef}
        className={`fixed top-0 right-0 w-80 bg-black/95 backdrop-blur-xl border-l border-white/10 transform transition-transform duration-300 z-40 ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}
        style={{
          height: '100vh',
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <nav 
          className="flex flex-col p-8 space-y-6 mt-20 pb-24"
          style={{
            touchAction: 'pan-y'
          }}
        >
          
          {/* User Info im Menü */}
          <div className="pb-4 border-b border-white/10 mb-2">
            <p className="text-sm text-white/50 mb-1">{user.name || 'User'}</p>
            <p className="text-xs text-white/40 mb-3">{user.email}</p>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {userData?.credits === null ? (
                  <span className="text-white/60">Lädt...</span>
                ) : userData?.credits === 0 ? (
                  <span className="text-white/40">0 SNAPs</span>
                ) : (
                  <span className="text-[#10B981]">{Number((userData?.credits || 0).toFixed(2))} SNAPs</span>
                )}
              </span>
              <Link 
                to="/credits"
                onClick={() => setMenuOpen(false)}
                className="px-3 py-1.5 bg-[#10B981] text-black rounded-lg text-xs font-bold hover:bg-[#059669] transition-all glow-green-soft"
              >
                + kaufen
              </Link>
            </div>
          </div>
          
          <button 
            onClick={() => {
              setMenuOpen(false);
              if (onBackToDashboard) onBackToDashboard();
            }}
            className="text-white hover:text-[#10B981] transition-colors text-lg font-medium text-left"
          >
            {isMasterAdmin && '🏠 '}Meine ID Card
          </button>
          
          {/* NEU: Vehicles Link - NUR für Admin */}
          {isMasterAdmin && (
            <Link
              to="/vehicles"
              onClick={() => setMenuOpen(false)}
              className="text-white hover:text-[#10B981] transition-colors text-lg font-medium text-left"
            >
              🚗 Vehicles
            </Link>
          )}
          
          {/* Meine Avatare - NEW NAVIGATION */}
          {avatars.length > 1 && (
            <div className="border-t border-white/10 pt-4">
              <div className="text-white/50 text-xs uppercase tracking-wider mb-3 px-2">Meine Avatare</div>
              <div className="space-y-2">
                {avatars.map((avatar, index) => (
                  <button
                    key={avatar.id}
                    onClick={() => {
                      handleSwitchAvatar(index);
                      setMenuOpen(false);
                      window.history.pushState({}, '', `?avatarId=${avatar.id}`);
                    }}
                    className={`w-full text-left p-3 rounded-lg transition-all ${
                      activeAvatarIndex === index
                        ? 'bg-[#10B981]/20 border-2 border-[#10B981]'
                        : 'bg-white/5 border-2 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                        avatar.status === 'ready' ? 'bg-[#10B981] glow-green-soft' :
                        avatar.status === 'training' ? 'bg-yellow-500 animate-pulse' :
                        'bg-white/30'
                      }`}></div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-semibold text-sm truncate">
                          {avatar.avatarName || `Avatar ${index + 1}`}
                        </div>
                        <div className="text-white/50 text-xs">
                          #{avatar.idNumber} · {
                            avatar.status === 'ready' ? 'Bereit' :
                            avatar.status === 'training' ? 'Training läuft...' :
                            'Pending'
                          }
                        </div>
                      </div>
                      
                      {activeAvatarIndex === index && (
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#10B981] flex items-center justify-center">
                          <svg className="w-4 h-4 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Neuer Avatar erstellen - IM MENÜ */}
          <button
            onClick={() => {
              setMenuOpen(false);
              if (onStartCreation) onStartCreation();
            }}
            className="w-full text-left p-4 rounded-lg bg-gradient-to-r from-[#10B981]/20 to-[#059669]/20 border-2 border-[#10B981] hover:from-[#10B981]/30 hover:to-[#059669]/30 transition-all"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#10B981] flex items-center justify-center flex-shrink-0">
                <Plus className="w-5 h-5 text-black" />
              </div>
              <div className="flex-1">
                <div className="text-white font-semibold text-base">Neuer Avatar</div>
                <div className="text-white/60 text-xs">Avatar erstellen & trainieren</div>
              </div>
            </div>
          </button>
          
          <div className="border-t border-white/10 my-2"></div>
          
          {/* Sicherheitseinstellungen */}
          <Link
            to="/settings/security"
            onClick={() => setMenuOpen(false)}
            className="text-white hover:text-emerald-500 transition-colors text-lg font-medium text-left flex items-center gap-2"
            data-testid="security-settings-link"
          >
            <Shield className="w-5 h-5" />
            Sicherheit
          </Link>
          
          {/* Luma Quick Mode - NUR für Master-Admin */}
          {isMasterAdmin && (
            <Link
              to="/quick-mode"
              className="text-[#10B981] hover:text-[#059669] transition-colors text-lg font-bold flex items-center gap-2 border-t border-white/10 pt-4 -mx-2 px-2"
              onClick={() => setMenuOpen(false)}
            >
              <Sparkles className="w-5 h-5" />
              Quick Mode
            </Link>
          )}

          {/* Admin-Panel Link - für ALLE Admins */}
          {isAdmin && (
            <button
              onClick={() => {
                setMenuOpen(false);

                // Admin-Panel erfordert 2FA (wird im AdminPanel selbst geprüft)
                if (!user.two_factor_enabled) {
                  alert('Admin-Panel erfordert 2FA!\n\nBitte aktiviere zuerst Zwei-Faktor-Authentifizierung in den Sicherheitseinstellungen.');
                  return;
                }

                // Direkt zum Admin-Panel (AdminStepUpModal übernimmt 2FA-Prüfung)
                navigate('/admin');
              }}
              className="text-[#10B981] hover:text-[#059669] transition-colors text-lg font-bold flex items-center gap-2 border-b border-white/10 pb-4 -mx-2 px-2 w-full text-left"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Admin-Panel
            </button>
          )}

          <button
            onClick={() => {
              setMenuOpen(false);
              if (onLogout) {
                onLogout();
              } else {
                localStorage.removeItem('snapshot_token');
                localStorage.removeItem('snapshot_user');
                localStorage.removeItem('snapshot_beta_access');
                window.location.href = '/welcome';
              }
            }}
            className="text-white/60 hover:text-white transition-colors text-sm text-left"
          >
            Abmelden
          </button>
          <div className="border-t border-white/10 my-2"></div>
          <Link to="/was-ist-snapshot" className="text-white/60 hover:text-white transition-colors text-sm">
            Was ist SNAPSHOT
          </Link>
          <Link to="/impressum" className="text-white/60 hover:text-white transition-colors text-sm">
            Impressum
          </Link>
          <Link to="/datenschutz" className="text-white/60 hover:text-white transition-colors text-sm">
            Datenschutz
          </Link>
          <Link to="/agb" className="text-white/60 hover:text-white transition-colors text-sm">
            AGB
          </Link>
          <Link to="/kontakt" className="text-white/60 hover:text-white transition-colors text-sm">
            Kontakt
          </Link>
          <button onClick={handleCookieSettings} className="text-white/60 hover:text-white transition-colors text-sm text-left">
            Cookie-Einstellungen
          </button>
          
          {/* 2FA Einstellungen */}
          <button 
            onClick={() => {
              setMenuOpen(false);
              setShow2FASetup(true);
            }}
            className="text-white/60 hover:text-[#10B981] transition-colors text-sm text-left"
          >
            🛡️ Zwei-Faktor-Auth
          </button>
          
          <div className="border-t border-white/10 my-2"></div>
          <button 
            onClick={() => {
              setMenuOpen(false);
              // 2FA-Gate für Account-Löschen
              if (user.two_factor_enabled) {
                setPending2FAAction('delete-account');
                setShow2FAGate(true);
              } else {
                setShowDeleteAccount(true);
              }
            }}
            className="text-red-500/80 hover:text-red-500 transition-colors text-sm text-left font-semibold"
          >
            Account löschen
          </button>
        </nav>
      </div>
      )}

      <div className="pt-20 pb-8">
        
        {/* ID Card - Luma Style mit Glow */}
        <div className="relative mb-8" data-showcase-idcard>
          {/* Card Container - Volle Breite mit Border-Top */}
          <div className="bg-gradient-to-br from-black/90 via-black/95 to-black/90 backdrop-blur-xl border-t-4 border-[#10B981] rounded-t-3xl shadow-2xl shadow-[#10B981]/30 px-[10px] py-[35px]">
            
            {/* Card Header - Zentriert */}
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#10B981]/20 max-w-4xl mx-auto">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center shadow-lg shadow-[#10B981]/50">
                <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-white">SNAPSHOT ID-Card</h2>
                <p className="text-xs sm:text-sm text-[#10B981]/80">Digitale Identität</p>
              </div>
            </div>

            {/* Card Content Grid - Zentriert */}
            {avatarsLoading ? (
              /* Skeleton Loading State */
              <div className="grid md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
                {/* Left Side - Skeleton Profile */}
                <div className="flex flex-col items-center md:items-start space-y-4">
                  <div className="relative">
                    <div className="w-40 h-40 sm:w-48 sm:h-48 rounded-2xl bg-white/5 border-2 border-white/10 animate-pulse" />
                    <div className="absolute -top-2 -right-2">
                      <div className="w-6 h-6 bg-white/10 rounded-full border-2 border-black animate-pulse" />
                    </div>
                  </div>
                  <div className="w-32 h-4 bg-white/10 rounded animate-pulse" />
                  <div className="w-48 h-7 bg-white/10 rounded animate-pulse" />
                </div>
                {/* Right Side - Skeleton Details */}
                <div className="space-y-4">
                  <div className="relative p-5 rounded-xl bg-white/5 border-2 border-white/10">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                      <div>
                        <div className="w-20 h-3 bg-white/10 rounded animate-pulse mb-2" />
                        <div className="w-28 h-6 bg-white/10 rounded animate-pulse" />
                      </div>
                      <div>
                        <div className="w-16 h-3 bg-white/10 rounded animate-pulse mb-2" />
                        <div className="w-24 h-6 bg-white/10 rounded animate-pulse" />
                      </div>
                      <div>
                        <div className="w-18 h-3 bg-white/10 rounded animate-pulse mb-2" />
                        <div className="w-28 h-6 bg-white/10 rounded animate-pulse" />
                      </div>
                      <div>
                        <div className="w-22 h-3 bg-white/10 rounded animate-pulse mb-2" />
                        <div className="w-24 h-6 bg-white/10 rounded animate-pulse" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">

              {/* Left Side - Profile */}
              <div className="flex flex-col items-center md:items-start space-y-4">
                {/* Profile Picture */}
                <div className="relative">
                  <div
                    onClick={() => { if (displayCharacterSheetUrl) setSheetModalOpen(true); }}
                    title={displayCharacterSheetUrl ? 'Referenz-Sheet ansehen' : undefined}
                    className={`relative w-40 h-40 sm:w-48 sm:h-48 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border-2 border-[#10B981]/30 flex items-center justify-center overflow-hidden shadow-xl shadow-[#10B981]/10 backdrop-blur-sm ${displayCharacterSheetUrl ? 'cursor-pointer hover:border-[#10B981]/70 transition-colors' : ''}`}
                  >
                    {(() => {
                      if (displayProfileImageUrl) {
                        return (
                          <img 
                            src={displayProfileImageUrl} 
                            alt="Profilbild" 
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        );
                      } else {
                        return <User className="w-16 h-16 sm:w-20 sm:h-20 text-[#10B981]/40" />;
                      }
                    })()}
                    {displayCharacterSheetUrl && (
                      <div className="absolute bottom-0 inset-x-0 bg-black/70 text-white text-[10px] font-semibold text-center py-1 pointer-events-none">
                        Referenz-Sheet ansehen
                      </div>
                    )}
                  </div>
                  
                  {/* Status Indicator */}
                  <div className="absolute -top-2 -right-2">
                    <div className={`w-6 h-6 ${displayStatusConfig.color} rounded-full border-2 border-black ${displayStatusConfig.glow} shadow-lg animate-pulse`}></div>
                  </div>
                </div>

                {/* Referenz-Sheet-Modal — öffnet sich beim Klick aufs Avatarbild */}
                {sheetModalOpen && displayCharacterSheetUrl && (
                  <div
                    className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
                    onClick={() => setSheetModalOpen(false)}
                  >
                    <button
                      onClick={() => setSheetModalOpen(false)}
                      className="absolute top-4 right-4 text-white/70 hover:text-white"
                      aria-label="Schließen"
                    >
                      <X className="w-7 h-7" />
                    </button>
                    <img
                      src={displayCharacterSheetUrl}
                      alt="Referenz-Sheet"
                      className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                )}

                {/* Avatar Name - NUR wenn Avatar existiert */}
                {(displayUserData?.status === 'ready') && (
                  <div className="text-center md:text-left w-full">
                    <p className="text-xs text-white/50 mb-1">Avatar Name</p>
                    <p className="text-xl sm:text-2xl font-bold text-[#10B981]">
                      {displayUserData?.avatarName}
                    </p>
                  </div>
                )}
              </div>

              {/* Right Side - Details */}
              <div className="space-y-4">
                
                {/* ULTRA-KOMPAKTES LAYOUT - Premium Glasmorphism */}
                <div className="relative p-5 rounded-xl bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md border-2 border-[#10B981]/30 shadow-xl shadow-[#10B981]/20">
                  {/* Subtle Glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#10B981]/5 to-transparent rounded-xl pointer-events-none"></div>
                  
                  {/* Grid: 2 Spalten */}
                  <div className="grid grid-cols-2 gap-x-6 gap-y-4 relative z-10">
                    
                    {/* Zeile 1: ID-Nummer */}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <CreditCard className="w-4 h-4 text-[#10B981]" />
                        <p className="text-xs text-white/50">ID-Nummer</p>
                      </div>
                      <p className="text-lg font-mono font-bold">#{displayUserData?.idNumber || '000000'}</p>
                    </div>

                    {/* Zeile 1: Status */}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <AlertCircle className="w-4 h-4 text-[#10B981]" />
                        <p className="text-xs text-white/50">Status</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {displayUserData?.status !== 'training' && (
                          <div className={`w-3 h-3 ${displayStatusConfig.color} rounded-full ${displayStatusConfig.glow} shadow-lg animate-pulse`}></div>
                        )}
                        <p className="text-lg font-bold">{displayStatusConfig.text}</p>
{/* Timer deaktiviert - wird später implementiert */}
                      </div>
                    </div>

                    {/* Zeile 2: Erstellt am */}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="w-4 h-4 text-[#10B981]" />
                        <p className="text-xs text-white/50">Erstellt</p>
                      </div>
                      <p className="text-lg font-bold">
                        {displayUserData?.createdAt ? new Date(displayUserData.createdAt).toLocaleDateString('de-DE') : 'N/A'}
                      </p>
                    </div>

                    {/* Zeile 2: Gültigkeit (nur wenn ready) */}
                    {displayUserData?.status === 'ready' && !isDemoTourEligible && (
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <svg className="w-4 h-4 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-xs text-white/50">Gültigkeit</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 ${countdownInfo.color} rounded-full ${countdownInfo.glow} shadow-lg animate-pulse`}></div>
                          <p className={`text-sm font-bold ${
                            countdownInfo.active ? 'text-white' : 'text-red-500'
                          }`}>
                            {countdownInfo.text}
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Zeile 3: Auto-Extend Toggle (nicht im Showcase/ReadOnly Mode) */}
                    {userData?.status === 'ready' && !readOnlyMode && !isDemoTourEligible && (
                      <div className="col-span-2 pt-3 border-t border-white/10">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            <div>
                              <p className="text-xs text-white/50">Auto-Verlängerung</p>
                              <p className="text-[10px] text-white/30">3 Snaps/Monat</p>
                            </div>
                            {/* Info Icon mit Tooltip */}
                            <div className="relative group">
                              <svg className="w-4 h-4 text-white/40 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-64 p-3 bg-black/95 border border-white/20 rounded-lg text-xs text-white/80 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-xl">
                                <p className="font-bold text-[#10B981] mb-2">⚠️ Avatar-Kraft</p>
                                <p className="mb-2"><span className="text-[#10B981]">AN:</span> Dein Avatar wird automatisch verlängert. 3 Snaps/Monat werden abgezogen.</p>
                                <p><span className="text-red-400">AUS:</span> Nach 30 Tagen verliert dein Avatar seine Kraft. Du kannst bisherige Bilder ansehen, aber keine neuen mehr erstellen.</p>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={async () => {
                              const newValue = !userData?.auto_extend;
                              // Optimistic update - instant response
                              setAvatars(prev => prev.map(a => 
                                a.id === userData.id ? {...a, auto_extend: newValue} : a
                              ));
                              
                              try {
                                const response = await apiClient.post('/api/avatar/auto-extend', {
                                  avatar_id: userData.id,
                                  auto_extend: newValue
                                });
                                const data = await response.json();
                                
                                if (!data.success && data.detail) {
                                  // Revert on error (e.g., not enough Snaps)
                                  setAvatars(prev => prev.map(a => 
                                    a.id === userData.id ? {...a, auto_extend: !newValue} : a
                                  ));
                                  alert(data.detail);
                                }
                              } catch (err) {
                                // Revert on error
                                setAvatars(prev => prev.map(a => 
                                  a.id === userData.id ? {...a, auto_extend: !newValue} : a
                                ));
                                console.error('Auto-extend toggle failed:', err);
                              }
                            }}
                            className={`relative w-11 h-6 rounded-full transition-colors ${
                              userData?.auto_extend ? 'bg-[#10B981]' : 'bg-white/20'
                            }`}
                          >
                            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                              userData?.auto_extend ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Zeile 4: Avatar löschen - INTEGRIERT */}
                    <div className="col-span-2 pt-3 border-t border-white/10">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <p className="text-xs text-white/50">Avatar löschen</p>
                        </div>
                        {canDeleteAvatar ? (
                          <button
                            onClick={() => setShowDeleteAvatar(true)}
                            className="px-3 py-1.5 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 rounded-lg text-xs font-semibold text-orange-500 hover:text-orange-400 transition-all"
                          >
                            Löschen
                          </button>
                        ) : (
                          <p className="text-xs text-white/40">{readOnlyMode ? 'Nur Ansicht' : 'Nicht verfügbar'}</p>
                        )}
                      </div>
                    </div>

                  </div>

                </div>
              </div>

            </div>
            )}

            {/* Grüner Verlaufs-Trenner */}
            <div className="my-8">
              <div className="h-1 bg-gradient-to-r from-transparent via-[#10B981] to-transparent opacity-50"></div>
            </div>

            {/* Action Buttons - ACCORDION STYLE */}
            <div className="space-y-4">
              
              {/* ACCORDION 1: Bild erstellen */}
              {canShowGeneratorSections && (
                <div data-demo-anchor="image-create" data-accordion="image-create" className="border-t-4 border-[#10B981]/40 rounded-xl overflow-hidden bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm">
                  {/* Accordion Header */}
                  <button
                    onClick={() => {
                      const wasOpen = !!accordionOpen.createImage;
                      toggleAccordion('createImage');
                      // Beim Öffnen Header in den Viewport scrollen, damit der User
                      // nicht den optischen Sprung sieht, wenn ein anderes Accordion
                      // gleichzeitig zumacht.
                      if (!wasOpen) {
                        requestAnimationFrame(() => {
                          setTimeout(() => {
                            const el = document.querySelector('[data-accordion="image-create"]');
                            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }, 60);
                        });
                      }
                    }}
                    className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#10B981]/20 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                        </svg>
                      </div>
                      <div className="text-left">
                        <h3 className="text-white font-bold text-lg">Bild erstellen</h3>
                        <p className="text-[#10B981]/80 text-xs">
                          Alles beginnt mit einem Bild
                        </p>
                      </div>
                    </div>
                    {accordionOpen.createImage ? (
                      <ChevronUp className="w-6 h-6 text-[#10B981]" />
                    ) : (
                      <ChevronDown className="w-6 h-6 text-white/50" />
                    )}
                  </button>
                  
                  {/* Accordion Content */}
                  {accordionOpen.createImage && (
                    <div className="border-t border-[#10B981]/20 p-5 sm:p-6 animate-slide-down">
                      <div className="space-y-6">
                        {/* Mode-Tabs — immer sichtbar mit Hintergrundbild, einer aktiv */}
                        {(() => {
                          const tabs = [
                            { id: 'scene',     label: 'Szene beschreiben',           bg: '/mode-cards/img-scene.png',     desc: 'Du beschreibst die Szene mit Worten.' },
                            { id: 'composite', label: 'Ich in einer Szene',          bg: '/mode-cards/img-composite.png', desc: 'Bild hochladen — du wirst eingefügt.' },
                            { id: 'rebuild',   label: 'Bild nachbauen mit deinem Gesicht',  bg: '/mode-cards/img-rebuild.png',   desc: 'Bild hochladen — wird mit deinem Gesicht neu gebaut.' },
                          ];
                          const tone = {
                            scene:     { ring: 'border-emerald-400/80 ring-2 ring-emerald-400/30', tint: 'bg-gradient-to-tr from-emerald-900/70 via-black/40 to-emerald-700/10' },
                            composite: { ring: 'border-purple-400/80 ring-2 ring-purple-400/30',   tint: 'bg-gradient-to-tr from-purple-900/70 via-black/40 to-purple-700/10'   },
                            rebuild:   { ring: 'border-blue-400/80 ring-2 ring-blue-400/30',       tint: 'bg-gradient-to-tr from-blue-900/70 via-black/40 to-blue-700/10'       },
                          };
                          return (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              {tabs.map(t => {
                                const isActive = generationMode === t.id;
                                const c = tone[t.id];
                                return (
                                  <button key={t.id}
                                    onClick={() => enterGenerationMode(t.id)}
                                    className={`relative h-32 rounded-2xl border-2 overflow-hidden transition-all focus:outline-none text-left group ${
                                      isActive ? c.ring : 'border-white/10 hover:border-white/30'
                                    }`}>
                                    <img src={t.bg} alt="" aria-hidden
                                      className={`absolute inset-0 w-full h-full object-cover transition-opacity ${
                                        isActive ? 'opacity-80' : 'opacity-45 group-hover:opacity-65'
                                      }`} />
                                    <div className={`absolute inset-0 transition-opacity ${
                                      isActive ? c.tint : 'bg-gradient-to-t from-black/90 via-black/50 to-black/10'
                                    }`} />
                                    <div className="relative h-full flex flex-col justify-end p-4">
                                      <h5 className={`font-bold text-base leading-tight mb-1 drop-shadow-md ${isActive ? 'text-white' : 'text-white/90'}`}>{t.label}</h5>
                                      <p className={`text-xs leading-snug drop-shadow-md ${isActive ? 'text-white/85' : 'text-white/65'}`}>{t.desc}</p>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })()}
                        {/* CLEAN PROMPT MODE: Hauptbereich - IMMER sichtbar */}
                        
                        {/* ═══ HAUPT-LAYOUT: Szene beschreiben | oder | Referenzbild ═══ */}
                        <div className={generationMode === 'scene' ? 'w-full' : 'grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-0 items-stretch'}>

                          {/* ── Text-Prompt (in B/C visuell RECHTS dank order-2) ── */}
                          <div className={`bg-white/5 border border-white/10 rounded-xl p-5 flex flex-col ${generationMode !== 'scene' ? 'md:order-3' : ''}`}>
                            <div className="flex items-center justify-between">
                              <label className="text-sm font-semibold text-white">
                                {generationMode === 'scene' ? 'Szene beschreiben' : 'Möchtest du Regieanweisungen geben?'}
                                {generationMode !== 'scene' && <span className="text-white/40 font-normal ml-1">(optional)</span>}
                              </label>
                              <button onClick={() => setShowInfoModal(true)}
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all bg-white/10 hover:bg-white/20 text-white border border-white/20 hover:border-white/40">
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                                Vorlagen
                              </button>
                            </div>
                            <p className="text-white/50 text-xs mt-1 mb-3">
                              💡 Beschreibe klar, was du sehen möchtest — je genauer du bist, desto besser das Ergebnis.
                            </p>
                            <textarea
                              value={promptText}
                              onChange={(e) => { setPromptText(e.target.value); setOriginalPromptDE(e.target.value); }}
                              placeholder={"Beispiel: Ich stehe auf einer Dachterrasse in New York bei Sonnenuntergang. Ich trage einen dunklen Anzug, lehne lässig am Geländer und halte ein Glas Wein in der Hand. Ich schaue direkt in die Kamera und lächle selbstbewusst. Im Hintergrund die Skyline mit leuchtenden Wolkenkratzern."}
                              className="flex-1 w-full px-4 py-4 bg-black/80 border-2 border-[#10B981]/30 rounded-xl text-white placeholder:text-white/40 focus:border-[#10B981] focus:ring-4 focus:ring-[#10B981]/20 transition-all resize-none shadow-lg min-h-[180px]"
                              data-testid="dream-image-textarea"
                            />
                            {referenceImagePreview && (
                              <div className="mt-3 flex items-start gap-2.5 p-3 rounded-xl bg-gradient-to-r from-white/5 to-transparent border border-white/10">
                                <span className="text-base mt-0.5">ℹ️</span>
                                <p className="text-sm text-white/60 leading-relaxed">
                                  <span className="text-white/80">Diese Beschreibung basiert auf deiner Bildanalyse.</span>{" "}
                                  Für Anpassungen: Nutze den <span className="text-[#10B981] font-medium">Remix-Chip</span> unter deinem generierten Bild.
                                </p>
                              </div>
                            )}
                          </div>

                          {/* ── MITTE + RECHTS: nur sichtbar, wenn Referenz-Modus (B oder C) ── */}
                          {generationMode !== 'scene' && (<>
                          <div className="hidden md:flex flex-col items-center justify-center px-4 md:order-2">
                            <div className="w-px flex-1 bg-white/10"></div>
                            <span className="text-white/30 text-xs font-medium py-3">und</span>
                            <div className="w-px flex-1 bg-white/10"></div>
                          </div>
                          <div className="md:hidden flex items-center gap-3 py-3">
                            <div className="flex-1 h-px bg-white/10"></div>
                            <span className="text-white/30 text-xs font-medium">und</span>
                            <div className="flex-1 h-px bg-white/10"></div>
                          </div>

                          {/* ── Referenz-Upload (in B/C visuell LINKS dank md:order-1) ── */}
                          <div className="bg-white/5 border border-white/10 rounded-xl p-5 flex flex-col md:order-1">
                            <label className="text-sm font-semibold text-white">
                              {generationMode === 'composite' ? 'Szene-Foto' : 'Vorlage-Foto'}
                              <span className="text-white/40 font-normal ml-1">(Pflicht)</span>
                            </label>
                            <p className="text-white/50 text-xs mt-1 mb-3">
                              💡 {generationMode === 'composite'
                                ? 'Lade ein Foto der Szene/Location hoch, in die du eingefügt werden möchtest.'
                                : 'Lade das Bild hoch, das mit deinem Gesicht neu gebaut werden soll. Format wird übernommen.'}
                            </p>

                            {/* Upload + Preview (Drag & Drop) */}
                            <div className={`bg-black/40 border rounded-xl p-3 mb-3 transition-all relative ${isDragging ? 'border-[#10B981] bg-[#10B981]/10' : 'border-[#10B981]/30'}`}
                              onDragOver={(e) => { e.preventDefault(); if (!referenceImagePreview) setIsDragging(true); }}
                              onDragLeave={() => setIsDragging(false)}
                              onDrop={(e) => { e.preventDefault(); setIsDragging(false); const file = e.dataTransfer.files?.[0]; if (file && file.type.startsWith('image')) handleFileSelected(file); }}
                            >
                              {isDragging && !referenceImagePreview && (
                                <div className="absolute inset-0 flex items-center justify-center bg-[#10B981]/10 border-2 border-dashed border-[#10B981] rounded-xl z-10 pointer-events-none">
                                  <span className="text-[#10B981] text-sm font-medium">Bild hier ablegen</span>
                                </div>
                              )}
                              {!readOnlyMode && (
                                <div className="flex gap-3 items-start">
                                  <div className={`flex-1 space-y-2 ${referenceImagePreview ? 'opacity-40' : ''}`}>
                                    <label className={`w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-all text-xs ${isUploadingReference ? 'bg-white/5 text-white/50' : 'bg-white/10 text-white/80 hover:bg-white/20 border border-dashed border-white/30 hover:border-[#10B981]/50'}`}>
                                      <input type="file" accept="image/*,.heic,.heif" className="hidden" disabled={isUploadingReference || !!referenceImagePreview}
                                        onChange={(e) => {
                                          const file = e.target.files?.[0];
                                          if (file) handleFileSelected(file);
                                          e.target.value = '';
                                        }}
                                      />
                                      {isUploadingReference ? (<><svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>Lädt...</>) : (<><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" /></svg>Hochladen</>)}
                                    </label>
                                    <input id="reference-url-input" type="url" placeholder="Bild-URL einfügen..." disabled={!!referenceImagePreview}
                                      className="w-full px-3 py-2 bg-white/5 border border-white/15 rounded-lg text-[11px] text-white placeholder-white/30 focus:border-[#10B981]/50 focus:outline-none"
                                      onKeyDown={async (e) => {
                                        if (e.key === 'Enter') { e.preventDefault(); const url = e.target.value?.trim(); if (url && url.match(/^https?:\/\/.+/i)) { await handleUrlUpload(url); e.target.value = ''; } }
                                      }}
                                      onPaste={(e) => {
                                        setTimeout(async () => { const url = e.target.value?.trim(); if (url && url.match(/^https?:\/\/.+/i)) { await handleUrlUpload(url); e.target.value = ''; } }, 100);
                                      }}
                                    />
                                  </div>
                                  {referenceImagePreview && (
                                    <div className="relative flex-shrink-0">
                                      {referenceImagePreview === 'loading' ? (
                                        <div className="w-20 h-20 rounded-lg border border-white/20 bg-white/5 flex items-center justify-center">
                                          <svg className="animate-spin h-5 w-5 text-[#10B981]" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                                        </div>
                                      ) : (
                                        <img src={referenceImagePreview} alt="Ref" className="w-20 h-20 object-cover rounded-lg border border-white/20" />
                                      )}
                                      <button onClick={() => { setReferenceImage(null); setReferenceImagePreview(null); setReferenceImageRatio(null); setReferenceFlowChoice(null); if (imageFormat === 'original') setImageFormat('portrait_9_16'); }}
                                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] hover:bg-red-600 transition-colors">✕</button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Hinweis-Box: zeigt, was mit dem Bild passiert (Modus ist bereits gewaehlt) */}
                            {referenceImagePreview ? (
                              <div className={`rounded-xl p-3 mt-auto border ${generationMode === 'composite' ? 'bg-purple-500/10 border-purple-500/30 text-purple-200' : 'bg-blue-500/10 border-blue-500/30 text-blue-200'}`}>
                                <p className="text-[11px] leading-relaxed">
                                  {generationMode === 'composite'
                                    ? 'Du wirst in diese Szene eingefügt — Originalbild bleibt erhalten.'
                                    : 'Dasselbe Bild wird komplett neu gerendert mit deinem Gesicht. Format wird automatisch vom Bild übernommen.'}
                                </p>
                              </div>
                            ) : (
                              <div className="rounded-xl p-3 mt-auto border border-white/10 bg-white/5">
                                <p className="text-[11px] text-white/40 leading-relaxed">
                                  Lade zuerst ein Bild hoch, um weiterzumachen.
                                </p>
                              </div>
                            )}
                          </div>
                          </>)}
                        </div>{/* Ende Haupt-Layout */}

                          {/* ═══ ZEILE 2: Kameraperspektive + Bildausschnitt ═══ */}
                          <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Kamera */}
                            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                              <label className={`block text-xs font-medium mb-2 text-center ${generationMode !== 'scene' && referenceImagePreview ? 'text-white/30' : 'text-white/80'}`}>Kamera-Perspektive {generationMode !== 'scene' && referenceImagePreview && <span className="text-white/20 text-[9px]">(Auto bei Referenzbild)</span>}</label>
                              <div className="grid grid-cols-3 gap-[10px]">
                                {[
                                  { value: 'none', label: 'Auto' },
                                  { value: '24mm', label: 'Weitwinkel' },
                                  { value: '35mm', label: 'Standard' },
                                  { value: '50mm', label: 'Natürlich' },
                                  { value: 'drone', label: 'Von oben' },
                                  { value: 'low', label: 'Von unten' },
                                ].map((cam) => (
                                  <button key={cam.value} disabled={generationMode !== 'scene' && referenceImagePreview} onClick={() => { if (!(generationMode !== 'scene' && referenceImagePreview)) setCamera(cam.value); }}
                                    className={`w-full h-[40px] rounded-lg text-[11px] font-medium transition-all text-center ${
                                      generationMode !== 'scene' && referenceImagePreview ? (cam.value === 'none' ? 'bg-white/20 text-white/40 border border-white/10' : 'bg-white/5 text-white/20 border border-white/5') :
                                      camera === cam.value ? 'bg-[#10B981] text-black shadow-lg shadow-[#10B981]/30' : 'bg-white/10 text-white/80 hover:bg-white/20 border border-white/10'
                                    }`}>{cam.label}</button>
                                ))}
                              </div>
                            </div>
                            {/* Bildausschnitt */}
                            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                              <label className={`block text-xs font-medium mb-2 text-center ${generationMode !== 'scene' && referenceImagePreview ? 'text-white/30' : 'text-white/80'}`}>Bildausschnitt {generationMode !== 'scene' && referenceImagePreview && <span className="text-white/20 text-[9px]">(Auto bei Referenzbild)</span>}</label>
                              <div className="grid grid-cols-2 gap-[10px]">
                                {[
                                  { value: 'none', label: 'Auto' },
                                  { value: 'full_body', label: 'Ganzkörper' },
                                  { value: 'half_body', label: 'Oberkörper' },
                                  { value: 'portrait', label: 'Gesicht' },
                                ].map((bf) => (
                                  <button key={bf.value} disabled={generationMode !== 'scene' && referenceImagePreview} onClick={() => { if (!(generationMode !== 'scene' && referenceImagePreview)) setBodyFraming(bf.value); }}
                                    className={`w-full h-[40px] rounded-lg text-[11px] font-medium transition-all text-center ${
                                      generationMode !== 'scene' && referenceImagePreview ? (bf.value === 'none' ? 'bg-white/20 text-white/40 border border-white/10' : 'bg-white/5 text-white/20 border border-white/5') :
                                      bodyFraming === bf.value ? 'bg-[#10B981] text-black shadow-lg shadow-[#10B981]/30' : 'bg-white/10 text-white/80 hover:bg-white/20 border border-white/10'
                                    }`}>{bf.label}</button>
                                ))}
                              </div>
                            </div>
                          </div>
                          {camera === '24mm' && bodyFraming === 'full_body' && (
                            <p className="text-[10px] text-amber-400/70 text-center mt-1.5">Hinweis: Weitwinkel mit Ganzkörper kann Gesichtsproportionen verzerren</p>
                          )}

                          {/* ═══ ZEILE 3: Bildformat + Anzahl Bilder ═══ */}
                          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Bildformat */}
                            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                              <label className={`block text-xs font-medium mb-2 text-center ${referenceFlowChoice === '2.2' && referenceImagePreview ? 'text-white/30' : 'text-white/80'}`}>Bildformat {referenceFlowChoice === '2.2' && referenceImagePreview && <span className="text-white/20 text-[9px]">(Format vom Referenzbild)</span>}</label>
                              <div className="grid grid-cols-4 gap-1.5">
                                {[
                                  { value: 'portrait_9_16', label: '9:16', sub: 'Storys, Reels, Shorts', w: 'w-3.5', h: 'h-6' },
                                  { value: 'landscape_16_9', label: '16:9', sub: 'Film, Kino, YouTube', w: 'w-7', h: 'h-4' },
                                  { value: 'square_1_1', label: '1:1', sub: 'Feed', w: 'w-5', h: 'h-5' },
                                  { value: 'portrait_4_5', label: '4:5', sub: 'Instagram Feed Max', w: 'w-5', h: 'h-6' },
                                ].map((f) => (
                                  <button key={f.value} data-demo-image-format={f.value} disabled={referenceFlowChoice === '2.2' && referenceImagePreview} onClick={() => { if (!(referenceFlowChoice === '2.2' && referenceImagePreview)) setImageFormat(f.value); }}
                                    className={`h-[90px] rounded-lg text-[10px] font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                                      referenceFlowChoice === '2.2' && referenceImagePreview ? (imageFormat === f.value ? 'bg-white/20 text-white/40 border border-white/10' : 'bg-white/5 text-white/20 border border-white/5') :
                                      imageFormat === f.value ? 'bg-[#10B981] text-black shadow-lg shadow-[#10B981]/30' : 'bg-white/10 text-white/80 hover:bg-white/20 border border-white/10'}`}>
                                    <div className="h-6 flex items-center justify-center"><div className={`${f.w} ${f.h} rounded-[2px] border-2 ${imageFormat === f.value ? 'border-black/50' : 'border-white/40'}`} /></div>
                                    <div className="font-bold text-[11px] leading-tight">{f.label}</div>
                                    <div className={`text-[8px] leading-tight text-center px-0.5 ${imageFormat === f.value ? 'text-black/50' : 'text-white/40'}`}>{f.sub}</div>
                                  </button>
                                ))}
                              </div>
                            </div>
                            {/* Anzahl Bilder */}
                            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                              <label className="block text-xs font-medium text-white/80 mb-2 text-center">Anzahl Bilder</label>
                              <div className="grid grid-cols-4 gap-1.5">
                                {[1, 2, 3, 4].map((n) => (
                                  <button key={n} onClick={() => setNumImages(n)}
                                    className={`h-[90px] rounded-lg text-center font-medium transition-all flex flex-col items-center justify-center gap-1.5 ${numImages === n ? 'bg-[#10B981] text-black shadow-lg shadow-[#10B981]/30' : 'bg-white/10 text-white/80 hover:bg-white/20 border border-white/10'}`}>
                                    <div className="flex items-end gap-[1px]">
                                      {Array.from({ length: n }, (_, i) => (
                                        <div key={i} className={`w-3.5 rounded-[2px] border ${numImages === n ? 'border-black/40 bg-black/10' : 'border-white/30 bg-white/5'}`}
                                          style={{ height: `${14 + i * 2}px`, marginBottom: `${(n - 1 - i) * 1}px` }} />
                                      ))}
                                    </div>
                                    <div className="text-[11px] font-bold">{n} {n === 1 ? 'Bild' : 'Bilder'}</div>
                                    <div className={`text-[9px] ${numImages === n ? 'text-black/40' : 'text-white/30'}`}>({snapText(n * (pricingSettings?.snaps_per_shot || 1) * ({low:0.5, medium:1, high:4}[imageQuality] || 1))})</div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>{/* Ende Grid: Bildformat + Anzahl */}

                          {/* ═══ ZEILE 4: Qualität (Standard / HD / Ultra) ═══ */}
                          <div className="mt-4 bg-white/5 border border-white/10 rounded-xl p-4">
                            <label className="block text-xs font-medium text-white/80 mb-2 text-center">
                              Qualität
                              <span className="text-white/40 text-[9px] ml-1">— bestimmt Detailgrad und SNAP-Preis</span>
                            </label>
                            <div className="grid grid-cols-3 gap-1.5">
                              {[
                                { value: 'low',    label: 'Standard', sub: '1K · günstig',    mult: 0.5 },
                                { value: 'medium', label: 'HD',       sub: '2K · empfohlen',  mult: 1.0 },
                                { value: 'high',   label: 'Ultra',    sub: '4K · maximal',    mult: 4.0 },
                              ].map((q) => (
                                <button key={q.value} onClick={() => setImageQuality(q.value)}
                                  className={`h-[80px] rounded-lg font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                                    imageQuality === q.value
                                      ? 'bg-[#10B981] text-black shadow-lg shadow-[#10B981]/30'
                                      : 'bg-white/10 text-white/80 hover:bg-white/20 border border-white/10'
                                  }`}>
                                  <div className="font-bold text-[13px] leading-tight">{q.label}</div>
                                  <div className={`text-[9px] leading-tight ${imageQuality === q.value ? 'text-black/50' : 'text-white/40'}`}>{q.sub}</div>
                                  <div className={`text-[10px] font-bold ${imageQuality === q.value ? 'text-black/70' : 'text-[#10B981]/80'}`}>
                                    {snapText(numImages * (pricingSettings?.snaps_per_shot || 1) * q.mult)}
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>

                        {/* 3. Generate Button */}
                        {/* Generate Button — auch während laufender Generierung klickbar */}
                          <button
                            id="demo-image-generate-button"
                            onClick={readOnlyMode
                              ? () => { navigate('/welcome'); setTimeout(() => window.scrollTo(0, 0), 100); }
                              : (isDemoTourEligible ? () => triggerDemoImageGeneration(demoRunIdRef.current || Date.now(), promptText) : handleCreateSuperImage)}
                            disabled={!readOnlyMode && !promptText && !referenceImagePreview}
                            className={`mt-2 w-full py-5 rounded-2xl font-semibold text-lg flex items-center justify-center gap-3 transition-all duration-300 ${
                              readOnlyMode
                                ? 'bg-gradient-to-r from-[#10B981] to-[#059669] text-black hover:shadow-xl hover:shadow-[#10B981]/40 hover:scale-[1.02] shadow-lg shadow-[#10B981]/30'
                                : (!promptText && !referenceImagePreview)
                                ? 'bg-white/10 text-white/40 cursor-not-allowed border-2 border-white/10'
                                : 'bg-gradient-to-r from-[#10B981] to-[#059669] text-black hover:shadow-2xl hover:shadow-[#10B981]/50 hover:scale-[1.02] shadow-lg shadow-[#10B981]/30 active:scale-[0.98]'
                            }`}
                            data-testid="create-super-image-button"
                          >
                                {readOnlyMode ? (
                              <>
                                <Sparkles className="w-6 h-6" />
                                Registrieren zum Nutzen
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-6 h-6" />
                                <span>{numImages} {numImages === 1 ? 'Bild' : 'Bilder'} generieren ({snapText(numImages * (pricingSettings?.snaps_per_shot || 1))})</span>
                              </>
                            )}
                          </button>

                        {/* Inline-Fehler für SNAP-Knappheit (ersetzt Browser-alert) */}
                        {imageError && (
                          <div className="mt-3 bg-red-500/20 border border-red-500/50 rounded-xl p-3 text-red-400 text-sm">
                            <div className="flex items-start justify-between gap-2">
                              <span className="flex-1">{imageError}</span>
                              <button
                                onClick={() => setImageError(null)}
                                className="text-red-400/60 hover:text-red-400 leading-none"
                                aria-label="Schließen"
                              >
                                ×
                              </button>
                            </div>
                            <a
                              href="/credits"
                              className="mt-2 inline-block text-[#10B981] font-bold text-sm hover:underline"
                            >
                              → SNAPs jetzt aufladen
                            </a>
                          </div>
                        )}

                        {/* 4. Erweiterte Optionen Toggle */}
                        <button style={{display:"none"}}
                          onClick={() => setAdvancedMode(!advancedMode)}
                          className={`w-full py-4 px-5 rounded-xl border-2 flex items-center justify-between transition-all ${
                            advancedMode 
                              ? 'bg-[#10B981]/20 border-[#10B981]' 
                              : 'bg-white/5 border-white/20 hover:border-white/40'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-xl">⚙️</span>
                            <div className="text-left">
                              <p className="font-semibold text-white">Erweiterte Optionen</p>
                              <p className="text-xs text-white/50">
                                {advancedMode ? 'Vorlagen & Feinsteuerung aktiv' : 'Tageszeit, Wetter, Kamera, Stil...'}
                              </p>
                            </div>
                          </div>
                          <div className={`w-12 h-7 rounded-full p-1 transition-colors ${
                            advancedMode ? 'bg-[#10B981]' : 'bg-white/20'
                          }`}>
                            <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                              advancedMode ? 'translate-x-5' : 'translate-x-0'
                            }`} />
                          </div>
                        </button>
                        
                        {/* 5. Erweiterte Optionen - NUR wenn Toggle AN */}
                        {false && (
                          <div className="space-y-4 pt-2">
                            {/* Warnung */}
                            <div className="px-4 py-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                              <p className="text-yellow-400 text-xs font-medium">
                                ⚠️ Hinweis: Die Chips ergänzen deinen Text automatisch. Falls du bereits einen fertigen Prompt eingefügt hast, können doppelte Parameter entstehen.
                              </p>
                            </div>
                            
                            {/* Info-Box */}
                            <div className="text-center py-2 px-4 bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg">
                              <p className="text-[#10B981] font-bold text-sm">💡 Tipp: Lass dich von unseren Beispiel-Ideen inspirieren!</p>
                              <p className="text-white/70 text-xs mt-1">
                                Avatar: {userData?.avatarName} · ID #{userData?.idNumber}
                              </p>
                            </div>
                            
                            {/* Vorlagen-Button */}
                            <button
                              onClick={() => setShowInfoModal(true)}
                              className="w-full border-t-4 border-[#10B981]/40 rounded-xl overflow-hidden bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm hover:bg-white/10 transition-all duration-200 shadow-lg"
                              data-testid="open-templates-button"
                            >
                              <div className="px-5 py-4 flex items-center justify-center gap-2">
                                <svg className="w-5 h-5 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                                </svg>
                                <span className="font-semibold text-white text-base">Vorlagen anzeigen ({PROMPT_TEMPLATES.length} Ideen)</span>
                              </div>
                            </button>
                            
                            {/* Live Prompt-Vorschau - nur wenn Text vorhanden */}
                            {promptText && (
                              <div className="p-4 bg-gradient-to-br from-[#10B981]/10 to-[#059669]/10 backdrop-blur-xl border border-[#10B981]/20 rounded-2xl">
                                <div className="flex items-center gap-2 mb-3">
                                  <Sparkles className="w-4 h-4 text-[#10B981]" />
                                  <h3 className="text-sm font-semibold text-[#10B981]">Wird automatisch hinzugefügt:</h3>
                                </div>
                                
                                <div className="space-y-2">
                                  {/* Tageszeit */}
                                  <div className="flex items-start gap-2">
                                    <span className="text-lg">🌅</span>
                                    <div className="flex-1">
                                      <p className="text-xs font-medium text-[#10B981]">{getLabelForValue('timeOfDay', timeOfDay)}</p>
                                      <p className="text-xs text-gray-300 leading-relaxed">{getKeywordsForValue('timeOfDay', timeOfDay)}</p>
                                    </div>
                                  </div>
                                  
                                  {/* Wetter */}
                                  <div className="flex items-start gap-2">
                                    <span className="text-lg">☀️</span>
                                    <div className="flex-1">
                                      <p className="text-xs font-medium text-[#10B981]">{getLabelForValue('weather', weather)}</p>
                                      <p className="text-xs text-gray-300 leading-relaxed">{getKeywordsForValue('weather', weather)}</p>
                                    </div>
                                  </div>
                                  
                                  {/* Kamera */}
                                  <div className="flex items-start gap-2">
                                    <span className="text-lg">📷</span>
                                    <div className="flex-1">
                                      <p className="text-xs font-medium text-[#10B981]">{getLabelForValue('camera', camera)}</p>
                                      <p className="text-xs text-gray-300 leading-relaxed">{getKeywordsForValue('camera', camera)}</p>
                                    </div>
                                  </div>
                                  
                                  {/* Bildstil */}
                                  <div className="flex items-start gap-2">
                                    <span className="text-lg">🎨</span>
                                    <div className="flex-1">
                                      <p className="text-xs font-medium text-[#10B981]">{getLabelForValue('style', style)}</p>
                                      <p className="text-xs text-gray-300 leading-relaxed">{getKeywordsForValue('style', style)}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {/* V6-STYLE: MODERNE SLIDE-UP PICKER */}
                            <V6ModernPicker
                              timeOfDay={timeOfDay}
                              setTimeOfDay={setTimeOfDay}
                              weather={weather}
                              setWeather={setWeather}
                              camera={camera}
                              setCamera={setCamera}
                              style={style}
                              setStyle={setStyle}
                              substyle={substyle}
                              setSubstyle={setSubstyle}
                              imageFormat={imageFormat}
                              setImageFormat={setImageFormat}
                            />
                            
                            {/* Jupiter Empfehlungen */}
                            {jupiterRecommendations && (
                              <div className="p-4 bg-gradient-to-br from-[#10B981]/15 to-[#059669]/10 border-2 border-[#10B981]/40 rounded-xl" data-testid="jupiter-recommendations-box">
                                <div className="flex items-start gap-3 mb-3">
                                  <div className="w-8 h-8 bg-gradient-to-br from-[#10B981] to-[#059669] rounded-full flex items-center justify-center flex-shrink-0">
                                    <Sparkles className="w-4 h-4 text-black" />
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="text-sm font-bold text-[#10B981] mb-1">Julias Empfehlungen</h4>
                                    <p className="text-xs text-white/70">{jupiterRecommendations.summary}</p>
                                  </div>
                                  <button 
                                    onClick={() => setJupiterRecommendations(null)}
                                    className="text-white/40 hover:text-white transition-colors"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                                
                                <div className="space-y-2">
                                  {jupiterRecommendations.recommendations.map((rec, idx) => (
                                    <div key={idx} className="p-3 bg-black/30 border border-white/10 rounded-lg">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-[#10B981] font-bold text-sm">#{idx + 1}</span>
                                        <span className="text-white font-semibold text-sm">
                                          {rec.category === 'portrait' ? 'Portrait' : rec.category === 'full' ? 'Ganzkörper' : 'Cinematic'} → {rec.style}
                                        </span>
                                      </div>
                                      <p className="text-xs text-white/70">{rec.reason}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* ACCORDION 2: Julia Voice Helper - AUSGEBLENDET */}
              {false && userData?.status === 'ready' && (
                <JuliaInlinePanel onPromptApply={handleJuliaPromptApply} />
              )}
              
              {/* ACCORDION 2: Video erstellen (öffnet VideoGeneratorFullscreen Studio) */}
              {canShowGeneratorSections && (
                <div
                  id="demo-video-accordion"
                  data-demo-anchor="video-create"
                  data-accordion="video-create"
                  className="border-t-4 border-[#10B981]/40 rounded-xl overflow-visible bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm"
                  style={{ scrollMarginTop: '120px' }}
                >
                  {/* Accordion Header */}
                  <button
                    id="demo-video-accordion-header"
                    onClick={() => {
                      if (readOnlyMode) {
                        navigate('/welcome');
                        window.scrollTo(0, 0);
                        return;
                      }
                      const wasOpen = !!accordionOpen.videoCreate;
                      toggleAccordion('videoCreate');
                      // Beim Öffnen: scroll-jump verhindern, indem wir den Video-Header
                      // gezielt in den Viewport holen (das vorher offene Accordion wird
                      // geschlossen, dadurch verschiebt sich der Header optisch nach oben).
                      if (!wasOpen) {
                        requestAnimationFrame(() => {
                          setTimeout(() => {
                            const el = document.querySelector('[data-accordion="video-create"]');
                            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }, 60);
                        });
                      }
                    }}
                    className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#10B981]/20 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="text-left">
                        <h3 className="text-white font-bold text-lg">Video erstellen</h3>
                        <p className="text-[#10B981]/80 text-xs">
                          Erwecke deine Bilder zum Leben
                        </p>
                      </div>
                    </div>
                    {accordionOpen.videoCreate ? (
                      <ChevronUp className="w-6 h-6 text-[#10B981]" />
                    ) : (
                      <ChevronDown className="w-6 h-6 text-white/50" />
                    )}
                  </button>

                  {/* Accordion Content - geführte Video-Generierung */}
                  {/* Tabs: immer sichtbar, einer aktiv, Klick wechselt Modus */}
                  {accordionOpen.videoCreate && !isDemoTourEligible && (
                    <div className="border-t border-[#10B981]/20 p-5 sm:p-6 pb-0 animate-slide-down">
                      <div className="space-y-4">
                        {(() => {
                          const vtabs = [
                            { id: 'shorts',    label: 'Shorts',     bg: '/mode-cards/vid-shorts.png',    desc: 'Kurze Hochkant-Clips für Reels & Stories.' },
                            { id: 'kino',      label: 'Kino-Modus', bg: '/mode-cards/vid-kino.png',      desc: 'Filmische Sequenzen, längere Szenen.' },
                            { id: 'hollywood', label: 'Hollywood',  bg: '/mode-cards/vid-hollywood.png', desc: 'Multi-Referenzen kombinieren, du führst Regie.' },
                          ];
                          const vtone = {
                            shorts:    { ring: 'border-pink-400/80 ring-2 ring-pink-400/30',     tint: 'bg-gradient-to-tr from-pink-900/70 via-black/40 to-pink-700/10' },
                            kino:      { ring: 'border-amber-400/80 ring-2 ring-amber-400/30',   tint: 'bg-gradient-to-tr from-amber-900/70 via-black/40 to-amber-700/10' },
                            hollywood: { ring: 'border-cyan-400/80 ring-2 ring-cyan-400/30',     tint: 'bg-gradient-to-tr from-cyan-900/70 via-black/40 to-cyan-700/10' },
                          };
                          return (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              {vtabs.map(t => {
                                const isActive = videoMode === t.id;
                                const c = vtone[t.id];
                                return (
                                  <button key={t.id}
                                onClick={() => enterVideoMode(t.id)}
                                className={`relative h-32 rounded-2xl border-2 overflow-hidden transition-all focus:outline-none text-left group ${
                                  isActive ? c.ring : 'border-white/10 hover:border-white/30'
                                }`}>
                                <img src={t.bg} alt="" aria-hidden
                                  className={`absolute inset-0 w-full h-full object-cover transition-opacity ${
                                    isActive ? 'opacity-80' : 'opacity-45 group-hover:opacity-65'
                                  }`} />
                                <div className={`absolute inset-0 transition-opacity ${
                                  isActive ? c.tint : 'bg-gradient-to-t from-black/90 via-black/50 to-black/10'
                                }`} />
                                <div className="relative h-full flex flex-col justify-end p-4">
                                  <h5 className={`font-bold text-base leading-tight mb-1 drop-shadow-md ${isActive ? 'text-white' : 'text-white/90'}`}>{t.label}</h5>
                                  <p className={`text-xs leading-snug drop-shadow-md ${isActive ? 'text-white/85' : 'text-white/65'}`}>{t.desc}</p>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })()}
                        {/* DEAD CODE — Original 3-Button Picker, wird unten gelöscht */}
                        <div className="hidden">
                          <button onClick={() => enterVideoMode('kino')}
                            className="group bg-white/5 hover:bg-amber-500/10 border border-white/10 hover:border-amber-400/60 rounded-2xl p-4 text-left transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400/40 flex items-start gap-4">
                            <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-500/30 to-amber-700/10 group-hover:from-amber-400/40 group-hover:to-amber-600/15 ring-1 ring-amber-400/20 group-hover:ring-amber-400/50 flex items-center justify-center flex-shrink-0 transition-all shadow-lg shadow-amber-500/10 overflow-hidden">
                              {/* Filmrolle */}
                              <svg className="w-11 h-11 text-amber-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 4.5h18M3 8.25h18M3 12h18M3 15.75h18M3 19.5h18" />
                                <rect x="3" y="4.5" width="18" height="15" rx="1.5" />
                                <circle cx="7" cy="12" r="1.5" fill="currentColor" />
                                <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                                <circle cx="17" cy="12" r="1.5" fill="currentColor" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-white font-bold text-base mb-1.5 leading-tight">Kino-Modus</h5>
                              <p className="text-white/60 text-xs leading-relaxed">Filmische Sequenzen mit ruhigen Kamerabewegungen, klassisches Seitenverhältnis. Für Storytelling-Clips und längere Szenen.</p>
                            </div>
                          </button>
                          {/* Shorts */}
                          <button onClick={() => enterVideoMode('shorts')}
                            className="group bg-white/5 hover:bg-pink-500/10 border border-white/10 hover:border-pink-400/60 rounded-2xl p-4 text-left transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-pink-400/40 flex items-start gap-4">
                            <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-pink-500/30 to-pink-700/10 group-hover:from-pink-400/40 group-hover:to-pink-600/15 ring-1 ring-pink-400/20 group-hover:ring-pink-400/50 flex items-center justify-center flex-shrink-0 transition-all shadow-lg shadow-pink-500/10 overflow-hidden">
                              {/* Hochkant-Smartphone mit Play */}
                              <svg className="w-11 h-11 text-pink-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <rect x="7" y="2.5" width="10" height="19" rx="2" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M10 18h4" />
                                <path d="M10.5 9.5l4 2.5-4 2.5z" fill="currentColor" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-white font-bold text-base mb-1.5 leading-tight">Shorts</h5>
                              <p className="text-white/60 text-xs leading-relaxed">Kurze, dynamische Hochkant-Clips für Reels, Stories und TikTok. Schnell generiert, sofort einsatzbereit.</p>
                            </div>
                          </button>
                          {/* Hollywood */}
                          <button onClick={() => enterVideoMode('hollywood')}
                            className="group bg-white/5 hover:bg-cyan-500/10 border border-white/10 hover:border-cyan-400/60 rounded-2xl p-4 text-left transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-400/40 flex items-start gap-4">
                            <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500/30 to-cyan-700/10 group-hover:from-cyan-400/40 group-hover:to-cyan-600/15 ring-1 ring-cyan-400/20 group-hover:ring-cyan-400/50 flex items-center justify-center flex-shrink-0 transition-all shadow-lg shadow-cyan-500/10 overflow-hidden">
                              {/* Mehrere überlappende Bild-Rahmen für Multi-Reference */}
                              <svg className="absolute w-10 h-10 text-cyan-300/40 -rotate-12 -translate-x-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                              </svg>
                              <svg className="absolute w-10 h-10 text-cyan-300/55 rotate-6 translate-x-1 translate-y-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                              </svg>
                              <svg className="relative w-9 h-9 text-cyan-100 drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-white font-bold text-base mb-1.5 leading-tight">Hollywood</h5>
                              <p className="text-white/60 text-xs leading-relaxed">Multi-Referenzen kombinieren — Start-Frame, End-Frame, weitere Bilder & Videos. Du regiest die Szene.</p>
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Hollywood inline rendering */}
                  {accordionOpen.videoCreate && !isDemoTourEligible && videoMode === 'hollywood' && (
                    <div className="border-t border-[#10B981]/20 p-5 sm:p-6 animate-slide-down">
                      <HollywoodSection
                        embedded={true}
                        open={true}
                        activeAvatarId={avatars[activeAvatarIndex]?.id}
                        presetPrompt={hollywoodPreset?.prompt}
                        presetSlots={hollywoodPreset?.slots}
                        onPresetConsumed={() => setHollywoodPreset(null)}
                      />
                    </div>
                  )}

                  {/* Kino-Modus / Shorts — VideoGeneratorFullscreen mit modus-spezifischem Preset */}
                  {accordionOpen.videoCreate && !isDemoTourEligible && (videoMode === 'kino' || videoMode === 'shorts') && (
                    <div className="border-t-0 animate-slide-down">
                      <VideoGeneratorFullscreen
                        image={{ url: videoPreselectedImage || null, studioMode: true }}
                        preselectedImageFirst={videoPreselectedImage}
                        preselectedPrompt={videoPreselectedPrompt}
                        preselectedMotionTags={videoPreselectedMotionTags}
                        preselectedVideoSettings={videoPreselectedSettings}
                        preselectedModel={videoPreselectedModel}
                        hideModelSelector={true}
                        onPromptConsumed={() => { setVideoPreselectedPrompt(null); setVideoPreselectedMotionTags(null); setVideoPreselectedSettings(null); setVideoPreselectedModel(null); }}
                        inline={true}
                        avatarId={avatars[activeAvatarIndex]?.id}
                        onClose={() => setAccordionOpen(prev => ({...prev, videoCreate: false}))}
                        preloadedPhotos={preloadedPhotos}
                        onGenerate={() => {
                          // Videos-Tab öffnen + History schließen
                          setGalleryTab('videos');
                          setAccordionOpen(prev => ({...prev, quickActions: false}));
                          // Pending-Animation + Scroll NACH React-Re-Render (Tab muss umgeschaltet sein)
                          requestAnimationFrame(() => {
                            setTimeout(() => {
                              // JETZT ist der Videos-Tab gerendert — Pending-Eintrag anzeigen
                              window.dispatchEvent(new CustomEvent('instantPendingVideo'));
                              window.dispatchEvent(new CustomEvent('fetchPendingVideos'));
                              // Zur Galerie scrollen
                              const gallerySection = document.getElementById('video-gallery-section');
                              if (gallerySection) {
                                const targetY = window.scrollY + gallerySection.getBoundingClientRect().top - 120;
                                window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
                              }
                            }, 100);
                          });
                        }}
                        onVideoReady={(videoData) => {
                          setKinoVideoReady(videoData);
                          setGalleryTab('videos');
                          // Accordion bleibt offen — User kann weiterarbeiten
                        }}
                        onVideoCompleted={async () => {
                          setGalleryTab('videos');
                          // Gallery-Video-Liste sofort aktualisieren
                          setTimeout(() => {
                            window.dispatchEvent(new CustomEvent('refreshVideoGallery'));
                          }, 500);
                          // History aktualisieren wenn Accordion offen
                          if (accordionOpen.quickActions) loadPromptHistory();
                        }}
                      />
                    </div>
                  )}

                  {accordionOpen.videoCreate && isDemoTourEligible && (
                    <div className="border-t border-[#10B981]/20 animate-slide-down">
                      <div className="relative">
                        <div className="pointer-events-none select-none">
                          <VideoGeneratorFullscreen
                            image={{ url: demoVideoSourceImage || effectiveDemoImageUrl || null, studioMode: true }}
                            preselectedImageFirst={demoVideoSourceImage || effectiveDemoImageUrl || null}
                            preselectedModel="cinematic_video"
                            demoPromptText={demoVideoPrompt}
                            inline={true}
                            avatarId={demoCardData?.id}
                            onClose={() => setAccordionOpen(prev => ({...prev, videoCreate: false}))}
                            preloadedPhotos={preloadedPhotos}
                            disableAutofocus={true}
                          />
                        </div>
                        <div className="absolute inset-0 z-10 cursor-not-allowed" />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Hollywood ist jetzt im Video-Accordion eingebettet (Picker → Hollywood) —
                  das standalone-Accordion wurde entfernt. */}

              {/* ACCORDION 3: History */}
              {userData?.status === 'ready' && !isDemoTourEligible && (
                <div data-accordion="history" className="border-t-4 border-[#10B981]/40 rounded-xl overflow-hidden bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm">
                  {/* Accordion Header */}
                  <button
                    onClick={() => {
                      const wasOpen = accordionOpen.quickActions;
                      toggleAccordion('quickActions');
                      // Erst beim Öffnen laden + Header in Viewport scrollen
                      if (!wasOpen) {
                        loadPromptHistory();
                        requestAnimationFrame(() => {
                          setTimeout(() => {
                            const el = document.querySelector('[data-accordion="history"]');
                            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }, 60);
                        });
                      }
                    }}
                    className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#10B981]/20 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="text-left">
                        <h3 className="text-white font-bold text-lg">History & Vorlagen</h3>
                        <p className="text-[#10B981]/80 text-xs">
                          Deine letzten Prompts & Ideen
                        </p>
                      </div>
                    </div>
                    {accordionOpen.quickActions ? (
                      <ChevronUp className="w-6 h-6 text-[#10B981]" />
                    ) : (
                      <ChevronDown className="w-6 h-6 text-white/50" />
                    )}
                  </button>

                  {/* Accordion Content — History inline */}
                  {accordionOpen.quickActions && (
                    <div className="border-t border-[#10B981]/20 p-4 sm:p-6 animate-slide-down overflow-x-hidden" style={{ touchAction: 'pan-y' }}>
                      {loadingHistory ? (
                        <div className="text-center py-8">
                          <div className="w-10 h-10 border-2 border-[#10B981]/30 border-t-[#10B981] rounded-full animate-spin mx-auto mb-3" />
                          <p className="text-white/50 text-sm">Lade History...</p>
                        </div>
                      ) : promptHistory.length === 0 ? (
                        <div className="text-center py-8">
                          <svg className="w-12 h-12 text-white/20 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-white/50 text-sm">Noch keine History vorhanden</p>
                          <p className="text-white/30 text-xs mt-1">Erstelle dein erstes Bild!</p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-[60vh] overflow-y-auto overflow-x-hidden" style={{ touchAction: 'pan-y', overscrollBehavior: 'contain' }}>
                          {promptHistory.map((item, index) => {
                            const isVideo = item.generation_type === 'video';
                            const costSnaps = item.cost_snaps || 0;
                            return (
                              <div
                                key={item.id}
                                className={`bg-gradient-to-br from-white/5 to-white/[0.02] border rounded-xl p-4 transition-all ${
                                  isVideo ? 'border-purple-500/30' : 'border-white/10 hover:border-[#10B981]/50'
                                }`}
                              >
                                <div className="flex items-center justify-between mb-2 min-w-0">
                                  <div className="flex items-center gap-2 min-w-0 flex-shrink overflow-hidden">
                                    <span className={`w-6 h-6 flex-shrink-0 flex items-center justify-center rounded ${
                                      isVideo ? 'bg-purple-500/20' : 'bg-[#10B981]/20'
                                    }`}>
                                      {isVideo ? (
                                        <svg className="w-3.5 h-3.5 text-purple-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
                                        </svg>
                                      ) : (
                                        <svg className="w-3.5 h-3.5 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
                                        </svg>
                                      )}
                                    </span>
                                    <span className={`px-2 py-0.5 border rounded text-xs font-bold ${
                                      isVideo ? 'bg-purple-500/20 border-purple-500/40 text-purple-300' : 'bg-[#10B981]/20 border-[#10B981]/40 text-[#10B981]'
                                    }`}>
                                      #{promptHistory.length - index}
                                    </span>
                                    <span className="text-white/40 text-xs">
                                      {new Date(item.created_at + (String(item.created_at).includes('Z') || String(item.created_at).includes('+') ? '' : 'Z')).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    {(() => {
                                      const isRemix = item.generation_type === 'remix' || item.prompt_source === 'remix';
                                      const label = isRemix ? 'Remix' : isVideo ? (item.video_model === 'kino_modus' || item.video_model === 'cinematic_video' ? 'Kino' : 'Shorts') : 'Foto';
                                      const color = label === 'Kino' ? 'bg-amber-500/15 border-amber-500/30 text-amber-300' : label === 'Remix' ? 'bg-cyan-500/15 border-cyan-500/30 text-cyan-300' : isVideo ? 'bg-purple-500/15 border-purple-500/30 text-purple-300' : 'bg-[#10B981]/15 border-[#10B981]/30 text-[#10B981]';
                                      return <span className={`px-2 py-0.5 border rounded text-xs font-bold ${color}`}>{label}</span>;
                                    })()}
                                    {costSnaps > 0 && (
                                      <span className="px-2 py-0.5 bg-white/5 border border-white/10 rounded text-white/50 text-xs">
                                        {costSnaps} SNAP{costSnaps !== 1 ? 's' : ''}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <p className="text-white font-medium text-sm leading-relaxed mb-3">
                                  {item.user_prompt ? (item.user_prompt.length > 200 ? item.user_prompt.substring(0, 200) + '...' : item.user_prompt) : '(Kein Text)'}
                                </p>
                                {!readOnlyMode && (
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleUsePrompt(item)}
                                      className="flex-1 py-2 px-3 bg-[#10B981] hover:bg-[#059669] text-black font-bold text-sm rounded-lg transition-all flex items-center justify-center gap-2"
                                    >
                                      Übernehmen
                                    </button>
                                    <button
                                      onClick={() => handleDeletePrompt(item.id)}
                                      className="py-2 px-3 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 text-sm rounded-lg transition-all"
                                    >
                                      Löschen
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Create First Avatar - MEGA PROMINENT für neue User */}
              {userData?.status === 'pending' && (
                <div className="text-center">
                  <p className="text-white/70 text-base mb-4">
                    Du hast noch keinen Avatar erstellt
                  </p>
                  <button
                    onClick={onStartCreation}
                    className="w-full py-5 px-8 bg-[#10B981] text-black rounded-2xl hover:bg-[#059669] transition-all font-bold text-lg glow-green pulse-glow shine-effect inline-flex items-center justify-center shadow-2xl"
                    data-testid="create-first-avatar-button"
                  >
                    Deinen ersten Avatar erstellen
                  </button>
                </div>
              )}

              {/* Training Cancel Button */}
              {userData?.status === 'training' && (
                <div className="space-y-3">
                  <div className="text-center py-2">
                    <p className="text-yellow-500 text-sm font-medium">
                      ⏳ Dein Avatar wird gerade trainiert...
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (window.confirm('Training wirklich abbrechen? Der Avatar wird gelöscht.')) {
                        // Refresh page to reset state
                        window.location.reload();
                      }
                    }}
                    className="w-full py-3 px-6 border-2 border-red-500/50 text-red-500 rounded-xl hover:bg-red-500/10 transition-all font-medium text-base"
                    data-testid="cancel-training-button"
                  >
                    Training abbrechen
                  </button>
                </div>
              )}

              {/* Expired Avatar + SNAPs vorhanden (nur wenn Avatar existiert, KEIN Auto-Extend, wirklich abgelaufen) */}
              {!countdownInfo.active && userData?.status === 'ready' && userData?.expires_at && !userData?.auto_extend && (userData?.credits || 0) > 0 && (
                <div className="space-y-4 p-6 bg-gradient-to-br from-orange-500/20 via-red-500/10 to-black border-2 border-orange-500/40 rounded-2xl glow-orange-soft">
                  <div className="text-center mb-5">
                    <p className="text-orange-400 text-xl font-black mb-3 tracking-wide">
                      ⚡ Game Over für diesen Avatar!
                    </p>
                    <p className="text-white/80 text-sm mb-2">
                      Du hast noch <span className="text-[#10B981] font-bold">{Math.round((userData?.credits || 0) * 100) / 100} SNAPs</span> übrig.
                    </p>
                    <div className="mt-4 p-4 bg-black/40 backdrop-blur-sm border border-white/10 rounded-xl text-left">
                      <p className="text-white/90 text-sm mb-3">
                        Deine Bilder bleiben safe, aber neue Kreationen?<br/>
                        <span className="text-emerald-400 font-bold">Dafür brauchst du frische Power!</span> 💪
                      </p>
                      <p className="text-emerald-400 text-sm font-bold mb-2">
                        🚀 Was jetzt?
                      </p>
                      <ul className="text-white/70 text-sm space-y-1.5">
                        <li className="flex items-center gap-2">
                          <span className="text-[#10B981]">🎨</span>
                          Schnapp dir einen deiner anderen Avatare
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-[#10B981]">✨</span>
                          Erschaffe einen brandneuen Avatar
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-[#10B981]">💚</span>
                          Schenk deine SNAPs einem Freund
                        </li>
                      </ul>
                    </div>
                  </div>

                  <Link
                    to="/credits"
                    className="block w-full py-3 px-6 bg-[#10B981] text-black rounded-xl hover:bg-[#059669] transition-all font-bold text-center glow-green pulse-glow"
                    data-testid="buy-extension-button"
                  >
                    💰 SNAPs aufladen
                  </Link>

                  <button
                    onClick={() => setShowTransferModal(true)}
                    className="w-full py-3 px-6 border-2 border-white/30 text-white rounded-xl hover:bg-white/5 hover:border-[#10B981] transition-all font-medium"
                    data-testid="transfer-snaps-button"
                  >
                    🎁 SNAPs verschenken
                  </button>

                  <p className="text-xs text-white/50 text-center leading-relaxed">
                    Falls deine SNAPs nicht mehr reichen, kannst du sie an Avatar-Freunde 
                    auf der Plattform weitergeben – gemeinsam sind wir stärker! 💚
                  </p>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Generierungs-Banner - DEAKTIVIERT (Animation läuft jetzt im Button!) */}
      {false && isGenerating && (
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4"
          style={{ touchAction: 'none' }}
        >
          <div className="bg-black border-2 border-[#10B981] rounded-2xl p-6 max-w-md w-full text-center glow-green-soft">
            {/* Kompakte Info */}
            <div className="mb-4">
              <div className="w-16 h-16 mx-auto mb-3 relative">
                <div className="absolute inset-0 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin"></div>
                <div className="absolute inset-2 border-4 border-[#059669] border-t-transparent rounded-full animate-spin" style={{animationDirection: 'reverse', animationDuration: '0.8s'}}></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
              </div>
              <h3 className="text-xl font-bold text-white mb-1">Deine Bilder entstehen! ✨</h3>
              <p className="text-xs text-white/50">Ca. 30-60 Sekunden</p>
            </div>
            
            {/* SPIELE - Zufällig ausgewählt! */}
            <div className="mb-3">
              {currentGame === 0 ? <SnapshotSnake /> : <SnapshotPong />}
              
              <p className="text-xs text-white/40 mt-3">
                {currentGame === 0 ? '🐍 Snake' : '🏓 Pong vs. KI'}
              </p>
            </div>
            
            {/* Dots */}
            <div className="flex justify-center gap-2">
              <div className="w-2 h-2 bg-[#10B981] rounded-full animate-bounce" style={{animationDelay: '0s'}}></div>
              <div className="w-2 h-2 bg-[#10B981] rounded-full animate-bounce" style={{animationDelay: '0.15s'}}></div>
              <div className="w-2 h-2 bg-[#10B981] rounded-full animate-bounce" style={{animationDelay: '0.3s'}}></div>
            </div>
          </div>
        </div>
      )}
      
      {/* Transfer Modal */}
        {showTransferModal && (
          <SnapTransferModal
            availableSnaps={userData?.credits || 0}
            onClose={() => setShowTransferModal(false)}
            onSuccess={() => {
              // Reload data after transfer
              fetchPhotos();
            }}
          />
        )}

        {/* Avatar Selector Dropdown - HIDDEN (replaced by menu navigation) */}
        {avatars.length > 1 && (
          <div className="hidden mb-8 p-6 rounded-2xl bg-gradient-to-br from-[#10B981]/15 via-black to-[#059669]/15 border-2 border-[#10B981]/50 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-[#10B981]/10 rounded-full blur-3xl"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#10B981] glow-green-soft animate-pulse"></div>
                  <label className="text-base font-bold text-[#10B981] tracking-wide">
                    Aktiver Avatar
                  </label>
                </div>
                <span className="text-xs text-white/50 px-3 py-1 bg-white/5 rounded-full border border-white/10">
                  {avatars.length} verfügbar
                </span>
              </div>
              
              <div className="relative">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
                  <User className="w-5 h-5 text-[#10B981]" />
                </div>
                <select
                  value={activeAvatarIndex}
                  onChange={(e) => handleSwitchAvatar(parseInt(e.target.value))}
                  className="w-full pl-12 pr-10 py-4 bg-black/60 border-2 border-[#10B981]/60 rounded-2xl text-white font-bold text-lg focus:border-[#10B981] focus:ring-4 focus:ring-[#10B981]/30 transition-all hover:bg-black/40 cursor-pointer appearance-none"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%2310B981' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.5em 1.5em'
                  }}
                  data-testid="avatar-selector"
                >
                  {avatars.map((avatar, index) => (
                    <option key={avatar.id} value={index} className="bg-black text-white">
                      {avatar.avatarName || `Avatar ${index + 1}`} (#{avatar.idNumber})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Info Box - NUR für User OHNE JEDEN Avatar */}
        {!isDemoTourEligible && (avatars.length === 0 || (avatars.length === 1 && userData?.status === 'pending')) ? (
          <div className="p-8 border-2 border-[#10B981] rounded-xl bg-gradient-to-br from-[#10B981]/10 to-black">
            <div className="text-center mb-6">
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                Willkommen bei <span className="text-[#10B981]">SNAPSHOT</span>!
              </h3>
              <p className="text-white/80 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
                Erstelle dein digitales Ich mit KI-Power! Die Technologie lernt dein Gesicht in nur 6-8 Minuten. 
                Danach kannst du in Sekunden fotorealistische Bilder und Videos von dir in jeder Situation generieren.
              </p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-4 mb-6">
              <Link 
                to="/credits" 
                className="bg-white/5 rounded-lg p-4 text-center block hover:bg-white/10 transition-all cursor-pointer hover:scale-105"
              >
                <h4 className="font-bold text-white mb-1">1. Snaps kaufen</h4>
                <p className="text-sm text-white/60">schon ab 10 € inkl. MwSt.</p>
              </Link>
              <div className="bg-white/5 rounded-lg p-4 text-center">
                <h4 className="font-bold text-white mb-1">2. Fotos hochladen</h4>
                <p className="text-sm text-white/60">10 klare Selfies von dir</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4 text-center">
                <h4 className="font-bold text-white mb-1">3. KI trainieren</h4>
                <p className="text-sm text-white/60">Dein Avatar wird erstellt</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4 text-center">
                <h4 className="font-bold text-white mb-1">4. Bilder und Videos generieren</h4>
                <p className="text-sm text-white/60">Unbegrenzte Möglichkeiten!</p>
              </div>
            </div>
          </div>
        ) : null}

        {/* Galerie Section - direkt unter ID-Card */}
        {/* scroll-mt-24: Header-Höhe-Offset für scrollIntoView, sonst liegt der Galerie-Titel unterm fixierten Page-Header */}
        <div className="mt-12 scroll-mt-24" data-testid="gallery-section">
          <h2 className="text-3xl font-bold text-white mb-6 text-center">
            Deine <span className="text-[#10B981]">Galerie</span>
          </h2>
          
          {/* ReadOnly: Galerie OHNE Scroll (iOS-Fix) */}
          {readOnlyMode || isDemoTourEligible ? (
            <div id="video-gallery-section" className="px-4">
              <Gallery 
                generations={isDemoTourEligible ? effectiveGalleryGenerations : photos} 
                loading={loadingGallery} 
                onDelete={null}
                avatarId={isDemoTourEligible ? demoCardData?.id : avatars[activeAvatarIndex]?.id}
                onUsePrompt={null}
                onRemix={async () => {}} 
                isGenerating={isGenerating}
                pendingImageJobs={effectivePendingImageJobs}
                numPendingImages={numImages}
                readOnly={true}
                assemblyQueue={[]}
                addToQueue={() => {}}
                removeFromQueue={() => {}}
                isInQueue={() => false}
                galleryTab={galleryTab}
                setGalleryTab={setGalleryTab}
                showcaseVideos={effectiveShowcaseVideos}
                showcasePendingVideos={demoPendingVideos}
              />
            </div>
          ) : (
            <div id="video-gallery-section">
              <Gallery
                generations={photos}
                loading={loadingGallery}
                onDelete={handleImageDelete}
                avatarId={avatars[activeAvatarIndex]?.id}
                onUsePrompt={handleUsePrompt}
                onRemix={handleRemix}
                isGenerating={isGenerating}
                pendingImageJobs={pendingImageJobs}
                numPendingImages={numImages}
                readOnly={false}
                assemblyQueue={assemblyQueue}
                addToQueue={addToQueue}
                removeFromQueue={removeFromQueue}
                isInQueue={isInQueue}
                galleryTab={galleryTab}
                setGalleryTab={setGalleryTab}
                onOpenVideoAccordion={(imageUrl) => {
                  // Video-Accordion öffnen + Bild vorauswählen + hochscrollen
                  if (imageUrl) setVideoPreselectedImage(imageUrl);
                  setAccordionOpen(prev => ({...prev, videoCreate: true}));
                  setTimeout(() => {
                    const el = document.querySelector('[data-accordion="video-create"]');
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                externalVideo={kinoVideoReady}
                onExternalVideoClosed={() => setKinoVideoReady(null)}
                onRefresh={fetchPhotos}
              />
            </div>
          )}
          
          {/* Instagram Follow - Unter Galerie (nicht im ReadOnly) */}
          {!readOnlyMode && photos.length > 0 && (
            <div className="mt-12 pt-8 border-t border-white/10">
              <div className="text-center">
                <p className="text-white/50 text-sm mb-3">Folge uns für Inspiration</p>
                <a
                  href="https://www.instagram.com/snapshotpro.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#10B981] to-[#059669] text-white rounded-full font-semibold hover:from-[#059669] hover:to-[#047857] transition-all"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  @snapshotpro.ai
                </a>
              </div>
            </div>
          )}
        </div>

      </div>

      {!readOnlyMode && (isDemoTourEligible || demoModal || demoBubble || demoPlayingVideoUrl || demoRewardFlyouts.length > 0) && (
        <>
          <DashboardDemoOverlay modal={demoModal} bubble={demoBubble} />
          <DashboardRewardFlyouts
            items={demoRewardFlyouts}
            onComplete={() => setDemoRewardFlyouts([])}
          />
          <DashboardDemoVideoPlayer
            videoUrl={demoPlayingVideoUrl}
            onClose={handleDemoVideoPlaybackEnded}
            onEnded={handleDemoVideoPlaybackEnded}
          />
        </>
      )}

      {/* Delete Modals */}
      {showDeleteAvatar && (
        <DeleteAvatarModal
          avatarName={userData?.avatarName || 'Dieser Avatar'}
          onConfirm={async () => {
            try {
              // Lösche nur den AKTIVEN Avatar (Training-Job + Imageen)
              const activeAvatar = avatars[activeAvatarIndex];
              
              const res = await apiClient.delete(`/api/training/${activeAvatar.trainingJobId}`, {
                user_id: userId
              });
              
              if (res.ok) {
                console.log('✅ Avatar gelöscht:', activeAvatar.avatarName);
                setShowDeleteAvatar(false);
                
                // Reload training jobs
                await checkTrainingStatus();
                
                // Wechsle zu erstem verfügbaren Avatar
                setActiveAvatarIndex(0);
                
                // Reload Galerie
                fetchPhotos();
              } else {
                alert('Fehler beim Löschen');
              }
            } catch (error) {
              console.error('❌ Delete error:', error);
              alert('Fehler beim Löschen');
            }
          }}
          onCancel={() => setShowDeleteAvatar(false)}
        />
      )}
      
      {showDeleteAccount && !isDeletingAccount && (
        <DeleteAccountModal
          onConfirm={async () => {
            const user = _getStoredUser();
            const userId = user?.id;

            if (!userId) {
              alert('Fehler: User-ID nicht gefunden.');
              setShowDeleteAccount(false);
              return;
            }

            // Sofort Confirm-Modal weg, Lösch-Spinner-Modal an
            setShowDeleteAccount(false);
            setIsDeletingAccount(true);

            try {
              const response = await apiClient.delete(`/api/auth/user/${userId}`, {
                user_id: userId
              });

              if (response.ok) {
                localStorage.removeItem('snapshot_token');
                localStorage.removeItem('snapshot_user');
                localStorage.removeItem('snapshot_referral_code');
                // Redirect mit Query-Param damit die Welcome-Page eine Bestätigung anzeigen kann
                window.location.href = '/welcome?deleted=1';
              } else {
                const error = await response.json().catch(() => ({}));
                setIsDeletingAccount(false);
                alert(`Fehler beim Löschen: ${error.detail || 'Unbekannter Fehler'}`);
              }
            } catch (error) {
              console.error('Delete account error:', error);
              setIsDeletingAccount(false);
              alert('Fehler beim Löschen des Accounts. Bitte versuche es später erneut.');
            }
          }}
          onCancel={() => setShowDeleteAccount(false)}
        />
      )}

      {/* Einmaliger Hinweis-Modal: Auto-Verlängerung */}
      {showAutoExtendHint && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => {}}>
          <div className="max-w-md w-full bg-[#0F1419] border-2 border-[#10B981]/30 rounded-2xl p-6 space-y-4 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#10B981]/20 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
              <div className="flex-1">
                <h3 className="text-white font-bold text-lg leading-tight">Dein Avatar ist startklar</h3>
                <p className="text-white/60 text-xs mt-1">Kurzer Hinweis zur Auto-Verlängerung</p>
              </div>
            </div>
            <div className="space-y-2 text-sm text-white/80 leading-relaxed">
              <p>Die <span className="text-[#10B981] font-semibold">Auto-Verlängerung ist aktiv</span>. Dein Avatar bleibt jeden Monat für <span className="text-white font-semibold">3 SNAPs</span> einsatzbereit — komplett ohne weiteres Zutun.</p>
              <p>Wenn du sie ausschaltest, verliert dein Avatar nach 30 Tagen seine Kraft. Du kannst ihn dann jederzeit für 3 SNAPs wieder reaktivieren — solange ist die Bild-Erstellung pausiert. Videos mit vorhandenen Fotos kannst du weiterhin generieren.</p>
            </div>
            <button
              onClick={async () => {
                try { await apiClient.post('/api/user/auto-extend-hint-seen', {}); } catch (_) {}
                const u = _getStoredUser();
                if (u) { localStorage.setItem('snapshot_user', JSON.stringify({...u, auto_extend_hint_seen: true})); }
                setShowAutoExtendHint(false);
              }}
              className="w-full py-3 rounded-xl bg-[#10B981] hover:bg-[#0EA66E] text-black font-bold transition-all"
            >Verstanden</button>
          </div>
        </div>
      )}

      {isDeletingAccount && <DeletingAccountModal />}
      
      {/* INFO MODAL: 9.600 Kombinationen */}
      {showInfoModal && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setShowInfoModal(false)}
          style={{ touchAction: 'none' }}
        >
          <div 
            className="bg-black backdrop-blur-3xl border-t-2 sm:border-2 border-[#10B981]/40 rounded-t-3xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[85vh] overflow-y-auto animate-slide-up shadow-2xl shadow-[#10B981]/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              {/* Handle-Strich - GRÜNER LICHTSTRAHL (wie Dropdowns) */}
              <div className="w-14 h-1.5 bg-gradient-to-r from-transparent via-[#10B981] to-transparent rounded-full mx-auto mb-6 shadow-md shadow-[#10B981]/30"></div>
              
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-white">Vorlagen</h3>
                  <p className="text-sm text-white/70">{PROMPT_TEMPLATES.length} Ideen</p>
                </div>
                <button 
                  type="button"
                  onClick={() => setShowInfoModal(false)}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center transition-colors duration-150"
                >
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Tipp */}
              <div className="bg-gradient-to-r from-[#10B981]/10 to-[#059669]/10 border border-[#10B981]/30 rounded-xl p-4 mb-6">
                <p className="text-sm text-[#10B981] font-bold mb-1">🚀 Deine Kreativität, deine Regeln!</p>
                <p className="text-xs text-white/70 leading-relaxed">
                  Die Vorlagen sind Startpunkte – kombiniere sie frei mit allen Bildstilen (Cinematic, Fashion, Realistisch...). 
                  Mix Monaco + Thriller? Venice Beach + Vintage? Probier's aus!
                </p>
              </div>
              
              {/* Beispiel-Prompts - KATEGORISIERT */}
              <div className="mb-6">
                <p className="text-xs text-white/60 font-bold mb-3">🎬 Beispiele (Klicken zum Übernehmen) - {PROMPT_TEMPLATES.length} Vorlagen:</p>
                <div className="space-y-4 max-h-96 sm:max-h-[70vh] overflow-y-auto">
                  {(() => {
                    // Gruppiere Vorlagen nach Kategorien
                    const grouped = PROMPT_TEMPLATES.reduce((acc, template) => {
                      const cat = template.category || 'other';
                      if (!acc[cat]) acc[cat] = [];
                      acc[cat].push(template);
                      return acc;
                    }, {});
                    
                    // Importiere Kategorie-Labels
                    const { TEMPLATE_CATEGORIES } = require('../data/promptTemplates');
                    
                    return Object.entries(grouped).map(([category, templates]) => {
                      const catInfo = TEMPLATE_CATEGORIES[category] || { title: category, icon: '📍' };
                      
                      return (
                        <div key={category} className="mb-4">
                          {/* Kategorie-Überschrift */}
                          <div className="sticky top-0 bg-[#1A1A1A]/95 backdrop-blur-sm border-b-2 border-[#10B981]/30 pb-2 mb-3 z-10">
                            <h4 className="text-sm font-bold text-[#10B981] flex items-center gap-2">
                              <span>{catInfo.icon}</span>
                              {catInfo.title}
                              <span className="text-xs text-white/40">({templates.length})</span>
                            </h4>
                            {catInfo.subtitle && (
                              <p className="text-xs text-white/50 ml-6">{catInfo.subtitle}</p>
                            )}
                          </div>
                          
                          {/* Vorlagen dieser Kategorie */}
                          <div className="space-y-2">
                            {templates.map((template, index) => (
                              <button
                                type="button"
                                key={`${category}-${index}`}
                                onClick={() => {
                                  setPromptText(template.full);
                                  setShowInfoModal(false);
                                  
                                  // Öffne Accordion + Scroll
                                  setAccordionOpen(prev => ({...prev, createImage: true}));
                                  setTimeout(() => {
                                    const promptField = document.querySelector('[data-testid="dream-image-textarea"]');
                                    if (promptField) {
                                      promptField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                      promptField.focus();
                                    }
                                  }, 300);
                                }}
                                className="w-full text-left p-3 bg-[#10B981]/5 hover:bg-[#10B981]/10 border border-[#10B981]/20 hover:border-[#10B981]/40 rounded-xl transition-all duration-200 hover:shadow-md"
                              >
                                <p className="text-sm font-semibold text-white/90">{template.short}</p>
                                <p className="text-xs text-white/50 mt-1 line-clamp-2">{template.full}</p>
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
              
              {/* Einstellungen Erklärung */}
              <div className="bg-black/40 border border-white/10 rounded-xl p-4">
                <p className="text-xs text-white/60 font-bold mb-3">⚙️ EINSTELLUNGEN</p>
                <div className="space-y-1.5 text-xs text-white/70">
                  <div className="flex gap-2"><span>🌅</span> Lichtstimmung – Tag, Nacht, Golden Hour</div>
                  <div className="flex gap-2"><span>🌦️</span> Wetter – Sonnig, Regen, Schnee</div>
                  <div className="flex gap-2"><span>📷</span> Kamera – Nah, Standard, Weitwinkel</div>
                  <div className="flex gap-2"><span>🎨</span> Stil – Action, Cinematic, Fashion</div>
                  <div className="flex gap-2"><span>📐</span> Format – 9:16 Hochkant, 16:9 Quer, 1:1 Quadrat</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      
      {/* Prompt History Panel */}
      <PromptHistoryPanel
        isOpen={showHistoryPanel}
        onClose={() => setShowHistoryPanel(false)}
        historyItems={promptHistory}
        onUsePrompt={readOnlyMode ? null : handleUsePrompt}
        onDeletePrompt={readOnlyMode ? null : handleDeletePrompt}
        isLoading={loadingHistory}
        totalGenerations={promptHistory.length}
        readOnly={readOnlyMode}
      />
      
      {/* 2FA Setup Modal */}
      {show2FASetup && (
        <TwoFactorSetup 
          onClose={() => setShow2FASetup(false)}
          onSuccess={() => {
            const userData = _getStoredUser();
            userData.two_factor_enabled = true;
            _setStoredUser(userData);
            setShow2FASetup(false);
          }}
        />
      )}
      
      {/* 2FA Gate */}
      {show2FAGate && (
        <TwoFactorGate
          title={pending2FAAction === 'admin-panel' ? 'Admin-Zugriff' : 'Account löschen'}
          onVerified={() => {
            setShow2FAGate(false);
            if (pending2FAAction === 'admin-panel') {
              navigate('/admin');
            } else if (pending2FAAction === 'delete-account') {
              setShowDeleteAccount(true);
            }
            setPending2FAAction(null);
          }}
          onCancel={() => {
            setShow2FAGate(false);
            setPending2FAAction(null);
          }}
        />
      )}
      
      {/* 🎬 FLOATING QUEUE BADGE - ENTFERNT (jetzt über Snap Studio) */}

      {/* 🎬 ASSEMBLED VIDEO PLAYER MODAL */}
      {playingAssembly && createPortal(
        <div 
          className="fixed inset-0 bg-black/95 flex flex-col items-center justify-center z-[9999]"
          style={{ height: '100dvh' }}
          onClick={() => setPlayingAssembly(null)}
        >
          {/* Close Button */}
          <button
            onClick={() => setPlayingAssembly(null)}
            className="absolute top-4 right-4 z-50 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          
          {/* Video */}
          <video
            src={`https://snapshotpro.ai${playingAssembly.video_url}`}
            poster={playingAssembly.thumbnail_url ? `https://snapshotpro.ai${playingAssembly.thumbnail_url}` : undefined}
            controls
            autoPlay
            playsInline
            className="max-h-[80vh] max-w-[95vw] rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
          
          {/* Action Buttons */}
          <div className="mt-4 flex gap-3" onClick={(e) => e.stopPropagation()}>
            {/* Edit Button */}
            <button
              onClick={() => {
                if (playingAssembly.project_config) {
                  setEditingProject(playingAssembly);
                  setPlayingAssembly(null);
                  setShowBeatleapWizard(true);
                } else {
                  alert('Projekt-Daten nicht verfügbar');
                }
              }}
              className="px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold rounded-xl flex items-center gap-2 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Bearbeiten
            </button>
            
            {/* Download Button */}
            <a
              href={`https://snapshotpro.ai${playingAssembly.video_url}`}
              download
              className="px-6 py-3 bg-[#10B981] hover:bg-[#059669] text-black font-bold rounded-xl flex items-center gap-2 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download
            </a>
          </div>
        </div>,
        document.body
      )}
      
      {/* 🎵 BEATLEAP WIZARD */}
      <BeatleapWizard
        isOpen={showBeatleapWizard}
        onClose={() => {
          setShowBeatleapWizard(false);
          setEditingProject(null);
        }}
        avatarId={avatars[activeAvatarIndex]?.id}
        availableClips={assemblyQueue}
        editingProject={editingProject}
        onComplete={(result) => {
          console.log('Beatleap complete:', result);
          setEditingProject(null);
          fetchAssemblies();
        }}
      />
      
      {/* 🎬✨ AI MAGIC CREATE - Eigenständig mit eigener Galerie (NUR Master-Admin) */}
      {isMasterAdmin && showMagicCreate && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black">
          <MagicCreate
            avatarId={avatars[activeAvatarIndex]?.id}
            generations={photos}
            onBack={() => setShowMagicCreate(false)}
            onComplete={(result) => {
              console.log('Magic complete:', result);
              setShowMagicCreate(false);
              fetchAssemblies();
            }}
          />
        </div>,
        document.body
      )}

      {/* Crop-Modal und Format-Picker entfernt — Referenzbild wird so genommen wie es ist */}
    </div>
  );
}
